import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { stableStringify } from '../../utils/stableStringify';

import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQueryClient } from '@tanstack/react-query';
import { pdf } from '@react-pdf/renderer';
import { format, formatDistanceToNow } from 'date-fns';
import BlockCanvas from '../BlockCanvas';
import DocumentChrome, { DocumentChromeProps } from '../DocumentChrome';
import { useConfirmDialog } from '../../../../components/ConfirmDialog';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, TrashIcon } from '../../../../components/IconButton/icons';
import { SI_CSS_VARS } from '../../siChromeTokens';
import { APPENDIX_CSS_VARS, APPENDIX_TOKENS } from '../../appendixTokens';
import {
  ASPECT_OPTIONS,
  aspectOption,
  photoAppendixPages,
  photoGrid,
  photoFrame,
  photoLabelWidth,
  PhotoFragment,
  appendixDefaultLayout,
  keepPageLayoutsInPlace,
  handOnPageLayouts,
  uploadAspectFor,
} from '../../photoAppendixLayout';
import { DATE_ORDINAL_CSS_VARS } from '../../dateOrdinal';
import { SUBSCRIPT_CSS_VARS, isSubscriptShortcut, toggleSubscript } from '../../subscript';
import DocumentPDF, { imageBlockFramePt } from '../DocumentPDF';
import TableAppendixPage, { tableCellLayoutKey } from '../AppendixPages/TableAppendixPage';
import LiveText from '../LiveText';
import PlanAppendixPage from '../AppendixPages/PlanAppendixPage';
import AttachmentPage, { describePageSize } from '../AppendixPages/AttachmentPage';
import { mapWithConcurrency } from '../../../../utils/concurrency';
import { captureDrawings, followDesignCriteria, reconcileQaList } from '../../createDocument';
import DocumentNav, { DocumentNavTab } from './DocumentNav';
import { measureParagraphLayout, measureLineStarts, ParagraphLayout, ParagraphLineInfo } from '../../utils/measureParagraphLines';
import { toBrowserImage } from '../../utils/imageInput';
import { compressImage, readImageSize, resampleForPdf, blobToDataUrl, FramePt } from '../../utils/images';
import { rasterisePdf, uploadPdfAttachment, PLAN_LONG_SIDE_PX, ENCRYPTED_PDF_MESSAGE } from '../../utils/pdfUpload';
import { rotateBitmap, trimBitmap, remapForCrop } from '../../utils/planBitmap';
import { planCandidates } from '../../utils/planMarkers';
import { buildClientAddressLines, resolveCareOf } from '../../../InvoiceRegister/config';
import {
  Document,
  DocumentTemplate,
  Block,
  DocumentHeaderFields,
  formatDateLongParts,
  mergeHeaderFields,
  createBlock,
  BlockType,
  ImageBlockData,
  ImageAppendixBlockData,
  ImageAppendixItem,
  PhotoPageLayout,
  InstructionListBlockData,
  RowFragment,
  instructionRowBlocks,
  LOCKED_BLOCK_TYPES,
  APPENDIX_BLOCK_TYPES,
  APPENDIX_EDIT_ONLY_CLASSES,
  appendixSectionIds,
  letterEndIndex,
  moveAppendixSection,
  TableAppendixBlockData,
  TableAppendixColumn,
  PlanAppendixBlockData,
  PlanMarker,
  PlanPage,
  PdfAttachmentBlockData,
  fillTableFromPhotos,
  normaliseColumnWidths,
  columnRemovalRefusal,
  visibleTableColumns,
  TablePhotoOption,
  blankTableRow,
  blankTableSection,
  photoNumbers,
  planPages,
  normalisePlan,
  planMarkersAll,
  blankPlanPage,
  TEMPLATES_WITH_CERTIFICATE_FOOTER,
  CERTIFICATE_FOOTER_SRC,
  DrawingListBlockData,
  liftDrawingLeadIn,
  displayJobNumberOf,
  displayProjectDescriptionOf,
  exportFileName,
  TEMPLATES_WITHOUT_PROJECT_ADDRESS,
  HEADER_FIELD_SCHEMAS,
  ImageAspect,
  generateBlockId,
  getAppendixStartNumber,
  ParagraphFragment,
  DocumentStatus,
  isSignedStatus,
  statusClass as documentStatusClass,
  DocumentSignature,
  SignatureSlot,
  SignatureStamp,
  SIGNATURE_BASE_HEIGHT_PT,
  DocumentContact,
  effectiveContact
} from '../../config';
import { notifyReadOnly, getReadOnlyMessage } from '../../../../utils/supabaseErrors';
import { getSignatureUrl, prefetchSignatureUrl } from '../../../../utils/signatureUrl';
import './document-editor.scss';

/**
 * The columns a content save writes, as one comparable object. Every place
 * that records "what the server has" (load, save, lifecycle refresh) and the
 * conflict check go through this, so none can disagree on the field list.
 * `title` is normalised: the editor sends '' for an emptied input while older
 * rows hold NULL, and the two must not read as different content.
 */
const persistedOf = (row: { header_fields: unknown; content: unknown; title: unknown; signatory_id: unknown }) => ({
  header_fields: row.header_fields,
  content: row.content ?? [],
  title: (row.title as string | null | undefined) || null,
  signatory_id: row.signatory_id ?? null,
});

/**
 * A signer's signature placement, as the document renderers read it.
 *
 * `sign_document()` snapshots these three columns off the team member whose
 * signature is being stamped, so the signing dialog previews the same numbers
 * the document will be signed with.
 */
interface SignaturePlacement {
  scale: number;
  offsetXMm: number;
  offsetYMm: number;
}

const DEFAULT_SIGNATURE_PLACEMENT: SignaturePlacement = { scale: 1, offsetXMm: 0, offsetYMm: 0 };

/** Read a placement off a team_members row, defaulting each column. */
const readPlacement = (row: {
  signature_scale?: number | null;
  signature_offset_x_mm?: number | null;
  signature_offset_y_mm?: number | null;
} | null | undefined): SignaturePlacement => ({
  scale: Number(row?.signature_scale ?? 1),
  offsetXMm: Number(row?.signature_offset_x_mm ?? 0),
  offsetYMm: Number(row?.signature_offset_y_mm ?? 0),
});


// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  is_cpeng?: boolean;
}

/**
 * The project columns the editor needs, as one string.
 *
 * Extracted because it is selected from TWO places -- the document fetch and
 * refreshProjectData -- and they must stay identical: a field added to one and
 * not the other makes the Refresh button silently blank that field.
 *
 * head_contractor uses the explicit FK hint because `clients` is already
 * embedded once via projects.client, so PostgREST cannot infer which
 * relationship is meant.
 */
/**
 * Templates whose Insert panel offers no photo appendix. Both are certificates:
 * a statement about drawings or completed works, not a record with photographs.
 */
const TEMPLATES_WITHOUT_APPENDIX: ReadonlySet<string> = new Set([
  'rwd_design_certificate',
  'rwc_as_constructed_certificate',
]);

/**
 * How a table appendix row's measured height is filed: by id AND kind, so a
 * row turned into a section heading (or back) counts as not measured yet
 * rather than keeping the other kind's height.
 */
const tableRowKey = (rowId: string, kind: 'row' | 'section'): string => `${rowId}/${kind}`;

const PROJECT_SELECT = `
  job_number,
  description,
  site_classification,
  retaining_standard,
  street_address,
  city,
  state,
  postcode,
  country,
  care_of,
  clients!client (
    company_name,
    address_type,
    street_address,
    po_box,
    postal_address,
    header_lines,
    billing_email,
    city,
    state,
    postcode
  ),
  contacts!primary_contact (
    first_name,
    last_name
  ),
  head_contractor:clients!projects_head_contractor_client_id_fkey (
    company_name
  )
`;

/** The stage columns, same reasoning. */
const STAGE_SELECT = `
  stage_id,
  stage_description,
  care_of,
  site_classification,
  retaining_standard,
  head_contractor:clients!project_stages_head_contractor_client_id_fkey (
    company_name
  ),
  contacts:contacts!project_stages_primary_contact_fkey (
    first_name,
    last_name
  )
`;

interface ProjectData {
  job_number: number;
  description: string;
  /** Bare enum letter ('A', 'H1', ...). The design certificate renders Class 'A'. */
  site_classification?: string | null;
  /** Names the consultant whose drawings an As Constructed Certificate cites. */
  retaining_standard?: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  care_of?: string | null;
  clients: {
    company_name: string;
    address_type: string | null;
    street_address: string | null;
    po_box: string | null;
    postal_address?: string | null;
    header_lines?: string | null;
    billing_email?: string | null;
    city: string | null;
    state: string | null;
    postcode: string | null;
  } | null;
  contacts: {
    first_name: string;
    last_name: string;
  } | null;
  // Head contractor, embedded from the FK on projects. Site instructions name
  // it; the letter chrome ignores it. Design and RWC set it in the UI, Advisory
  // and RWD have no field so it stays null.
  //
  // FLAT, unlike ProjectDetail.head_contractor in ProjectDetailModal/config.ts,
  // which nests { client, contact }: that one is built by get_project_details,
  // this one is a PostgREST embed of a single FK.
  head_contractor?: { company_name: string } | null;
  stage?: {
    stage_id: string;
    stage_description: string;
    care_of?: string | null;
    /** Takes precedence over the project's, as care_of does. */
    site_classification?: string | null;
    /** Takes precedence over the project's, as care_of does. */
    retaining_standard?: string | null;
    // The stage's own head contractor takes precedence over the project's,
    // the same way resolveCareOf prefers the stage's care-of.
    head_contractor?: { company_name: string } | null;
    /** The stage's own primary contact; null inherits. Read via effectiveContact. */
    contacts?: DocumentContact | null;
  } | null;
}

// One entry on a page: a whole block, or one piece of a paragraph that
// continues on the next page
interface PageItem {
  blockIndex: number;
  fragment?: ParagraphFragment;
  /** Which rows of an instruction list or drawing list this page shows. */
  rowFragment?: RowFragment;
}

/** Which kind of page-owning block an appendix page belongs to. */
type AppendixKind = 'photo' | 'table' | 'plan' | 'attachment';

// Page content structure for distribution
interface PageContent {
  /**
   * Sequential over EVERY page, attachments included. It is the React key on
   * both surfaces and the key of the thumbnail hosts, so it must stay unique
   * and ordered -- which is why the footer's number is a separate field.
   */
  pageNumber: number;
  /**
   * The number printed in "Page n of N". Counts report pages only: an
   * attached PDF's pages are somebody else's sheets, merged in as they are,
   * and carry no footer. Undefined on those.
   */
  reportPageNumber?: number;
  blockIndices: number[]; // Indices of blocks on this page (a split paragraph appears on each of its pages)
  items: PageItem[];
  type: 'body' | 'appendix';
  appendixKind?: AppendixKind;
  appendixBlockId?: string; // Which appendix block this page belongs to
  appendixPageIndex?: number; // Which page within this appendix (0-based)
  appendixTotalPages?: number; // Total pages for this appendix
  /** Which rows of a table appendix this page shows. */
  rowFragment?: RowFragment;
  /** Which photos of a photo appendix this page shows, and its layout. */
  photoFragment?: PhotoFragment;
  /** An attached PDF page's own size; the export merges it in at this size. */
  pageSize?: { widthPt: number; heightPt: number };
  /**
   * Set on a body page that belongs to a blank page: the id of the
   * `blank_page` marker that opened it. The rail names it "Blank page"
   * instead of "Continued".
   */
  blankPageId?: string;
}

// Helper to estimate block height before measurement (conservative estimates)
/**
 * Blocks the paginator cuts BETWEEN ROWS rather than moving whole.
 *
 * Membership is not cosmetic: a block outside this set goes through the generic
 * "does it fit?" path, and because content pages are drawn <Page wrap={false}>
 * an over-tall one makes the PAGE taller instead of breaking -- the export
 * quietly stops being A4. Any block that repeats a row per record belongs here.
 * See "A row-based block breaks between rows" in DEVELOPMENT.md.
 */
const ROW_BLOCK_TYPES: ReadonlySet<Block['type']> = new Set<Block['type']>([
  'instruction_list',
  'drawing_list',
  'qa_list',
]);

const getEstimatedBlockHeight = (block: Block): number => {
  switch (block.type) {
    case 'heading':
      const level = (block.data as { level?: number }).level || 1;
      return level === 1 ? 60 : level === 2 ? 50 : 45;
    case 'paragraph':
      const content = (block.data as { content?: string }).content || '';
      // Conservative estimate: ~30px per 100 chars, min 50px
      return Math.max(50, Math.ceil(content.length / 80) * 30);
    case 'bullet_list':
    case 'scope_list':
      const items = (block.data as { items?: unknown[] }).items || [];
      // 40px per item to account for wrapping text
      return Math.max(50, items.length * 40);
    case 'image':
      return 350; // Images are typically larger
    case 'image_appendix':
      const appendixImages = (block.data as { images?: unknown[] }).images || [];
      return Math.max(250, Math.ceil(appendixImages.length / 3) * 250);
    case 'table':
      return 200;
    case 'signature':
      // Signature: closing + company + signature space + line + name, plus the
      // title and registration lines once signed. On a site instruction the
      // distribution column sits beside it, so the line is the taller of the
      // two -- close enough to the same estimate. First-paint estimate only;
      // real measurement corrects it.
      return 210;
    case 'instruction_list':
      // One row of one line each, plus its rule and padding. First-paint
      // estimate only; real measurement corrects it.
      const instructionRows = (block.data as { rows?: unknown[] }).rows || [];
      return Math.max(40, instructionRows.length * 32);
    case 'drawing_list':
      // Two lead-in sentences plus a line per drawing. First-paint estimate
      // only; real measurement corrects it.
      const drawingRows = (block.data as { rows?: unknown[] }).rows || [];
      return Math.max(60, 60 + drawingRows.length * 20);
    case 'qa_list':
      // Two lines per QA result. First-paint estimate only.
      const qaRows = (block.data as { rows?: unknown[] }).rows || [];
      return Math.max(40, qaRows.length * 40);
    case 'blank_page':
      // A marker of no height.
      return 0;
    case 'table_appendix':
      // A line per row. First-paint estimate only; the appendix branch of the
      // paginator uses the measured row heights.
      const tableRows = (block.data as { rows?: unknown[] }).rows || [];
      return Math.max(40, 40 + tableRows.length * 24);
    case 'divider':
      return 30;
    case 'spacer':
      const height = (block.data as { height?: string }).height || 'medium';
      return height === 'small' ? 20 : height === 'large' ? 60 : 40;
    default:
      return 60;
  }
};

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * The left panel's state is owned by the register, not by this component.
 *
 * The editor is keyed on documentId (see Documents/index.tsx), so it remounts
 * on every sibling click -- state held here would collapse the panel and reset
 * its tab each time you used it.
 */
interface DocumentEditorProps {
  navCollapsed: boolean;
  onNavCollapsedChange: (collapsed: boolean) => void;
  navTab: DocumentNavTab;
  onNavTabChange: (tab: DocumentNavTab) => void;
  /**
   * The job of the document last loaded, held above the key so the panel can
   * keep its list on screen while the next one loads. Null on the way in from
   * the register, where the incoming document's job is not known yet.
   */
  navScope: { projectId: number | null; stageId: number | null } | null;
  onNavScopeChange: (scope: { projectId: number | null; stageId: number | null }) => void;
}

/**
 * The Fields tab's refresh mark. One drawing for every section's Refresh, so
 * the project fields, the drawing set and the QA items read as the same action.
 */
const RefreshIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const DocumentEditor: React.FC<DocumentEditorProps> = ({
  navCollapsed,
  onNavCollapsedChange,
  navTab,
  onNavTabChange,
  navScope,
  onNavScopeChange,
}) => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();

  // Document state
  const [document, setDocument] = useState<Document | null>(null);
  // The SI No input's own text. Kept out of `document` because it is committed
  // by set_document_sequence(), not by the autosave.
  const [sequenceInput, setSequenceInput] = useState('');
  // In-app replacements for window.confirm / window.alert. Promise-based, so
  // the export can pause mid-flight and wait for an answer where it used to.
  const { confirm, notify, dialog: confirmDialog } = useConfirmDialog();
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [engineers, setEngineers] = useState<TeamMember[]>([]);
  const [signatures, setSignatures] = useState<DocumentSignature[]>([]);
  // The signed-in user's own team member id, so we can work out which signature
  // slots they are entitled to sign.
  const [myTeamMemberId, setMyTeamMemberId] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  const [signingSlot, setSigningSlot] = useState<SignatureSlot | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  // The signer's own identity, shown in the sign confirmation so they can see
  // exactly what will be stamped before committing.
  const [myName, setMyName] = useState<string>('');
  const [myPositionTitle, setMyPositionTitle] = useState<string | null>(null);
  const [myRegistrationNumber, setMyRegistrationNumber] = useState<string | null>(null);
  const [mySignaturePath, setMySignaturePath] = useState<string | null>(null);
  // The current user's own signature placement, for the signing dialog's
  // preview. Same three numbers sign_document() snapshots.
  const [myPlacement, setMyPlacement] = useState<SignaturePlacement>(DEFAULT_SIGNATURE_PLACEMENT);
  const [mySignaturePreviewUrl, setMySignaturePreviewUrl] = useState<string | null>(null);
  // The document's nominated signatory (for a certified slot a manager may sign
  // on their behalf, stamping THEIR signature -- so the sign dialog previews the
  // signatory, not the manager).
  const [signatoryInfo, setSignatoryInfo] = useState<{ name: string; title: string | null; registration: string | null; signaturePath: string | null; placement: SignaturePlacement } | null>(null);
  // Signed URL for the signature stamped into the document's signature block
  // (the author-slot signature). Loads async, but only fills the image inside a
  // fixed-height box, so it never changes measured height.
  const [stampImageUrl, setStampImageUrl] = useState<string | null>(null);

  // Loading/error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Bumps on every local edit; the auto-save debounce keys off it so edits made
  // while a save is in flight still trigger another save.
  const [dirtyTick, setDirtyTick] = useState(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const versionCountRef = useRef<number>(0);
  // Latest document for use inside timers, unmount cleanup and beforeunload
  const documentRef = useRef<Document | null>(null);
  // changeCounter counts edits; savedCounter is the count at the last successful
  // save. They differ exactly when there is something unsaved.
  const changeCounterRef = useRef(0);
  const savedCounterRef = useRef(0);
  const savingRef = useRef(false);
  const saveAgainRef = useRef(false);
  // A version snapshot requested while a save was in flight
  const pendingVersionRef = useRef(false);
  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current = session?.access_token ?? null;
  // Tracks the auto-seeded paragraph for a brand-new draft — used to focus it once on load
  const pendingAutoFocusRef = useRef(false);
  // Save reliability. See "Saves retry, and never overwrite a newer version"
  // in DEVELOPMENT.md.
  // The row's updated_at as the server last reported it: every content save
  // is conditional on it, so a copy saved from another tab or device is never
  // silently overwritten.
  const loadedUpdatedAtRef = useRef<string | null>(null);
  // The payload as last known to be on the server (loaded, saved, or adopted
  // after a lifecycle action). A conditional save that matched no row
  // compares the server's columns against this to tell a real conflict from
  // a server-side change to some other column. Kept as an object: it is
  // serialised only when that comparison is made.
  const lastPersistedRef = useRef<ReturnType<typeof persistedOf> | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryAttemptRef = useRef(0);
  // A conflict dialog is open: saves wait for the answer.
  const conflictPendingRef = useRef(false);
  // The user chose to leave without their unsaved edits (or to reload the
  // server's copy): the unmount flush and the unload flush stand down.
  const discardLocalRef = useRef(false);
  const saveDocumentRef = useRef<(withVersion?: boolean) => Promise<void>>(async () => {});

  // PDF export state
  const [isExporting, setIsExporting] = useState(false);

  // Sidebar tab state
  const [activeTab, setActiveTab] = useState<'pages' | 'info' | 'variables'>('pages');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Page the reader is looking at (largest visible page), for the thumbnail rail
  const [activePageIndex, setActivePageIndex] = useState(0);
  // Bumped (debounced) whenever the pages could have changed, to refresh thumbnails
  const [thumbnailVersion, setThumbnailVersion] = useState(0);
  const thumbnailHostsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Image URLs for appendix display (signed URLs from Supabase)
  const [appendixImageUrls, setAppendixImageUrls] = useState<Record<string, string>>({});

  // Appendix editing state
  const [focusedAppendixId, setFocusedAppendixId] = useState<string | null>(null);
  const [uploadingAppendix, setUploadingAppendix] = useState(false);
  const [dragOverAppendixId, setDragOverAppendixId] = useState<string | null>(null);
  // Files that could not be added on the last upload attempt, shown in the drop zone
  const [appendixUploadErrors, setAppendixUploadErrors] = useState<string[]>([]);
  const [showAspectMenu, setShowAspectMenu] = useState(false);
  /** The photo appendix's "This page" layout menu. */
  const [showPageLayoutMenu, setShowPageLayoutMenu] = useState(false);
  /** The photo whose aspect menu is open, if any. */
  const [photoAspectMenuFor, setPhotoAspectMenuFor] = useState<string | null>(null);
  const appendixFileInputRef = useRef<HTMLInputElement>(null);

  // Appendix delete confirmation state
  const [deleteAppendixConfirm, setDeleteAppendixConfirm] = useState<{ blockId: string; title: string } | null>(null);

  // Document delete confirmation state
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  /**
   * The editor's visible width as `--editor-main-width`, which an appendix's
   * floating action bar is capped to (it wraps onto a second row beyond it).
   * The bar is centred on the page, and the plan's bar is wider than the
   * page; uncapped, it ran past the editor's edge and gave the whole editor a
   * horizontal scrollbar whenever it was open.
   */
  const editorMainResizeRef = useRef<ResizeObserver | null>(null);
  const editorMainRef = useCallback((el: HTMLDivElement | null) => {
    editorMainResizeRef.current?.disconnect();
    editorMainResizeRef.current = null;
    if (!el) return;
    const publish = () => el.style.setProperty('--editor-main-width', `${el.clientWidth}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    editorMainResizeRef.current = observer;
  }, []);

  // Close the action-bar menus when the appendix loses focus
  useEffect(() => {
    if (!focusedAppendixId) {
      setShowAspectMenu(false);
      setShowPageLayoutMenu(false);
      setPhotoAspectMenuFor(null);
      setShowPlanSourceMenu(false);
      setShowPlaceMenu(false);
      setShowColumnsMenu(false);
    }
  }, [focusedAppendixId]);

  // Page constants - A4 dimensions
  // We measure the actual rendered page height rather than calculating from mm
  // because browser mm rendering varies by device/zoom
  const [measuredPageHeight, setMeasuredPageHeight] = useState<number>(0);
  
  // Measure actual page dimensions once the page is on screen. Keyed on
  // `loading`, not run once on mount: while the document is loading the
  // editor renders its skeleton, which has no page, and a timer is not a
  // mount signal. See "The measuring box must be mounted when the measure
  // effect runs" in DEVELOPMENT.md.
  useEffect(() => {
    if (loading) return;
    const measurePage = () => {
      // Scoped to .editor-main. This is the height the whole page plan is
      // built from, and an unscoped query takes the FIRST .document-page in
      // DOM order -- which is no longer guaranteed to be the canvas now that a
      // panel renders ahead of it. Anything in that panel carrying the class
      // would silently repaginate the document against it, and the PDF would
      // follow. See "WYSIWYG Builders: Editor / PDF Parity" in DEVELOPMENT.md.
      const pageEl = window.document.querySelector('.editor-main .document-page') as HTMLElement;
      if (pageEl) {
        setMeasuredPageHeight(pageEl.offsetHeight);
      }
    };
    
    // The page is mounted by the time this runs, so measure now; the timer
    // is a backstop for a layout that has not settled, as in measureContent.
    measurePage();
    const timer = setTimeout(measurePage, 100);
    return () => clearTimeout(timer);
  }, [loading]);
  
  // Calculate content heights based on measured page or fallback to calculation
  const MM_TO_PX = 96 / 25.4; // ~3.78px per mm at 96 DPI (fallback)
  // Page margins from the Word letter template (see document-editor.scss).
  // Content must stop 22mm above the page bottom on every page. DocumentPDF
  // uses the same value as its page paddingBottom, so a block that fits here
  // fits there. (The footer sits at bottom: 10mm.)
  const FOOTER_CLEARANCE_MM = 22;
  const TOP_PADDING_MM = 32.5;
  
  // Use measured height if available, otherwise calculate
  const PAGE_HEIGHT_PX = measuredPageHeight || (297 * MM_TO_PX);
  const FOOTER_CLEARANCE_PX = FOOTER_CLEARANCE_MM * MM_TO_PX;
  const TOP_PADDING_PX = TOP_PADDING_MM * MM_TO_PX;
  
  // Page 1: no top padding (letterhead), just footer clearance
  const PAGE1_CONTENT_HEIGHT_PX = PAGE_HEIGHT_PX - FOOTER_CLEARANCE_PX;
  // Pages 2+: 20mm top padding, footer clearance
  const PAGE_CONTENT_HEIGHT_PX = PAGE_HEIGHT_PX - TOP_PADDING_PX - FOOTER_CLEARANCE_PX;
  
  // Block height measurement. The gap above a block (its margin-top, present
  // only when it and the block above both want the blank line) is kept apart
  // from the box height because the first block on a page gets no gap.
  // Paragraphs also record where each line starts, so they can be cut between
  // lines when they do not fit on a page.
  interface MeasuredBlock {
    height: number;
    gapAbove: number;
    lines?: ParagraphLineInfo & { html: string };
    /**
     * Per-row heights of a row-based block -- an instruction list or a drawing
     * list -- so it can be broken between rows across pages. Measured
     * individually because these are the only blocks whose parts are separately
     * placeable.
     *
     * `tableHeadHeight` is the instruction table's header, repeated on every
     * page the list continues onto. `leadInHeight` is the drawing list's two
     * sentences, which appear ONCE, above the first fragment only.
     */
    rowHeights?: number[];
    /**
     * A table appendix's rows as `<rowId>/<kind>`, parallel to rowHeights, so
     * the paginator can look a height up by row rather than by position. See
     * tableRowKey and the table_appendix branch of `pages`.
     */
    rowKeys?: string[];
    tableHeadHeight?: number;
    leadInHeight?: number;
  }
  const measurementRef = useRef<HTMLDivElement>(null);
  const [blockHeights, setBlockHeights] = useState<Record<string, MeasuredBlock>>({});
  const [headerHeight, setHeaderHeight] = useState(200); // Default header height

  // =======================================
  // DATA FETCHING
  // =======================================

  // Fetch once per document and signed-in user. Keyed on the user id, not the
  // session object: the auth helper emits a new session object on every auth
  // event (a tab regaining focus is enough), and re-fetching then would remount
  // the editor and discard whatever had not yet been saved.
  const sessionUserId = session?.user?.id ?? null;
  useEffect(() => {
    const fetchDocument = async () => {
      if (!sessionUserId || !documentId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch document with related data
        const { data: docData, error: docError } = await supabase
          .from('documents')
          .select(`
            *,
            document_templates (*),
            document_signatures (
              slot_key,
              slot_label,
              signed_by,
              signed_at,
              signer_name,
              signer_position_title,
              signer_registration_number,
              signature_storage_path,
              signature_scale,
              signature_offset_x_mm,
              signature_offset_y_mm
            ),
            projects ( ${PROJECT_SELECT} ),
            project_stages!stage_id ( ${STAGE_SELECT} )
          `)
          .eq('id', documentId)
          .single();

        if (docError) {
          console.error('Error fetching document:', docError);
          setError('Document not found or you do not have access.');
          return;
        }

        // Seed an empty paragraph when a fresh Draft has no blocks yet, so the user
        // always has a focusable starting point instead of a blank page.
        const fetchedDoc = docData as unknown as Document;
        if (fetchedDoc.status === 'Draft' && (!fetchedDoc.content || fetchedDoc.content.length === 0)) {
          fetchedDoc.content = [createBlock('paragraph')];
          pendingAutoFocusRef.current = true;
        }
        // A Design Certificate draft from before its two lead-in sentences
        // were paragraphs gets them as paragraphs, so they can be edited like
        // the rest of the body. Draft only: an issued certificate keeps the
        // shape it was issued in. Saved with the next edit, like the seeded
        // paragraph above; until then the server's copy draws the same page.
        if (fetchedDoc.status === 'Draft' && fetchedDoc.content?.length) {
          fetchedDoc.content = liftDrawingLeadIn(fetchedDoc.content);
        }
        setDocument(fetchedDoc);
        setSequenceInput(fetchedDoc.sequence_number != null ? String(fetchedDoc.sequence_number) : '');
        documentRef.current = fetchedDoc;
        loadedUpdatedAtRef.current = (docData as { updated_at?: string }).updated_at ?? null;
        // As fetched, before the seeded paragraph: that one is not on the server.
        lastPersistedRef.current = persistedOf(docData);
        setTemplate(docData.document_templates as unknown as DocumentTemplate);
        setSignatures((docData.document_signatures as unknown as DocumentSignature[]) || []);
        const fetchedProject = docData.projects as unknown as ProjectData | null;
        const fetchedStage = docData.project_stages as unknown as ProjectData['stage'];
        setProjectData(fetchedProject ? { ...fetchedProject, stage: fetchedStage } : null);
        // Lift the job above the key, so the panel keeps its list painted
        // through the next sibling's load rather than blinking out with it.
        onNavScopeChange({ projectId: fetchedDoc.project_id, stageId: fetchedDoc.stage_id });

        // Fetch engineers for signatory dropdown
        const { data: engineersData } = await supabase
          .from('team_members')
          .select('id, first_name, last_name, is_cpeng')
          .eq('type', 'Engineer')
          .eq('is_active', true)
          .order('first_name');

        setEngineers(engineersData || []);

        // Who am I? Needed to decide which signature slots I may sign.
        const { data: myProfile } = await supabase
          .from('profiles')
          .select('team_member_id, team_members (role, type, first_name, last_name, position_title, registration_number, signature_storage_path, signature_scale, signature_offset_x_mm, signature_offset_y_mm)')
          .eq('id', sessionUserId)
          .single();

        if (myProfile) {
          const tm = myProfile.team_members as unknown as {
            role: string;
            type: string;
            first_name: string;
            last_name: string;
            position_title: string | null;
            registration_number: string | null;
            signature_storage_path: string | null;
            signature_scale: number | null;
            signature_offset_x_mm: number | null;
            signature_offset_y_mm: number | null;
          } | null;
          setMyTeamMemberId(myProfile.team_member_id || null);
          setIsManager(tm?.role === 'manager');
          setMyName(tm ? `${tm.first_name} ${tm.last_name}`.trim() : '');
          setMyPositionTitle(tm?.position_title || null);
          setMyRegistrationNumber(tm?.registration_number || null);
          setMySignaturePath(tm?.signature_storage_path || null);
          setMyPlacement(readPlacement(tm));
        }

        // Get current version count
        const { count } = await supabase
          .from('document_versions')
          .select('*', { count: 'exact', head: true })
          .eq('document_id', documentId);

        versionCountRef.current = count || 0;

      } catch (err) {
        console.error('Error fetching document:', err);
        setError('An error occurred while loading the document.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUserId, supabase, documentId, onNavScopeChange]);

  // Update browser tab title
  useEffect(() => {
    if (document) {
      const title = document.title || document.document_number;
      window.document.title = `${title} | TE Admin`;
    }

    // Restore title when unmounting
    return () => {
      window.document.title = 'Documents | TE Admin';
    };
  }, [document]);

  // Focus the auto-seeded first paragraph on a brand-new draft so the user lands
  // with a blinking cursor instead of a blank page they have to hunt for.
  useEffect(() => {
    if (loading || !document || !pendingAutoFocusRef.current) return;
    const timer = setTimeout(() => {
      const firstEditable = window.document.querySelector(
        '.editor-main .document-page .block-wrapper [contenteditable="true"]'
      ) as HTMLElement | null;
      if (firstEditable) firstEditable.focus();
      pendingAutoFocusRef.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, [loading, document]);

  // Fetch signed URLs for appendix images
  useEffect(() => {
    const fetchAppendixImageUrls = async () => {
      if (!document?.content) return;

      const storagePaths: string[] = [];
      
      // Collect every appendix-page image: the photos, a plan, and the page
      // previews of an attached PDF.
      const want = (path: string | null | undefined) => {
        if (path && !appendixImageUrls[path]) storagePaths.push(path);
      };
      for (const block of document.content) {
        if (block.type === 'image_appendix') {
          const data = block.data as ImageAppendixBlockData;
          for (const img of data.images || []) want(img.storagePath);
        } else if (block.type === 'plan_appendix') {
          for (const plan of planPages(block.data as PlanAppendixBlockData)) want(plan.storagePath);
        } else if (block.type === 'pdf_attachment') {
          for (const page of (block.data as PdfAttachmentBlockData).pages || []) want(page.thumbPath);
        }
      }

      if (storagePaths.length === 0) return;

      // Fetch signed URLs
      const newUrls: Record<string, string> = {};
      for (const path of storagePaths) {
        try {
          const { data } = await supabase.storage
            .from('document-images')
            .createSignedUrl(path, 3600);
          
          if (data?.signedUrl) {
            newUrls[path] = data.signedUrl;
          }
        } catch (err) {
          console.error('Error fetching signed URL:', path, err);
        }
      }

      if (Object.keys(newUrls).length > 0) {
        setAppendixImageUrls(prev => ({ ...prev, ...newUrls }));
      }
    };

    fetchAppendixImageUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.content, supabase]);

  // Measure block heights after content renders.
  //
  // Keyed on `loading` as well as the content: the document is set several
  // awaits before the loading flag clears, and until it clears the editor
  // renders its skeleton, with no measurement container in it. On a remote
  // Supabase those awaits outlast the 200ms retry below, so the retry found
  // nothing either, and the page plan stayed on getEstimatedBlockHeight's
  // first-paint guesses until the next edit: under-filled pages that "snap
  // into place" on the first keystroke. Running again when `loading` flips
  // measures the container the moment it exists. See "The measuring box
  // must be mounted when the measure effect runs" in DEVELOPMENT.md.
  useEffect(() => {
    if (!document || loading) return;

    const measureContent = () => {
      const measureEl = measurementRef.current;
      if (!measureEl) return;

      // Measure header area height (letterhead + header section + margins)
      const letterheadEl = measureEl.querySelector('.document-letterhead');
      const headerEl = measureEl.querySelector('.document-header-section');
      let newHeaderHeight = 0;
      
      if (letterheadEl) {
        const el = letterheadEl as HTMLElement;
        const style = window.getComputedStyle(el);
        const marginBottom = parseFloat(style.marginBottom) || 0;
        newHeaderHeight += el.offsetHeight + marginBottom;
      }
      
      if (headerEl) {
        const el = headerEl as HTMLElement;
        const style = window.getComputedStyle(el);
        const marginBottom = parseFloat(style.marginBottom) || 0;
        newHeaderHeight += el.offsetHeight + marginBottom;
      }
      
      // Fallback if nothing measured
      if (newHeaderHeight === 0) {
        newHeaderHeight = 200;
      }

      // Measure each block's box (offsetHeight excludes margins) and the gap
      // above it, which BlockCanvas expresses as margin-top.
      //
      // Driven from document.content rather than from every .block-wrapper in
      // the container: an instruction item holds blocks of its own, so the
      // container is now full of NESTED wrappers that are not separately
      // placeable and must not be measured as though they were.
      const newHeights: Record<string, MeasuredBlock> = {};
      document.content.forEach(block => {
        const element = measureEl.querySelector(
          `.block-wrapper[data-block-id="${block.id}"]`
        ) as HTMLElement | null;
        if (!element) return;

        const style = window.getComputedStyle(element);
        const gapAbove = parseFloat(style.marginTop) || 0;
        // Fractional height: offsetHeight rounds to whole pixels, and the
        // rounding accumulates over a page of blocks
        const measured: MeasuredBlock = { height: element.getBoundingClientRect().height, gapAbove };

        if (block.type === 'paragraph') {
          const paragraphEl = element.querySelector('.paragraph-content') as HTMLElement | null;
          if (paragraphEl) {
            measured.lines = {
              ...measureLineStarts(paragraphEl),
              html: (block.data as { content?: string }).content || ''
            };
          }
        }

        if (block.type === 'instruction_list') {
          // Each item's own height, so the list can be cut between items, plus
          // the table header that is repeated on every page it continues onto.
          measured.rowHeights = Array.from(element.querySelectorAll('[data-instruction-row]'))
            .map(row => (row as HTMLElement).getBoundingClientRect().height);
          const head = element.querySelector('.si-table-head') as HTMLElement | null;
          measured.tableHeadHeight = head ? head.getBoundingClientRect().height : 0;
        }

        if (block.type === 'qa_list') {
          // A row is its bullet AND the dash line under it, measured together
          // so a page break can never separate a QA result from its reference.
          measured.rowHeights = Array.from(element.querySelectorAll('[data-qa-row]'))
            .map(row => (row as HTMLElement).getBoundingClientRect().height);
        }

        if (block.type === 'drawing_list') {
          // Same idea, one row at a time, so a long drawing set breaks between
          // drawings instead of growing the page past A4 -- content pages are
          // drawn with wrap={false}, so an over-tall block does not spill onto
          // a new PDF page, it makes the page itself taller.
          measured.rowHeights = Array.from(element.querySelectorAll('[data-drawing-row]'))
            .map(row => (row as HTMLElement).getBoundingClientRect().height);
          // The design-criteria sentence and the "listed below:" line, measured
          // together with their blank lines as the distance from the top of the
          // block to the first row. They are said once, so only the fragment
          // that starts the list carries them.
          const rowsEl = element.querySelector('.drawing-list-block__rows') as HTMLElement | null;
          measured.leadInHeight = rowsEl
            ? rowsEl.getBoundingClientRect().top - element.getBoundingClientRect().top
            : measured.height;
        }

        newHeights[block.id] = measured;
      });

      // Table appendices are measured in their own box (.measurement-appendix),
      // at the appendix page's 170mm width rather than the letter's 154.6mm:
      // a cell wraps differently at each, and the appendix page is where the
      // rows are drawn. The title is the lead-in (first page only), the column
      // header is repeated on every page, and each row is its own height.
      document.content.forEach(block => {
        if (block.type !== 'table_appendix') return;
        const element = measureEl.querySelector(
          `.measurement-appendix [data-table-appendix="${block.id}"]`
        ) as HTMLElement | null;
        if (!element) return;
        const content = element.closest('.apx-content') as HTMLElement | null;
        const title = content?.querySelector('.apx-title') as HTMLElement | null;
        const head = element.querySelector('.table-appendix__head') as HTMLElement | null;
        const titleStyle = title ? window.getComputedStyle(title) : null;
        const rowEls = Array.from(element.querySelectorAll<HTMLElement>('[data-table-row]'));
        newHeights[block.id] = {
          height: element.getBoundingClientRect().height,
          gapAbove: 0,
          leadInHeight: title
            ? title.getBoundingClientRect().height + (parseFloat(titleStyle?.marginBottom || '0') || 0)
            : 0,
          tableHeadHeight: head ? head.getBoundingClientRect().height : 0,
          rowHeights: rowEls.map(row => row.getBoundingClientRect().height),
          rowKeys: rowEls.map(row => tableRowKey(
            row.dataset.tableRow || '',
            row.classList.contains('table-appendix__row--section') ? 'section' : 'row'
          )),
        };
      });

      setBlockHeights(prev => {
        const changed = JSON.stringify(newHeights) !== JSON.stringify(prev);
        return changed ? newHeights : prev;
      });
      setHeaderHeight(prev => newHeaderHeight !== prev ? newHeaderHeight : prev);
    };

    // Wait for letterhead image to load before measuring
    const letterheadImg = measurementRef.current?.querySelector('.document-letterhead img') as HTMLImageElement;
    
    if (letterheadImg && !letterheadImg.complete) {
      const handleLoad = () => {
        measureContent();
        letterheadImg.removeEventListener('load', handleLoad);
      };
      letterheadImg.addEventListener('load', handleLoad);
      
      // Also measure after delay as fallback
      const timer = setTimeout(measureContent, 500);
      return () => {
        clearTimeout(timer);
        letterheadImg.removeEventListener('load', handleLoad);
      };
    } else {
      // Image already loaded or no letterhead
      measureContent();
      const timer = setTimeout(measureContent, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.content, document?.header_fields, loading]);

  // Calculate page distribution based on measured heights
  const pages = useMemo((): PageContent[] => {
    // The blank page the body flow is currently inside, if any: set by its
    // marker block, cleared by the next page-owning block. Every body page
    // created while it is set belongs to it.
    let currentBlankPage: string | null = null;
    const newBodyPage = (pageNumber: number): PageContent => ({
      pageNumber,
      blockIndices: [],
      items: [],
      type: 'body',
      ...(currentBlankPage ? { blankPageId: currentBlankPage } : {}),
    });

    // If no content, return single page with no blocks
    if (!document?.content.length) {
      return [newBodyPage(1)];
    }

    // A paragraph may be cut between lines when it does not fit, keeping at
    // least this many lines on each side of the cut (Word's widow/orphan rule)
    const MIN_LINES_EACH_SIDE = 2;

    const result: PageContent[] = [];
    let currentPage: PageContent = newBodyPage(1);
    let currentHeight = headerHeight; // Start with header on page 1
    let hasSeenAppendix = false;

    const addItem = (item: PageItem) => {
      currentPage.items.push(item);
      currentPage.blockIndices.push(item.blockIndex);
    };
    const startNewPage = () => {
      result.push(currentPage);
      currentPage = newBodyPage(result.length + 1);
      currentHeight = 0;
    };
    // Place the pending pieces of a paragraph on the current page (each call
    // adds the pieces gathered since the last one; `parts` is filled in later)
    /**
     * Place a row-based block's fragments, numbering them as they go. `parts` is
     * only known once the last one is placed, so the final call back-fills it
     * across every fragment of this block.
     */
    const addRowFragments = (
      fragments: Array<{ rowStart: number; rowEnd: number }>,
      blockIndex: number,
      final = false
    ) => {
      while (fragments.length > 0) {
        const fragment = fragments.shift()!;
        const placed = result.concat(currentPage).reduce(
          (n, page) => n + page.items.filter(it => it.blockIndex === blockIndex && it.rowFragment).length, 0
        );
        addItem({
          blockIndex,
          rowFragment: { ...fragment, part: placed, parts: 0 },
        });
      }
      if (!final) return;
      const all = result.concat(currentPage)
        .flatMap(page => page.items.filter(it => it.blockIndex === blockIndex && it.rowFragment));
      all.forEach(item => {
        item.rowFragment = { ...item.rowFragment!, parts: all.length };
      });
    };

    const addPieces = (
      pieces: Array<{ lineStart: number; lineEnd: number }>,
      blockIndex: number,
      block: Block,
      lines: NonNullable<MeasuredBlock['lines']>,
      _final: boolean
    ) => {
      while (pieces.length > 0) {
        const piece = pieces.shift()!;
        const charStart = lines.lineStarts[piece.lineStart] ?? 0;
        const charEnd = piece.lineEnd >= lines.lineStarts.length ? lines.textLength : lines.lineStarts[piece.lineEnd];
        addItem({
          blockIndex,
          fragment: {
            part: piece.lineStart === 0 ? 0 : -1, // corrected below
            parts: 0,
            lineStart: piece.lineStart,
            lineEnd: piece.lineEnd,
            charStart,
            charEnd,
            sourceHtml: lines.html
          }
        });
        // Part index = how many pieces of this block have been placed so far
        const placed = result.concat(currentPage).reduce(
          (n, page) => n + page.items.filter(it => it.blockIndex === blockIndex && it.fragment).length, 0
        );
        const item = currentPage.items[currentPage.items.length - 1];
        item.fragment = { ...item.fragment!, part: placed - 1 };
        void block;
      }
    };
    
    // Small safety buffer for measurement variance. Heights are measured from
    // the real DOM with the same font the PDF uses, so 1mm is plenty; anything
    // larger silently steals space from the bottom of every page.
    const safetyBuffer = 6;
    const effectivePage1Height = PAGE1_CONTENT_HEIGHT_PX - safetyBuffer;
    const effectivePageHeight = PAGE_CONTENT_HEIGHT_PX - safetyBuffer;
    
    // Helper to finalize body page before appendix
    const finalizeBodyPageBeforeAppendix = () => {
      // A blank page with nothing written on it yet is still a page.
      if (currentPage.blockIndices.length > 0 || currentPage.blankPageId) {
        result.push(currentPage);
      } else if (result.length === 0) {
        // First page with no content yet
        result.push(currentPage);
      }
    };
    
    // The content area of an appendix page: A4 less its 15mm top and 30mm
    // bottom padding, the box a table's rows are fitted into.
    const APPENDIX_CONTENT_HEIGHT_PX =
      PAGE_HEIGHT_PX - (APPENDIX_TOKENS.padTopMm + APPENDIX_TOKENS.padBottomMm) * MM_TO_PX - safetyBuffer;

    // Distribute blocks across pages (signature is now a block type)
    document.content.forEach((block, index) => {
      // Page-owning blocks start new page sections. Each kind decides how
      // many pages it needs; the body resumes on a fresh page after it.
      if (APPENDIX_BLOCK_TYPES.has(block.type)) {
        // If this is the first appendix, finalize body pages with signature
        if (!hasSeenAppendix) {
          finalizeBodyPageBeforeAppendix();
          hasSeenAppendix = true;
        } else if (currentPage.blockIndices.length > 0 || currentPage.blankPageId) {
          // Not first appendix, but we have body content (or a blank page,
          // which is a page even before anything is written on it) - push it
          result.push(currentPage);
        }

        const pushAppendixPage = (
          kind: AppendixKind,
          pageIndex: number,
          totalPages: number,
          extra: Partial<PageContent> = {}
        ) => {
          result.push({
            pageNumber: result.length + 1,
            blockIndices: pageIndex === 0 ? [index] : [], // Only the first page carries the block index
            items: pageIndex === 0 ? [{ blockIndex: index }] : [],
            type: 'appendix',
            appendixKind: kind,
            appendixBlockId: block.id,
            appendixPageIndex: pageIndex,
            appendixTotalPages: totalPages,
            ...extra,
          });
        };

        if (block.type === 'image_appendix') {
          // Each page's photos and layout come from photoAppendixPages, and
          // the PDF draws the page from this fragment rather than slicing the
          // photos again: with a page laid out on its own, the pages are no
          // longer one size and a second slicing would disagree.
          const fragments = photoAppendixPages(block.data as ImageAppendixBlockData);
          fragments.forEach((photoFragment, i) => pushAppendixPage('photo', i, fragments.length, { photoFragment }));
        } else if (block.type === 'plan_appendix') {
          // One page per plan: a level, a wing, a site. Never fewer than one.
          const count = Math.max(1, planPages(block.data as PlanAppendixBlockData).length);
          for (let i = 0; i < count; i++) pushAppendixPage('plan', i, count);
        } else if (block.type === 'pdf_attachment') {
          const attachment = block.data as PdfAttachmentBlockData;
          const count = Math.max(1, attachment.pages.length);
          for (let i = 0; i < count; i++) {
            const size = attachment.pages[i];
            pushAppendixPage('attachment', i, count, size
              ? { pageSize: { widthPt: size.widthPt, heightPt: size.heightPt } }
              : {});
          }
        } else if (block.type === 'table_appendix') {
          // Cut BETWEEN rows, like the instruction list, but against the
          // appendix page's box: the title once above the first slice, the
          // column header above every slice. A section heading is never left
          // as the last row of a page -- it moves down with the row it heads.
          const rows = (block.data as TableAppendixBlockData).rows || [];
          const measured = blockHeights[block.id];
          const headHeight = measured?.tableHeadHeight ?? 0;
          const leadInHeight = measured?.leadInHeight ?? 0;
          const fragments: RowFragment[] = [];

          // Heights by ROW, not by position. A row just added or removed
          // shifts every index after it, and before this the whole table fell
          // back to a single page until the measurement pass caught up: a
          // visible collapse, and the page that held the row the author
          // clicked unmounted -- taking its pending caret with it, so a row
          // added on page 2 was never focused. A row not measured yet (the
          // one just added) borrows the shortest measured height, which for a
          // blank row is what it measures at; the next pass corrects it.
          const measuredKeys = measured?.rowKeys ?? [];
          const measuredHeights = measured?.rowHeights ?? [];
          const heightByKey = new Map(measuredKeys.map((key, i) => [key, measuredHeights[i]]));
          // The shortest of each kind: a blank row measures as a one-line row,
          // and a section heading is shorter than either, so the fallback must
          // come from the same kind or a new row is placed too short and jumps
          // a page once measured. Keyed on kind as well as id, so a row just
          // turned into a heading (or back) is "not measured yet" too.
          const shortest = (kind: 'row' | 'section') => {
            const heights = measuredKeys
              .map((key, i) => (key.endsWith(`/${kind}`) ? measuredHeights[i] : Infinity))
              .filter(h => h !== Infinity);
            return heights.length > 0 ? Math.min(...heights) : Math.min(...measuredHeights);
          };
          const rowHeights = rows.map(row => heightByKey.get(tableRowKey(row.id, row.kind)) ?? shortest(row.kind));

          if (heightByKey.size === 0 || rows.length === 0) {
            // Not measured yet (or empty): one page holding everything, which
            // the measurement pass corrects a moment later.
            fragments.push({ rowStart: 0, rowEnd: rows.length, part: 0, parts: 1 });
          } else {
            let rowFrom = 0;
            let guard = 0;
            while (rowFrom < rows.length && guard++ < 200) {
              const available = APPENDIX_CONTENT_HEIGHT_PX - headHeight - (rowFrom === 0 ? leadInHeight : 0);
              let rowTo = rowFrom;
              let used = 0;
              while (rowTo < rows.length && used + rowHeights[rowTo] <= available) {
                used += rowHeights[rowTo];
                rowTo++;
              }
              // A page has to take at least one row, however tall.
              if (rowTo === rowFrom) rowTo = rowFrom + 1;
              // Keep a heading with the row under it.
              if (rowTo < rows.length && rowTo - rowFrom > 1 && rows[rowTo - 1].kind === 'section') rowTo--;
              fragments.push({ rowStart: rowFrom, rowEnd: rowTo, part: fragments.length, parts: 0 });
              rowFrom = rowTo;
            }
          }
          fragments.forEach((fragment, i) => {
            pushAppendixPage('table', i, fragments.length, {
              rowFragment: { ...fragment, parts: fragments.length },
            });
          });
        }

        // Start new body page for any content after the appendix. A blank
        // page ends at a page-owning block: what follows is the letter's
        // flow again ("Continued") until the next marker.
        currentBlankPage = null;
        currentPage = newBodyPage(result.length + 1);
        currentHeight = 0;
        return;
      }

      // A blank page's marker: a fresh page, which this and every body page
      // after it (until the next page-owning block) is named by. The marker
      // is NOT placed on the page -- neither surface draws anything for it,
      // so the first real block starts the page with no gap above it on
      // either. (Placed as a zero-height block it still cost the editor a
      // wrapper's line while the PDF drew nothing: 25pt of drift.)
      if (block.type === 'blank_page') {
        currentBlankPage = block.id;
        if (currentPage.items.length > 0) startNewPage();
        else currentPage.blankPageId = block.id;
        return;
      }

      // Regular block handling
      const measured: MeasuredBlock = blockHeights[block.id]
        || { height: getEstimatedBlockHeight(block), gapAbove: 0 };
      const limitFor = () => (currentPage.pageNumber === 1 ? effectivePage1Height : effectivePageHeight);

      // A row-based block is cut BETWEEN rows: an instruction is never split in
      // half, a drawing never straddles a page, and a QA result is never parted
      // from the reference underneath it.
      //
      // They differ only in what sits above the rows. An instruction list
      // repeats its table header on every page it appears on; a drawing list
      // says its two lead-in sentences once, above the first fragment only; a
      // QA list has neither, so both terms are zero.
      if (ROW_BLOCK_TYPES.has(block.type)
          && measured.rowHeights && measured.rowHeights.length > 0) {
        const rowHeights = measured.rowHeights;
        const headHeight = measured.tableHeadHeight ?? 0;
        const leadInHeight = measured.leadInHeight ?? 0;
        const rowCount = rowHeights.length;
        const fragments: Array<{ rowStart: number; rowEnd: number }> = [];
        let rowFrom = 0;
        let guard = 0;

        while (rowFrom < rowCount && guard++ < 200) {
          const pageEmpty = currentPage.items.length === 0;
          // No gap above the first block on a page, nor above a continuation.
          const gapAbove = pageEmpty || rowFrom > 0 ? 0 : measured.gapAbove;
          // Repeated above every fragment; the lead-in only above the first.
          const chrome = headHeight + (rowFrom === 0 ? leadInHeight : 0);
          const available = limitFor() - currentHeight - gapAbove - chrome;

          let rowTo = rowFrom;
          let used = 0;
          while (rowTo < rowCount && used + rowHeights[rowTo] <= available) {
            used += rowHeights[rowTo];
            rowTo++;
          }

          if (rowTo === rowFrom) {
            // Nothing fits. Move to a fresh page and try again -- unless this
            // IS a fresh page, in which case one item is taller than a whole
            // page and there is nothing to do but place it and let it run over.
            if (!pageEmpty) {
              startNewPage();
              continue;
            }
            rowTo = rowFrom + 1;
            used = rowHeights[rowFrom];
          }

          fragments.push({ rowStart: rowFrom, rowEnd: rowTo });
          currentHeight += gapAbove + chrome + used;
          rowFrom = rowTo;

          if (rowFrom < rowCount) {
            addRowFragments(fragments, index);
            startNewPage();
          }
        }
        addRowFragments(fragments, index, true);
        return;
      }

      // Paragraphs with measured lines can be cut between lines
      const lines = block.type === 'paragraph' ? measured.lines : undefined;
      const lineCount = lines ? lines.lineStarts.length : 0;
      const lineHeight = lines?.lineHeightPx || 0;

      if (lines && lineCount > 1 && lineHeight > 0) {
        const pieces: Array<{ lineStart: number; lineEnd: number }> = [];
        let lineFrom = 0;
        let guard = 0;
        // A blank line made by forced breaks starts at the same character
        // offset as the line after it, so a cut between them cannot be
        // expressed as a text slice (the slicer keeps the breaks with the
        // earlier piece). Step such a cut back to the last unambiguous line.
        const unambiguousCut = (cut: number): number => {
          let c = cut;
          while (c > lineFrom && c < lineCount && lines.lineStarts[c] === lines.lineStarts[c + 1]) c--;
          return c;
        };
        while (lineFrom < lineCount && guard++ < 200) {
          const remaining = lineCount - lineFrom;
          const isFirstPiece = lineFrom === 0;
          const pageEmpty = currentPage.items.length === 0;
          // The first block on a page never has a gap above it (CSS :first-child);
          // a continuation piece never has one either
          const gapAbove = pageEmpty || !isFirstPiece ? 0 : measured.gapAbove;
          const available = limitFor() - currentHeight - gapAbove;
          const remainingHeight = isFirstPiece ? measured.height : remaining * lineHeight;

          if (remainingHeight <= available) {
            pieces.push({ lineStart: lineFrom, lineEnd: lineCount });
            currentHeight += gapAbove + remainingHeight;
            lineFrom = lineCount;
            break;
          }

          // Lines that physically fit, reduced so that at least MIN_LINES_EACH_SIDE
          // remain for the next page (Word cuts a line earlier rather than
          // leaving a widow); the piece kept here must also reach the minimum.
          const fitRaw = Math.min(Math.floor(available / lineHeight), remaining - MIN_LINES_EACH_SIDE);
          const fit = fitRaw > 0 ? unambiguousCut(lineFrom + fitRaw) - lineFrom : fitRaw;
          if (fit >= MIN_LINES_EACH_SIDE) {
            // Cut here and continue on the next page
            pieces.push({ lineStart: lineFrom, lineEnd: lineFrom + fit });
            lineFrom += fit;
            addPieces(pieces, index, block, lines, false);
            startNewPage();
            continue;
          }

          if (!pageEmpty) {
            // Too little room for a clean cut: move the rest to a fresh page
            startNewPage();
            continue;
          }

          // A fresh page and it still does not fit: cut as far as possible
          const forcedRaw = Math.max(1, Math.min(Math.floor(available / lineHeight), remaining - 1));
          const forced = Math.max(1, unambiguousCut(lineFrom + forcedRaw) - lineFrom);
          pieces.push({ lineStart: lineFrom, lineEnd: lineFrom + forced });
          lineFrom += forced;
          addPieces(pieces, index, block, lines, false);
          startNewPage();
        }
        addPieces(pieces, index, block, lines, true);
        return;
      }

      // Use different page height for page 1 (has letterhead, no top padding)
      const currentEffectiveHeight = limitFor();

      // The first block on a page never has a gap above it (CSS :first-child)
      const gapAbove = currentPage.items.length === 0 ? 0 : measured.gapAbove;

      // Check if this block fits on current page
      if (currentHeight + gapAbove + measured.height > currentEffectiveHeight && currentPage.items.length > 0) {
        // Block doesn't fit, push current page and start new one
        startNewPage();
        addItem({ blockIndex: index });
        currentHeight += measured.height;
      } else {
        addItem({ blockIndex: index });
        currentHeight += gapAbove + measured.height;
      }
    });

    // Second pass: a paragraph placed in several pieces needs each piece to
    // know the total, so PDF and editor can tell first/middle/last apart
    const partCounts = new Map<number, number>();
    for (const page of result.concat(currentPage)) {
      for (const item of page.items) {
        if (item.fragment) partCounts.set(item.blockIndex, (partCounts.get(item.blockIndex) || 0) + 1);
      }
    }
    for (const page of result.concat(currentPage)) {
      for (const item of page.items) {
        if (item.fragment) {
          const parts = partCounts.get(item.blockIndex) || 1;
          if (parts <= 1) {
            delete item.fragment; // ended up on one page after all
          } else {
            item.fragment = { ...item.fragment, parts };
          }
        }
      }
    }
    
    // Finalize remaining content
    if (!hasSeenAppendix) {
      // No appendix - just push current page if it has content
      if (currentPage.blockIndices.length > 0 || currentPage.blankPageId) {
        result.push(currentPage);
      }
    } else if (currentPage.blockIndices.length > 0 || currentPage.blankPageId) {
      // Has appendix and content after it - add the remaining body content
      // (signature was already placed before appendix)
      result.push(currentPage);
    }
    
    // If we have no pages yet (edge case), add at least one body page
    if (result.length === 0) {
      result.push(newBodyPage(1));
    }

    // "Page n of N" counts the report's own pages; an attached PDF's pages
    // are merged in unnumbered.
    let reportPage = 0;
    for (const page of result) {
      if (page.appendixKind !== 'attachment') page.reportPageNumber = ++reportPage;
    }

    // A blank page's pages are numbered within it for the rail ("Blank page 2/3").
    const textPages = new Map<string, PageContent[]>();
    for (const page of result) {
      if (page.blankPageId) {
        const list = textPages.get(page.blankPageId) ?? [];
        list.push(page);
        textPages.set(page.blankPageId, list);
      }
    }
    textPages.forEach(list => list.forEach((page, i) => {
      page.appendixPageIndex = i;
      page.appendixTotalPages = list.length;
    }));

    return result;
  }, [document?.content, blockHeights, headerHeight, PAGE1_CONTENT_HEIGHT_PX, PAGE_CONTENT_HEIGHT_PX, PAGE_HEIGHT_PX, MM_TO_PX]);

  // Total page count
  const totalPages = pages.length;
  /** Pages that carry a footer -- the N in "Page n of N". */
  const reportPageCount = useMemo(
    () => pages.filter(p => p.appendixKind !== 'attachment').length,
    [pages]
  );
  /**
   * The page a certificate's trustee line goes on, or null for a template that
   * numbers its pages instead (see TEMPLATES_WITH_CERTIFICATE_FOOTER). The last
   * page DocumentPDF draws: an attached PDF's pages are merged in after render
   * and carry no footer of any kind -- the rule reportPageCount counts by.
   * Passed to DocumentPDF rather than worked out again there, so the two
   * surfaces cannot choose different pages.
   */
  const certificateFooterPage = useMemo(() => {
    if (!TEMPLATES_WITH_CERTIFICATE_FOOTER.has(template?.code ?? '')) return null;
    const drawn = pages.filter(p => p.appendixKind !== 'attachment');
    return drawn.length > 0 ? drawn[drawn.length - 1].pageNumber : null;
  }, [pages, template?.code]);
  /** The appendix sections in order, for the rail's move buttons. */
  const appendixSections = useMemo(
    () => appendixSectionIds(document?.content ?? []),
    [document?.content]
  );
  /**
   * Every photo's number, once per content change. The table and plan pages,
   * their action bars and the Place menu all read it; each working it out
   * for itself walked the whole document once per page per render.
   */
  const photoNumberMap = useMemo(
    () => photoNumbers(document?.content ?? []),
    [document?.content]
  );

  /** Every photo in the document, numbered, for a table row's photo picker. */
  const tablePhotos = useMemo((): TablePhotoOption[] => {
    const out: TablePhotoOption[] = [];
    for (const block of document?.content ?? []) {
      if (block.type !== 'image_appendix') continue;
      for (const img of (block.data as ImageAppendixBlockData).images || []) {
        const number = photoNumberMap.get(img.id);
        if (number == null) continue;
        out.push({
          id: img.id,
          number,
          caption: img.caption || '',
          url: img.storagePath ? appendixImageUrls[img.storagePath] ?? null : null,
        });
      }
    }
    return out.sort((a, b) => a.number - b.number);
  }, [document?.content, photoNumberMap, appendixImageUrls]);

  /**
   * Leave the editor, back to wherever it was opened from.
   *
   * The register is only the default. Opened from a project's Documents tab or
   * from an inspection row in Construction Admin, the caller puts the page it
   * was on -- and what that page needs to put its modal back on the right tab --
   * into history state, and closing returns there instead of dumping the user on
   * /documents with their modal gone.
   */
  /**
   * Open another document on this job from the left panel.
   *
   * location.state is carried across deliberately: it holds the `returnTo` the
   * project modal put there, and closeEditor below reads it to get back. Drop
   * it and Escape would land the user in the register instead of where they
   * came from.
   *
   * Nothing is flushed here on purpose -- the editor is keyed on documentId, so
   * this navigation unmounts it and the unmount effect saves. See the comment
   * on that key in Documents/index.tsx.
   */
  const openSibling = useCallback((siblingId: string) => {
    navigate(`/documents/${siblingId}`, { state: location.state });
  }, [navigate, location.state]);

  const closeEditor = useCallback(() => {
    const returnTo = (location.state as { returnTo?: { pathname: string; search?: string; state?: unknown } } | null)?.returnTo;
    if (returnTo?.pathname) {
      navigate({ pathname: returnTo.pathname, search: returnTo.search ?? '' }, { state: returnTo.state ?? null });
      return;
    }
    navigate('/documents');
  }, [navigate, location.state]);

  // Whatever has keyboard focus and holds an edit: a LiveText or paragraph
  // (committed on a short timer, so the last moment's keystrokes are only in
  // the DOM) or a sidebar input (the SI number commits by RPC on blur).
  // Blurring it is how Escape ends an edit and how an exit path takes the
  // edit before saving. Returns whether anything was blurred.
  const blurActiveField = (): boolean => {
    const active = window.document.activeElement;
    if (!(active instanceof HTMLElement)) return false;
    if (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT') {
      active.blur();
      return true;
    }
    return false;
  };

  // Escape closes the editor -- unless something is being typed in, in which
  // case it ends that edit and a second press closes. Closing straight from
  // inside a field was the shortest route to losing it: the field was
  // unmounted with the keystrokes of the last moment uncommitted (a removed
  // element fires no blur in Chrome), and Escape is a natural way to
  // "finish" a cell.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A dialog owns Escape while it is open. Closing the editor underneath
      // a conflict dialog navigated into the leave guard, whose own dialog
      // replaced the conflict one and left its answer -- and every later
      // save -- waiting forever.
      if (window.document.querySelector('.confirm-dialog')) return;
      if (blurActiveField()) return;
      closeEditor();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeEditor]);

  // ⌘, toggles a subscript in whichever text field is being typed in -- every
  // block's, through one listener, so a new block gets it without asking
  // (subscript.ts). The browser's own use of ⌘, (settings) is refused only
  // inside a field of this document.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSubscriptShortcut(e)) return;
      const active = window.document.activeElement;
      if (!(active instanceof HTMLElement) || !active.isContentEditable) return;
      if (!active.closest('.document-editor')) return;
      e.preventDefault();
      toggleSubscript();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // =======================================
  // AUTO-SAVE
  // =======================================

  // Apply a local edit. The ref is the source of truth and is updated
  // synchronously, so several edits in one event each build on the previous
  // one, and an edit made while the editor is unmounting (a paragraph
  // committing its last keystrokes) still reaches the unmount flush.
  const applyDocumentUpdate = useCallback((updater: (prev: Document) => Document) => {
    const prev = documentRef.current;
    if (!prev) return;
    const next = updater(prev);
    documentRef.current = next;
    setDocument(next);
    changeCounterRef.current += 1;
    setDirtyTick(changeCounterRef.current);
    setHasUnsavedChanges(true);
  }, []);

  /**
   * Apply a change the server already made -- a status advanced by one of the
   * signing RPCs -- without treating it as a local edit.
   *
   * applyDocumentUpdate arms the autosave, which is wrong twice over here: the
   * value came FROM the server, and the document it lands on has just left
   * Draft. The save it armed was refused by the edit lock and surfaced as
   * "Could not save" a couple of seconds after signing, on a document that had
   * in fact saved fine.
   */
  const applyServerUpdate = useCallback((updater: (prev: Document) => Document) => {
    const prev = documentRef.current;
    if (!prev) return;
    const next = updater(prev);
    documentRef.current = next;
    setDocument(next);
  }, []);

  // NOTE: status is deliberately NOT written here. The document lifecycle
  // (Draft -> Awaiting Approval -> Signed -> Sent) is owned entirely by the
  // lifecycle RPCs (sign/submit/reopen/unsign, mark/unmark sent). If the content autosave also wrote status it
  // could write a STALE status onto a document an RPC had just advanced --
  // surfacing as "Invalid document status transition ...". Content saves only
  // ever run on a Draft (see flushBeforeLifecycleChange and isEditing), so they
  // never need to touch status.
  const documentPayload = (doc: Document) => ({
    header_fields: doc.header_fields,
    content: doc.content,
    title: doc.title,
    signatory_id: doc.signatory_id
  });

  // Snapshot the current document into document_versions
  const createVersion = useCallback(async (doc: Document) => {
    if (!session?.user) return;
    const { data: profileData } = await supabase
      .from('profiles')
      .select('team_member_id')
      .eq('id', session.user.id)
      .single();

    versionCountRef.current += 1;

    await supabase
      .from('document_versions')
      .insert({
        document_id: doc.id,
        version_number: versionCountRef.current,
        title: doc.title,
        header_fields: doc.header_fields,
        content: doc.content,
        status: doc.status,
        signatory_id: doc.signatory_id,
        created_by: profileData?.team_member_id
      });
  }, [session, supabase]);

  // A save that failed is tried again on a backoff while anything is unsaved,
  // so a network blip or an expired token when the debounce fired no longer
  // leaves "Could not save" on screen until the next keystroke. A new edit
  // re-arms the ordinary debounce and drops the retry; a successful save
  // resets the backoff.
  const scheduleRetry = useCallback(() => {
    if (retryTimeoutRef.current) return;
    const attempt = retryAttemptRef.current;
    retryAttemptRef.current = attempt + 1;
    const delay = Math.min(2000 * 2 ** attempt, 30000);
    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;
      if (changeCounterRef.current !== savedCounterRef.current) void saveDocumentRef.current(false);
    }, delay);
  }, []);

  // Save the latest document. Reads from refs so it never writes a stale copy,
  // and only clears the dirty flag if nothing changed while the request ran.
  const saveDocument = useCallback(async (withVersion: boolean = false) => {
    if (savingRef.current) {
      // A save is running; run again when it finishes so nothing is skipped,
      // and remember if that run must also write a version snapshot
      saveAgainRef.current = true;
      if (withVersion) pendingVersionRef.current = true;
      return;
    }
    const doc = documentRef.current;
    if (!doc) return;
    if (!session?.user) {
      // Between auth events the session can be momentarily absent; the edits
      // are still here, so try again rather than consume the debounce.
      scheduleRetry();
      return;
    }
    // Only a Draft is editable: every field in the editor is disabled once the
    // document leaves it, and enforce_document_edit_lock rejects the write
    // anyway. Callers are expected to flush before a lifecycle change rather
    // than rely on this, but a save that slips through afterwards would fail
    // and be reported to the user as lost work, so it stops here.
    if (doc.status !== 'Draft') return;
    if (conflictPendingRef.current) return;

    savingRef.current = true;
    const counterAtStart = changeCounterRef.current;
    let succeeded = false;
    // Run again straight away (a benign conflict was resolved, or the user
    // chose to keep their version).
    let retryNow = false;
    const payload = documentPayload(doc);
    const expectedUpdatedAt = loadedUpdatedAtRef.current;

    /**
     * The conditional update matched no row: updated_at moved since it was
     * last seen. Most such moves are not conflicts at all -- a renumbering
     * RPC, a lifecycle action, anything that touched another column -- so
     * the server's copy of the saved columns is compared against what was
     * last persisted from here. Equal means only metadata changed: adopt
     * the new updated_at and save again. Different means another tab or
     * device saved content after this one loaded it, and the user decides.
     */
    const resolveConflict = async (): Promise<'retry' | 'stop'> => {
      const { data: server, error: readError } = await supabase
        .from('documents')
        .select('updated_at, header_fields, content, title, signatory_id')
        .eq('id', doc.id)
        .single();
      if (readError || !server) {
        console.error('Error reading document after a conflicting save:', readError);
        setSaveStatus('error');
        scheduleRetry();
        return 'stop';
      }
      const serverUpdatedAt = server.updated_at as string;

      // The row has not moved, so the update did match it and was refused
      // anyway: a policy, not a conflict. No retry can change that, and the
      // conflict dialog would loop ("Keep my version" matching nothing again).
      if (serverUpdatedAt === expectedUpdatedAt) {
        console.error('Document save affected no rows although the row is unchanged: refused by policy.');
        setSaveStatus('error');
        void notify({
          title: 'This document cannot be saved',
          message: <p>The server refused the change. The document may not be yours to edit, or your access may have changed. The edits made here are not saved.</p>,
        });
        return 'stop';
      }

      if (stableStringify(persistedOf(server)) === stableStringify(lastPersistedRef.current)) {
        loadedUpdatedAtRef.current = serverUpdatedAt;
        return 'retry';
      }

      conflictPendingRef.current = true;
      setSaveStatus('error');
      try {
        const keepMine = await confirm({
          title: 'This document was changed somewhere else',
          message: (
            <>
              <p>It was saved from another tab or device after you opened it here, so the two copies now differ.</p>
              <p><strong>Keep my version</strong> overwrites that copy with this one. <strong>Load theirs</strong> reloads the saved copy and drops the changes made here since it was opened.</p>
            </>
          ),
          confirmLabel: 'Keep my version',
          cancelLabel: 'Load theirs…',
          tone: 'warning',
        });
        if (keepMine) {
          loadedUpdatedAtRef.current = serverUpdatedAt;
          return 'retry';
        }
        // The destructive choice is confirmed on its own, so dismissing
        // either dialog (overlay, X, Escape) changes nothing: the edits stay
        // here, unsaved, and the next save asks again.
        const loadTheirs = await confirm({
          title: 'Load the other copy?',
          message: <p>The changes made here since the document was opened will be lost. This cannot be undone.</p>,
          confirmLabel: 'Load theirs and discard mine',
          cancelLabel: 'Go back',
          tone: 'danger',
        });
        if (!loadTheirs) return 'stop';
        // Reload the server's copy. The local edits are given up explicitly,
        // so neither the unload flush nor its prompt may run.
        discardLocalRef.current = true;
        savedCounterRef.current = changeCounterRef.current;
        window.location.reload();
        return 'stop';
      } finally {
        conflictPendingRef.current = false;
      }
    };

    try {
      setSaveStatus('saving');

      // Read document_number back: a Draft title change re-expands it in the
      // database (sync_document_number), so the toolbar would otherwise keep
      // showing the number the document was created with until a reload.
      // And updated_at, which the next save is conditional on.
      let update = supabase
        .from('documents')
        .update(payload)
        .eq('id', doc.id);
      if (expectedUpdatedAt) update = update.eq('updated_at', expectedUpdatedAt);
      const { data: savedRow, error: updateError } = await update
        .select('document_number, updated_at')
        .maybeSingle();

      if (updateError) {
        console.error('Error saving document:', updateError);
        setSaveStatus('error');
        scheduleRetry();
        return;
      }

      if (!savedRow) {
        retryNow = (await resolveConflict()) === 'retry';
        return;
      }

      loadedUpdatedAtRef.current = savedRow.updated_at as string;
      lastPersistedRef.current = persistedOf(payload);
      retryAttemptRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (savedRow.document_number) {
        setDocument(prev =>
          prev && prev.document_number !== savedRow.document_number
            ? { ...prev, document_number: savedRow.document_number }
            : prev
        );
      }

      if (withVersion) {
        await createVersion(doc);
      }

      savedCounterRef.current = counterAtStart;
      succeeded = true;
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      if (changeCounterRef.current === counterAtStart) {
        setHasUnsavedChanges(false);
      }

      // Update the documents list cache with the new document_date
      queryClient.setQueriesData(
        { queryKey: ['get_filtered_documents'] },
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((listDoc: any) =>
              listDoc.id === doc.id
                ? {
                    ...listDoc,
                    document_date: doc.header_fields?.document_date || listDoc.document_date,
                    title: doc.title,
                    status: doc.status
                  }
                : listDoc
            )
          };
        }
      );

      // Reset to idle after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);

    } catch (err) {
      console.error('Error saving document:', err);
      setSaveStatus('error');
      scheduleRetry();
    } finally {
      savingRef.current = false;
      const runAgain = saveAgainRef.current;
      const versionStillOwed = pendingVersionRef.current;
      saveAgainRef.current = false;
      pendingVersionRef.current = false;
      if (retryNow) {
        void saveDocument(withVersion || versionStillOwed);
      } else if (succeeded && (runAgain || versionStillOwed || changeCounterRef.current !== savedCounterRef.current)) {
        // Edits arrived during a successful save (or a snapshot was
        // requested): persist them now, without another debounce. After a
        // FAILED save the backoff retry owns the next attempt.
        void saveDocument(versionStillOwed);
      }
    }
  }, [session, supabase, queryClient, createVersion, confirm, notify, scheduleRetry]);
  saveDocumentRef.current = saveDocument;

  // Debounced auto-save: 2s after the last edit
  useEffect(() => {
    if (dirtyTick === 0) return;
    if (changeCounterRef.current === savedCounterRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    // A fresh edit takes over from a pending retry: this timer will save,
    // and if it fails the backoff starts again from the short end.
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    retryAttemptRef.current = 0;
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveDocument(false);
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [dirtyTick, saveDocument]);

  // Last-resort save that survives page unload. supabase-js cannot set
  // keepalive, so this posts straight to PostgREST with the session token.
  const flushSaveKeepalive = useCallback(() => {
    const doc = documentRef.current;
    const token = accessTokenRef.current;
    if (!doc || !token || doc.status !== 'Draft') return false;
    const client = supabase as unknown as { supabaseUrl?: string; supabaseKey?: string };
    if (!client.supabaseUrl || !client.supabaseKey) return false;
    const body = JSON.stringify(documentPayload(doc));
    // keepalive requests are limited to 64KB; larger documents rely on the prompt
    if (body.length > 60 * 1024) return false;
    // Conditional like every other save: this one gets no reply, so it must
    // not be the one that overwrites a newer copy.
    const condition = loadedUpdatedAtRef.current
      ? `&updated_at=eq.${encodeURIComponent(loadedUpdatedAtRef.current)}`
      : '';
    try {
      void fetch(`${client.supabaseUrl}/rest/v1/documents?id=eq.${encodeURIComponent(doc.id)}${condition}`, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          apikey: client.supabaseKey,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal'
        },
        body
      });
      return true;
    } catch (err) {
      console.error('Error flushing document save:', err);
      return false;
    }
  }, [supabase]);

  // Flush unsaved edits when the editor closes (Escape, close button, in-app
  // navigation) instead of dropping the pending debounce with them. The page
  // is still alive here, so the ordinary save path (auth, retries via the
  // in-flight coalescing, no size cap) is used; if a save is already running
  // it is simply asked to run once more with the latest content.
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (discardLocalRef.current) return;
      if (changeCounterRef.current !== savedCounterRef.current) {
        void saveDocument(false);
      }
    };
  }, [saveDocument]);

  // Tab close / reload: send the latest content with keepalive so it survives
  // the unload, and still let the browser ask before leaving - the request
  // may fail (expired token, network) and there is no way to know in time.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (discardLocalRef.current) return;
      // The field being typed in commits on a short timer; the keystrokes of
      // the last moment are still only in the DOM, so take them now. The
      // sidebar's number box commits by RPC on blur, so it is blurred too.
      blurActiveField();
      if (changeCounterRef.current === savedCounterRef.current) return;
      flushSaveKeepalive();
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushSaveKeepalive]);

  // Leaving the editor in-app -- Escape, the close button, a sibling in the
  // left panel, the browser's Back -- is held until the unsaved edits are
  // written, and asked about if they cannot be. Before this, in-app
  // navigation got one unmount flush whose failure went to the console along
  // with the component, and Back got nothing at all.
  const shouldBlock = useCallback(
    () => !discardLocalRef.current && changeCounterRef.current !== savedCounterRef.current,
    []
  );
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let cancelled = false;
    const settled = () => new Promise<void>(resolve => {
      const tick = (): void => {
        if (savingRef.current) setTimeout(tick, 50);
        else resolve();
      };
      tick();
    });
    (async () => {
      // A conflict dialog is waiting for its answer; leaving now would stack
      // a second dialog on it and strand the first. Stay until it is answered.
      if (conflictPendingRef.current) {
        blocker.reset();
        return;
      }
      // Nothing else blurs the field being typed in on a Back navigation.
      blurActiveField();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (documentRef.current?.status === 'Draft') await saveDocument(false);
      await settled();
      if (cancelled) return;
      if (changeCounterRef.current === savedCounterRef.current) {
        blocker.proceed();
        return;
      }
      const leave = await confirm({
        title: 'Your latest changes could not be saved',
        message: (
          <>
            <p>The document could not be written to the server. Stay to try again, or leave and lose the changes made since it last saved.</p>
          </>
        ),
        confirmLabel: 'Leave without saving',
        cancelLabel: 'Stay',
        tone: 'danger',
      });
      if (cancelled) return;
      if (leave) {
        discardLocalRef.current = true;
        blocker.proceed();
      } else {
        blocker.reset();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blocker, saveDocument, confirm]);

  // =======================================
  // UPDATE HANDLERS
  // =======================================

  const updateHeaderField = useCallback((
    field: keyof DocumentHeaderFields,
    value: string,
    // Whether this field is also the document's title. Driven by the header
    // field schema rather than hard-coded to 'report_title', because each
    // chrome has a different field that plays that role.
    syncsTitle = false
  ) => {
    applyDocumentUpdate(prev => ({
      ...prev,
      header_fields: {
        ...prev.header_fields,
        [field]: value
      },
      title: syncsTitle ? value : prev.title
    }));
  }, [applyDocumentUpdate]);

  /**
   * Renumber a draft.
   *
   * Goes through set_document_sequence() rather than the autosave: the number
   * has a unique index behind it and document_number is derived from it, so a
   * second writer racing the RPC would let the two disagree. The RPC returns
   * both, and they are written back together.
   */
  const commitSequence = useCallback(async () => {
    if (!document || document.status !== 'Draft') return;
    const parsed = parseInt(sequenceInput, 10);
    if (Number.isNaN(parsed) || parsed === document.sequence_number) {
      setSequenceInput(document.sequence_number != null ? String(document.sequence_number) : '');
      return;
    }

    const { data, error: seqError } = await supabase.rpc('set_document_sequence', {
      p_document_id: document.id,
      p_sequence: parsed,
    });

    if (seqError) {
      notifyReadOnly(seqError);
      // A rejected number is a recoverable answer to something the user typed --
      // most often "that one is already used on this job", now that hand-added
      // inspections draw from the same sequence. setError() is the editor's
      // FATAL state (document not found, load failed) and replaces the whole
      // page with a "Back to Documents" screen, which threw the user out of the
      // document over a typo.
      void notify({
        title: 'That number is taken',
        message: <p>{seqError.message}</p>,
        tone: 'warning',
      });
      setSequenceInput(document.sequence_number != null ? String(document.sequence_number) : '');
      return;
    }

    setDocument(prev => prev && ({
      ...prev,
      sequence_number: data.sequence_number as number,
      document_number: data.document_number as string,
    }));
    setSequenceInput(String(data.sequence_number));
  }, [document, sequenceInput, supabase, notify]);

  const refreshProjectData = useCallback(async () => {
    if (!document?.project_id) return;
    // The stage is re-read too: its own primary contact, care-of and head
    // contractor take precedence over the project's, so a refresh that kept
    // the stage as first loaded would miss a change made to it since.
    const [projectRes, stageRes] = await Promise.all([
      supabase
        .from('projects')
        .select(PROJECT_SELECT)
        .eq('id', document.project_id)
        .single(),
      document.stage_id
        ? supabase
            .from('project_stages')
            .select(STAGE_SELECT)
            .eq('id', document.stage_id)
            .single()
        : Promise.resolve(null),
    ]);
    if (projectRes.error || !projectRes.data) {
      console.error('Error refreshing project data:', projectRes.error);
      return;
    }
    if (stageRes?.error) console.error('Error refreshing stage data:', stageRes.error);
    const fetched = projectRes.data as unknown as ProjectData;
    const freshStage = stageRes && !stageRes.error
      ? (stageRes.data as unknown as ProjectData['stage'])
      : undefined;
    setProjectData(prev => ({ ...fetched, stage: freshStage !== undefined ? freshStage : prev?.stage ?? null }));
  }, [document?.project_id, document?.stage_id, supabase]);

  const updateSignatory = useCallback((signatoryId: string | null) => {
    applyDocumentUpdate(prev => ({
      ...prev,
      signatory_id: signatoryId
    }));
  }, [applyDocumentUpdate]);

  const updateBlocks = useCallback((blocks: Block[]) => {
    applyDocumentUpdate(prev => ({
      ...prev,
      content: blocks
    }));
  }, [applyDocumentUpdate]);

  const addBlock = useCallback((type: BlockType) => {
    const newBlock = createBlock(type);
    applyDocumentUpdate(prev => ({
      ...prev,
      content: [...prev.content, newBlock]
    }));
  }, [applyDocumentUpdate]);

  /**
   * Put the caret in a block that is about to render (Enter's new paragraph),
   * at its start or end -- as soon as React has committed it, not after a
   * fixed delay.
   *
   * It used to wait 150ms (two stacked timers), long enough for the caret to
   * be seen sitting at the front of the old block before it moved down. The
   * delay was covering one real case: a new block at the bottom of a page is
   * rendered there first and then moved to the next page once the layout is
   * re-measured, which remounts it and drops the focus. So for a few frames
   * after placing the caret, if the block's element has been replaced and
   * nothing else has taken the focus, it is placed again in the new element.
   */
  const focusBlockWhenRendered = useCallback((blockId: string, atEnd?: boolean) => {
    const find = () => window.document.querySelector(
      `.editor-main [data-block-id="${blockId}"] [contenteditable="true"]`
    ) as HTMLElement | null;
    const place = (el: HTMLElement) => {
      el.focus();
      const range = window.document.createRange();
      range.selectNodeContents(el);
      range.collapse(!atEnd); // true = start, false = end
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    };
    let placed: HTMLElement | null = null;
    let frames = 0;
    const tick = () => {
      const el = find();
      if (el && el !== placed) {
        const active = window.document.activeElement;
        // Only re-place when the focus went nowhere (the element it was in
        // was removed); never take it from something the user clicked.
        const focusIsFree = !placed || !active || active === window.document.body || active === placed;
        if (focusIsFree) {
          place(el);
          placed = el;
        }
      }
      // ~0.4s: covers the re-measure that moves a block to the next page.
      if (++frames < 24) requestAnimationFrame(tick);
    };
    // First attempt in a microtask: the caller is inside the Enter keydown,
    // whose state update React commits in a microtask of its own, queued
    // first. Waiting even one frame let a fast typist's next keystrokes land
    // in the OLD block ("Line" typed after Enter came out as "LineLine" and
    // an empty paragraph below it).
    queueMicrotask(tick);
  }, []);

  /**
   * Add a signature block at the end of the LETTER -- after its last block,
   * before the first appendix -- which is where the paginator closes the
   * letter's pages. addBlock appends to the end of the content, which on a
   * report with appendices put the signature on a "Continued" page after the
   * last of them.
   */
  const addLetterSignature = useCallback(() => {
    const signature = createBlock('signature');
    applyDocumentUpdate(prev => {
      const at = letterEndIndex(prev.content);
      return { ...prev, content: [...prev.content.slice(0, at), signature, ...prev.content.slice(at)] };
    });
  }, [applyDocumentUpdate]);

  /**
   * Add a paragraph to the LETTER: before the first page-owning block or
   * text appendix, and before the signature when the letter ends with one --
   * a paragraph after "Yours faithfully" is never what was meant. Appending
   * to the end of the content, as addBlock does, would put it after the
   * appendices, on an untitled "Continued" page.
   */
  const addLetterParagraph = useCallback(() => {
    const paragraph = createBlock('paragraph');
    applyDocumentUpdate(prev => {
      const content = prev.content;
      let at = letterEndIndex(content);
      // Step back over a trailing signature (and anything after it that is
      // still the letter's sign-off, such as "Encl.").
      const signatureAt = content.slice(0, at).map(b => b.type).lastIndexOf('signature');
      if (signatureAt !== -1) at = signatureAt;
      return { ...prev, content: [...content.slice(0, at), paragraph, ...content.slice(at)] };
    });
  }, [applyDocumentUpdate]);


  // Reload the document's lifecycle state after a signing action. The RPCs move
  // the status server-side, so the client must read it back rather than guess.
  const refreshLifecycle = useCallback(async () => {
    if (!documentId) return;

    const { data } = await supabase
      .from('documents')
      .select('status, sent_at, sent_by, updated_at, header_fields, content, title, signatory_id, document_signatures (slot_key, slot_label, signed_by, signed_at, signer_name, signer_position_title, signer_registration_number, signature_storage_path, signature_scale, signature_offset_x_mm, signature_offset_y_mm)')
      .eq('id', documentId)
      .single();

    if (!data) return;

    const nextStatus = data.status as DocumentStatus;
    setSignatures((data.document_signatures as unknown as DocumentSignature[]) || []);
    // The saved columns as the server now has them. A lifecycle RPC moves
    // updated_at and may rewrite header_fields (the issue snapshot); the next
    // content save is conditional on the one and compared against the other,
    // so both are adopted here or the first save after a reopen would be
    // reported as a conflict with... this same user. The content itself is
    // adopted only when nothing is unsaved locally, which the flush before
    // every lifecycle action normally guarantees.
    const serverPayload = {
      header_fields: data.header_fields as Document['header_fields'],
      content: (data.content ?? []) as Document['content'],
      title: data.title as Document['title'],
      signatory_id: data.signatory_id as Document['signatory_id'],
    };
    loadedUpdatedAtRef.current = data.updated_at as string;
    lastPersistedRef.current = persistedOf(serverPayload);
    const nothingUnsaved = changeCounterRef.current === savedCounterRef.current;
    applyServerUpdate(prev => ({
      ...prev,
      status: nextStatus,
      sent_at: data.sent_at as string | null,
      sent_by: data.sent_by as string | null,
      ...(nothingUnsaved ? serverPayload : {}),
    }));

    // Every list that shows this document's status is cached and outlives the
    // editor. The content autosave patches the register's rows, but a lifecycle
    // RPC never saves, so without this a document signed, sent or reopened here
    // went on showing its old status in the register after closing -- and in
    // the left panel beside it -- until something else refetched.
    queryClient.invalidateQueries({ queryKey: ['get_filtered_documents'] });
    const current = documentRef.current;
    if (current) {
      queryClient.invalidateQueries({
        queryKey: ['document_siblings', current.project_id, current.stage_id],
      });
    }
  }, [documentId, supabase, applyServerUpdate, queryClient]);

  // Flush any pending edits before a lifecycle action. Once the document leaves
  // Draft the edit lock refuses content changes, so an unsaved keystroke would
  // otherwise be stranded and surface later as "Could not save".
  const flushBeforeLifecycleChange = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    // Only a Draft is editable, so only a Draft can have pending edits to flush.
    // A document that has left Draft (e.g. Awaiting Approval, being signed by a
    // certifier or manager) is frozen -- saving its content would be rejected by
    // the edit lock and surface as a spurious "Could not save".
    if (documentRef.current?.status === 'Draft') {
      await saveDocument(false);
    }
  }, [saveDocument]);

  /**
   * Report a lifecycle action that failed.
   *
   * `lifecycleError` is rendered in exactly one place -- inside the signing
   * modal -- so it is the right home for handleSign's failures and no use at
   * all to the toolbar buttons, which have no modal open. Setting it there
   * left Submit, Withdraw and Return to draft looking like dead buttons: the
   * RPC was refused, the message was stored, and nothing was ever drawn.
   *
   * A dialog rather than a toast because these messages are not status
   * chatter. The lifecycle RPCs are written to be read verbatim and several
   * are instructions the user has to act on ("SI 5 is already recorded
   * against another inspection on this job. Renumber one of them before
   * issuing SI 5."), which a toast would time out from under them.
   */
  const reportLifecycleFailure = useCallback((err: unknown, fallback: string) => {
    // A QA read-only rejection has its own global toast and its own wording;
    // showing the raw 'read_only_qa' alongside it would say the same thing twice
    // and worse.
    if (getReadOnlyMessage(err)) {
      notifyReadOnly(err);
      return;
    }
    void notify({
      title: 'Could not update this document',
      // The RPC's messages are written to be shown to the user verbatim.
      message: <p>{(err as { message?: string })?.message || fallback}</p>,
    });
  }, [notify]);

  const handleSign = useCallback(async (slot: SignatureSlot) => {
    if (!document) return;

    setIsSigning(true);
    setLifecycleError(null);
    try {
      await flushBeforeLifecycleChange();

      const { error } = await supabase.rpc('sign_document', {
        p_document_id: document.id,
        p_slot_key: slot.key
      });

      // Treat "already signed" as success -- the slot is filled, which is the
      // outcome the click wanted -- so a redundant call closes the dialog rather
      // than showing a confusing error.
      const msg = (error as { message?: string } | null)?.message || '';
      if (error && !/already (been )?signed/i.test(msg)) throw error;

      await refreshLifecycle();
      setSigningSlot(null);
    } catch (err) {
      console.error('Error signing document:', err);
      notifyReadOnly(err);
      // The RPC's messages are written to be shown to the user verbatim.
      setLifecycleError((err as { message?: string })?.message || 'Could not sign this document.');
    } finally {
      setIsSigning(false);
    }
  }, [document, supabase, refreshLifecycle, flushBeforeLifecycleChange]);

  const handleSubmitForApproval = useCallback(async () => {
    if (!document) return;

    setIsSigning(true);
    try {
      await flushBeforeLifecycleChange();
      const { error } = await supabase.rpc('submit_document_for_approval', { p_document_id: document.id });
      if (error) throw error;
      await refreshLifecycle();
    } catch (err) {
      console.error('Error submitting document:', err);
      reportLifecycleFailure(err, 'Could not submit this document.');
    } finally {
      setIsSigning(false);
    }
  }, [document, supabase, refreshLifecycle, flushBeforeLifecycleChange, reportLifecycleFailure]);

  const handleUnsign = useCallback(async (slotKey: string) => {
    if (!document) return;

    setIsSigning(true);
    try {
      const { error } = await supabase.rpc('unsign_document', {
        p_document_id: document.id,
        p_slot_key: slotKey
      });
      if (error) throw error;
      await refreshLifecycle();
    } catch (err) {
      console.error('Error withdrawing signature:', err);
      reportLifecycleFailure(err, 'Could not withdraw the signature.');
    } finally {
      setIsSigning(false);
    }
  }, [document, supabase, refreshLifecycle, reportLifecycleFailure]);

  const handleReopen = useCallback(async () => {
    if (!document) return;

    // Returning to Draft deletes every signature on the document, and for a
    // template that records an inspection it withdraws that record too. Both
    // are irreversible -- the only way back is to sign again -- so the dialog
    // names what is actually there rather than warning in the abstract.
    const signedLabels = signatures.map(sig => sig.slot_label).filter(Boolean);
    // An unsigned submission is only a request for signatures: pulling it back
    // costs nothing but the hand-over, and saying "signing again" would be wrong.
    const isUnsignedRequest = document.status === 'Awaiting Approval' && signatures.length === 0;
    const wasSent = document.status === 'Sent';
    const proceed = await confirm({
      title: 'Return this document to draft?',
      message: (
        <>
          <p>
            {signedLabels.length > 0
              ? `This deletes the ${signedLabels.join(' and ')} signature${signedLabels.length > 1 ? 's' : ''}.`
              : isUnsignedRequest
                ? 'This withdraws the request for approval so the document can be edited.'
                : 'This returns the document to Draft.'}
            {template?.creates_inspection && isSignedStatus(document.status)
              ? ' Its inspection record is withdrawn from Construction Admin.'
              : ''}
          </p>
          {wasSent && (
            <p>It has already been sent to the client, who will still hold the signed copy.</p>
          )}
          <p>
            {isUnsignedRequest
              ? 'It will need submitting for approval again.'
              : 'It will need signing again.'}
          </p>
        </>
      ),
      confirmLabel: 'Return to draft',
      tone: 'danger',
    });
    if (!proceed) return;

    setIsSigning(true);
    try {
      const { error } = await supabase.rpc('reopen_document', { p_document_id: document.id });
      if (error) throw error;
      // The RPC has already deleted the signatures and moved the status; show
      // that now rather than after the re-read, which is what makes the ink
      // vanish on the click instead of a round trip later. refreshLifecycle
      // then confirms it from the server.
      setSignatures([]);
      applyServerUpdate(prev => ({ ...prev, status: 'Draft', sent_at: null, sent_by: null }));
      await refreshLifecycle();
    } catch (err) {
      console.error('Error reopening document:', err);
      reportLifecycleFailure(err, 'Could not reopen this document.');
    } finally {
      setIsSigning(false);
    }
  }, [document, supabase, refreshLifecycle, applyServerUpdate, signatures, template, confirm, reportLifecycleFailure]);

  /**
   * Signed <-> Sent. Neither touches a signature or the content, and both are
   * undoable in one click, so neither asks for confirmation.
   */
  const handleSetSent = useCallback(async (sent: boolean) => {
    if (!document) return;

    setIsSigning(true);
    try {
      const { error } = await supabase.rpc(
        sent ? 'mark_document_sent' : 'unmark_document_sent',
        { p_document_id: document.id }
      );
      // Already in the state the click asked for (another tab got there first):
      // that is the outcome wanted, so re-read rather than report a failure.
      const msg = (error as { message?: string } | null)?.message || '';
      if (error && !/already marked as sent|is not marked as sent/i.test(msg)) throw error;
      await refreshLifecycle();
    } catch (err) {
      console.error('Error changing sent status:', err);
      reportLifecycleFailure(err, sent ? 'Could not mark this document as sent.' : 'Could not change this document back to signed.');
    } finally {
      setIsSigning(false);
    }
  }, [document, supabase, refreshLifecycle, reportLifecycleFailure]);

  const handleDeleteDocument = useCallback(async () => {
    if (!document) return;

    // A signed document that records an inspection takes that record with it
    // (inspections.document_id cascades), which is a consequence outside this
    // screen entirely -- the row simply disappears from Construction Admin --
    // so it has to be said here.
    const withdrawsInspection = template?.creates_inspection && isSignedStatus(document.status);
    const proceed = await confirm({
      title: 'Delete this document?',
      message: (
        <>
          <p>
            <strong>{document.title || document.document_number}</strong> and all of its
            versions and images will be removed.
            {withdrawsInspection
              ? ' Its inspection record is removed from Construction Admin too.'
              : ''}
          </p>
          <p>This cannot be undone.</p>
        </>
      ),
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!proceed) return;

    setIsDeletingDocument(true);
    // Select back the deleted row so we can detect the silent-RLS case
    // (zero rows affected with no error).
    const { data: deleted, error: deleteError } = await supabase
      .from('documents')
      .delete()
      .eq('id', document.id)
      .select('id');
    if (deleteError) {
      console.error('Error deleting document:', deleteError);
      void notify({
        title: 'Could not delete the document',
        message: <p>{deleteError.message}</p>,
      });
      setIsDeletingDocument(false);
      return;
    }
    if (!deleted || deleted.length === 0) {
      console.error('Document delete returned no rows — likely blocked by RLS.');
      void notify({
        title: 'Could not delete the document',
        message: <p>You do not have permission to delete this document.</p>,
      });
      setIsDeletingDocument(false);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['get_filtered_documents'] });
    // And the builder's left panel, whose cache is keyed on the job and
    // outlives this editor. Left stale it would go on offering the document
    // just deleted, and clicking it lands on "Document not found".
    queryClient.invalidateQueries({
      queryKey: ['document_siblings', document.project_id, document.stage_id],
    });
    // Same way out as any other close: back to whoever opened this. The list
    // being returned to re-fetches when it mounts, so the deleted row is gone
    // from it -- there is no reason to strand the user on the register.
    // There is nothing left to save to, so the leave guard stands down.
    discardLocalRef.current = true;
    closeEditor();
  }, [document, template, supabase, queryClient, closeEditor, confirm, notify]);

  // =======================================
  // APPENDIX UPDATE HANDLERS
  // =======================================

  const updateAppendixBlock = useCallback((blockId: string, updates: Partial<ImageAppendixBlockData>) => {
    applyDocumentUpdate(prev => ({
      ...prev,
      content: prev.content.map(block => {
        if (block.id === blockId && block.type === 'image_appendix') {
          return {
            ...block,
            data: { ...block.data, ...updates } as ImageAppendixBlockData
          };
        }
        return block;
      })
    }));
  }, [applyDocumentUpdate]);

  const handleAppendixColumnsChange = useCallback((blockId: string, columns: 1 | 2) => {
    updateAppendixBlock(blockId, { columns });
  }, [updateAppendixBlock]);

  const handleAppendixAspectChange = useCallback((blockId: string, aspect: ImageAspect) => {
    updateAppendixBlock(blockId, { aspect });
    setShowAspectMenu(false);
  }, [updateAppendixBlock]);

  const handleAppendixRowsChange = useCallback((blockId: string, rows: 1 | 2) => {
    updateAppendixBlock(blockId, { rows });
  }, [updateAppendixBlock]);

  const handleAppendixContinueNumberingChange = useCallback((blockId: string, value: boolean) => {
    updateAppendixBlock(blockId, { continueNumbering: value });
  }, [updateAppendixBlock]);

  const handleAppendixTitleChange = useCallback((blockId: string, title: string) => {
    updateAppendixBlock(blockId, { title });
  }, [updateAppendixBlock]);

  const handleAppendixImageUpload = useCallback(async (blockId: string, files: FileList) => {
    if (files.length === 0) return;

    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;

    setUploadingAppendix(true);
    setAppendixUploadErrors([]);

    // Only the photos this upload adds. They are appended to the block as it
    // is when the upload ENDS (below): it takes seconds a photo, and anything
    // done meanwhile -- a caption typed, a photo moved or removed, a page laid
    // out -- would otherwise be overwritten by the copy taken here.
    const added: ImageAppendixItem[] = [];
    const failures: string[] = [];
    // Each new photo's width / height, read from the JPEG as uploaded.
    const ratios = new Map<string, number>();

    for (const file of Array.from(files)) {
      try {
        if (file.size > 20 * 1024 * 1024) {
          console.warn('Image too large:', file.name);
          failures.push(`${file.name}: larger than 20 MB`);
          continue;
        }

        const newImageId = generateBlockId();

        // HEIC/HEIF (iPhone) photos are converted to JPEG first; other files pass through
        const sourceFile = await toBrowserImage(file);

        // Compress image before upload
        let uploadData: Blob | File = sourceFile;
        if (sourceFile.type.startsWith('image/')) {
          try {
            uploadData = await compressImage(sourceFile, {
              maxWidth: 1920,
              maxHeight: 1920,
              quality: 0.85
            });
          } catch (compressError) {
            console.warn('Compression failed, uploading original:', compressError);
          }
        }

        const fileName = `${crypto.randomUUID()}.jpeg`;
        const filePath = fileName;

        const { error: uploadError } = await supabase.storage
          .from('document-images')
          .upload(filePath, uploadData, {
            contentType: 'image/jpeg'
          });

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          failures.push(`${file.name}: ${uploadError.message || 'upload failed'}`);
          continue;
        }

        // Get signed URL for display
        const { data: urlData } = await supabase.storage
          .from('document-images')
          .createSignedUrl(filePath, 3600);

        if (urlData?.signedUrl) {
          setAppendixImageUrls(prev => ({ ...prev, [filePath]: urlData.signedUrl }));
        }

        const size = await readImageSize(uploadData);
        if (size) ratios.set(newImageId, size.width / size.height);

        added.push({
          id: newImageId,
          imageId: fileName,
          storagePath: filePath,
          caption: ''
        });

      } catch (err) {
        console.error('Error uploading image:', err);
        const reason = err instanceof Error ? err.message : String(err);
        failures.push(`${file.name}: ${reason || 'could not be read'}`);
      }
    }

    const current = documentRef.current?.content.find(b => b.id === blockId);
    if (added.length > 0 && current && current.type === 'image_appendix') {
      const currentData = current.data as ImageAppendixBlockData;
      const newImages = [...(currentData.images || []), ...added];
      // A photo turned the other way to the page it lands on -- a landscape
      // shot on a portrait page -- gets its own aspect, so it is shown whole
      // rather than cropped to the page's frame. The page's grid is left alone.
      photoAppendixPages({ ...currentData, images: newImages }).forEach(page => {
        for (let i = page.start; i < page.end; i++) {
          const ratio = ratios.get(newImages[i].id);
          const aspect = ratio ? uploadAspectFor(ratio, page.layout.aspect) : undefined;
          if (aspect) newImages[i] = { ...newImages[i], aspect };
        }
      });
      updateAppendixBlock(blockId, { images: newImages });
    }
    setAppendixUploadErrors(failures);
    setUploadingAppendix(false);

    // Clear file input
    if (appendixFileInputRef.current) {
      appendixFileInputRef.current.value = '';
    }
  }, [supabase, updateAppendixBlock]);

  // Remove and reorder read the block from the ref, like the caption: they
  // also work out the page layouts from it, and a stale render's copy would
  // put back whatever changed since.
  const handleAppendixRemoveImage = useCallback((blockId: string, imageId: string) => {
    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;

    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    const newImages = appendixData.images.filter(img => img.id !== imageId);

    updateAppendixBlock(blockId, {
      images: newImages,
      pageLayouts: handOnPageLayouts(appendixData, imageId),
    });
  }, [updateAppendixBlock]);

  const handleAppendixReorderImage = useCallback((blockId: string, imageId: string, direction: 'up' | 'down') => {
    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;

    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    const index = appendixData.images.findIndex(img => img.id === imageId);
    if (index === -1) return;

    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= appendixData.images.length) return;

    const newImages = [...appendixData.images];
    [newImages[index], newImages[swapWith]] = [newImages[swapWith], newImages[index]];

    updateAppendixBlock(blockId, {
      images: newImages,
      pageLayouts: keepPageLayoutsInPlace(appendixData.pageLayouts, appendixData.images, newImages),
    });
  }, [updateAppendixBlock]);

  /**
   * Lay out the page that starts at `anchorId` on its own, or (null) hand it
   * back to the appendix's layout. The whole layout is stored, not just what
   * was changed, so a later change to the appendix's layout leaves a page that
   * was set by hand as it was set.
   */
  const handleAppendixPageLayoutChange = useCallback((blockId: string, anchorId: string, layout: PhotoPageLayout | null) => {
    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;
    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    const next = { ...(appendixData.pageLayouts || {}) };
    if (layout) next[anchorId] = layout;
    else delete next[anchorId];
    updateAppendixBlock(blockId, { pageLayouts: Object.keys(next).length > 0 ? next : undefined });
  }, [updateAppendixBlock]);

  /** Give one photo its own frame shape, or (undefined) the page's again. */
  const handleAppendixPhotoAspectChange = useCallback((blockId: string, imageId: string, aspect: ImageAspect | undefined) => {
    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;
    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    updateAppendixBlock(blockId, {
      images: appendixData.images.map(img => {
        if (img.id !== imageId) return img;
        const { aspect: _previous, ...rest } = img;
        return aspect ? { ...rest, aspect } : rest;
      }),
    });
    setPhotoAspectMenuFor(null);
  }, [updateAppendixBlock]);

  // Reads the block from the ref, not from `document`: a caption commits as
  // it is typed, and a commit made in the same tick as another edit (the
  // field unmounting, say) would otherwise be built on a stale render and
  // overwrite it.
  const handleAppendixCaptionChange = useCallback((blockId: string, imageId: string, caption: string) => {
    const appendixBlock = documentRef.current?.content.find(b => b.id === blockId);
    if (!appendixBlock || appendixBlock.type !== 'image_appendix') return;

    const appendixData = appendixBlock.data as ImageAppendixBlockData;
    const newImages = appendixData.images.map(img =>
      img.id === imageId ? { ...img, caption } : img
    );

    updateAppendixBlock(blockId, { images: newImages });
  }, [updateAppendixBlock]);

  const triggerAppendixFileUpload = useCallback((blockId: string) => {
    setFocusedAppendixId(blockId);
    appendixFileInputRef.current?.click();
  }, []);

  const handleAppendixFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (focusedAppendixId && e.target.files && e.target.files.length > 0) {
      handleAppendixImageUpload(focusedAppendixId, e.target.files);
    }
  }, [focusedAppendixId, handleAppendixImageUpload]);

  const handleDeleteAppendix = useCallback((blockId: string) => {
    if (!document) return;
    const content = document.content;
    const index = content.findIndex(b => b.id === blockId);
    let newContent: Block[];
    if (index !== -1 && content[index].type === 'blank_page') {
      // A blank page is its marker AND the body blocks after it, up to the
      // next page-owning block or marker: deleting the page deletes what was
      // written on it.
      let end = index + 1;
      while (end < content.length && !APPENDIX_BLOCK_TYPES.has(content[end].type) && content[end].type !== 'blank_page') end++;
      newContent = [...content.slice(0, index), ...content.slice(end)];
    } else {
      newContent = content.filter(b => b.id !== blockId);
    }
    // A location plan numbering its markers from this table would be left
    // with a pin per row and nothing to write in any of them -- empty
    // circles, printed. It goes back to photo numbers with its markers
    // cleared, which the confirmation has already said.
    newContent = newContent.map(b => {
      if (b.type !== 'plan_appendix') return b;
      const plan = b.data as PlanAppendixBlockData;
      if (plan.labelSource !== 'item' || plan.tableBlockId !== blockId) return b;
      const data = normalisePlan(plan);
      return {
        ...b,
        data: { ...data, labelSource: 'image', tableBlockId: null, pages: (data.pages ?? []).map(pg => ({ ...pg, markers: [] })) },
      };
    });
    updateBlocks(newContent);
    setDeleteAppendixConfirm(null);
    setFocusedAppendixId(null);
  }, [document, updateBlocks]);

  // =======================================
  // TABLE / PLAN / ATTACHMENT APPENDICES
  // =======================================

  /** Replace one appendix block's data wholesale (the page components hand back the full shape). */
  const updateBlockData = useCallback((blockId: string, data: Block['data']) => {
    applyDocumentUpdate(prev => ({
      ...prev,
      content: prev.content.map(block => (block.id === blockId ? { ...block, data } : block)),
    }));
  }, [applyDocumentUpdate]);

  /**
   * Swap an appendix section -- a page-owning block, or a blank page with
   * what is written on it -- with its neighbour in the given direction. They
   * are filtered out of the body canvases (a blank page's marker is never
   * placed at all), so the block handle's move menu cannot reach them; this
   * is their only reordering. See moveAppendixSection.
   */
  const moveAppendixBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    applyDocumentUpdate(prev => {
      const content = moveAppendixSection(prev.content, blockId, direction);
      return content === prev.content ? prev : { ...prev, content };
    });
  }, [applyDocumentUpdate]);

  const handleFillFromPhotos = useCallback((blockId: string) => {
    const current = documentRef.current;
    if (!current) return;
    const { blocks, added } = fillTableFromPhotos(current.content, blockId);
    if (added === 0) {
      void notify({
        title: 'Nothing to add',
        message: <p>Every photo in the document already has a row in this table.</p>,
      });
      return;
    }
    updateBlocks(blocks);
  }, [updateBlocks, notify]);

  const updateTableColumns = useCallback((blockId: string, columns: TableAppendixColumn[]) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'table_appendix') return;
    updateBlockData(blockId, { ...(block.data as TableAppendixBlockData), columns });
  }, [updateBlockData]);

  /** Append a row or a section heading to the end of a table appendix. */
  const appendTableRow = useCallback((blockId: string, kind: 'row' | 'section') => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'table_appendix') return;
    const data = block.data as TableAppendixBlockData;
    updateBlockData(blockId, { ...data, rows: [...data.rows, kind === 'row' ? blankTableRow() : blankTableSection()] });
  }, [updateBlockData]);

  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  /**
   * The one table row whose photo picker is open. Here rather than in the
   * table page, so it follows its row to whichever page holds it and there
   * is never more than one.
   */
  const [tablePicker, setTablePicker] = useState<{ blockId: string; rowId: string } | null>(null);

  // --- plan ---
  //
  // A plan appendix is a set of PAGES, one plan each (a level, a wing). Every
  // handler here addresses a page; placing and removing markers address the
  // whole appendix, because the pool of photos is shared across its pages.

  const planFileInputRef = useRef<HTMLInputElement>(null);
  /** Which plan page the next chosen file goes to. */
  const [planUploadTarget, setPlanUploadTarget] = useState<{ blockId: string; pageIndex: number } | null>(null);
  /** The one plan page with a bitmap operation in flight, and which. */
  const [planBusy, setPlanBusy] = useState<{ blockId: string; pageIndex: number; what: 'upload' | 'rotate' | 'trim' } | null>(null);
  const [showPlanSourceMenu, setShowPlanSourceMenu] = useState(false);
  const [showPlaceMenu, setShowPlaceMenu] = useState(false);

  // Close an open action-bar menu on a click anywhere outside it, or Escape.
  // Losing the appendix's focus (below) was the only other way to close one,
  // so a click elsewhere on the same page -- the plan, the table -- left it
  // open until its own button was pressed again. Pointerdown, not click, so a
  // drag that starts on the page closes it too; a press inside any dropdown
  // (its button or its menu) is left to that dropdown.
  const anyActionMenuOpen = showAspectMenu || showPageLayoutMenu || photoAspectMenuFor != null
    || showPlanSourceMenu || showPlaceMenu || showColumnsMenu;
  useEffect(() => {
    if (!anyActionMenuOpen) return;
    const closeAll = () => {
      setShowAspectMenu(false);
      setShowPageLayoutMenu(false);
      setPhotoAspectMenuFor(null);
      setShowPlanSourceMenu(false);
      setShowPlaceMenu(false);
      setShowColumnsMenu(false);
    };
    // A press inside one menu closes every other: a photo's aspect menu and a
    // bar menu could otherwise both be open, each skipping the other's press.
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.('.photo-aspect-control')) {
        setShowAspectMenu(false);
        setShowPageLayoutMenu(false);
        setShowPlanSourceMenu(false);
        setShowPlaceMenu(false);
        setShowColumnsMenu(false);
        return;
      }
      if (target?.closest?.('.appendix-action-bar .dropdown-container')) {
        setPhotoAspectMenuFor(null);
        return;
      }
      closeAll();
    };
    // Escape closes the menu and ONLY the menu: the editor closes itself on
    // Escape from a window listener, so the event is stopped here (capture,
    // on the document, runs first) or one press would close both.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      closeAll();
    };
    window.document.addEventListener('pointerdown', onPointerDown, true);
    window.document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.document.removeEventListener('pointerdown', onPointerDown, true);
      window.document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [anyActionMenuOpen]);
  /** Which page of the focused appendix was clicked, so one bar shows. */
  const [focusedAppendixPageIndex, setFocusedAppendixPageIndex] = useState(0);

  /** Replace one page of a plan appendix, converting the old single-plan shape on the way. */
  const updatePlanPage = useCallback((blockId: string, pageIndex: number, patch: Partial<PlanPage>) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const data = normalisePlan(block.data as PlanAppendixBlockData);
    updateBlockData(blockId, {
      ...data,
      pages: (data.pages ?? []).map((p, i) => (i === pageIndex ? { ...p, ...patch } : p)),
    });
  }, [updateBlockData]);

  /**
   * Swap a plan page's bitmap for a rotated or trimmed one, re-mapping its
   * markers onto the new image. The markers are read HERE, when the new
   * bitmap is ready, not when the operation started: the download, re-encode
   * and upload take seconds, and a pin dragged or placed meanwhile would
   * otherwise be overwritten by the snapshot taken before them. The page is
   * found by id (a page removed meanwhile is skipped), and nothing is written
   * if its plan was replaced in the meantime -- the new bitmap was made from
   * the old one.
   */
  const replacePlanBitmap = useCallback((
    blockId: string,
    planId: string,
    fromPath: string,
    bitmap: { storagePath: string; naturalWidth: number; naturalHeight: number },
    remap: (marker: PlanMarker) => PlanMarker
  ) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const pages = planPages(block.data as PlanAppendixBlockData);
    const index = pages.findIndex(p => p.id === planId);
    const latest = pages[index];
    if (!latest || latest.storagePath !== fromPath) return;
    updatePlanPage(blockId, index, { ...bitmap, markers: latest.markers.map(remap) });
  }, [updatePlanPage]);

  const addPlanPage = useCallback((blockId: string) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const data = normalisePlan(block.data as PlanAppendixBlockData);
    updateBlockData(blockId, { ...data, pages: [...(data.pages ?? []), blankPlanPage()] });
  }, [updateBlockData]);

  const removePlanPage = useCallback(async (blockId: string, pageIndex: number) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const data = normalisePlan(block.data as PlanAppendixBlockData);
    const pages = data.pages ?? [];
    if (pages.length <= 1) return;
    const target = pages[pageIndex];
    if (target && (target.storagePath || target.markers.length > 0)) {
      const proceed = await confirm({
        title: 'Remove this plan page?',
        message: (
          <p>
            Its plan{target.markers.length > 0 ? ` and ${target.markers.length} placed marker${target.markers.length === 1 ? '' : 's'}` : ''} will
            be removed from the appendix. The photos themselves are untouched.
          </p>
        ),
        confirmLabel: 'Remove page',
        tone: 'danger',
      });
      if (!proceed) return;
    }
    updateBlockData(blockId, { ...data, pages: pages.filter((_, i) => i !== pageIndex) });
    setFocusedAppendixPageIndex(Math.max(0, pageIndex - 1));
  }, [updateBlockData, confirm]);

  const triggerPlanUpload = useCallback((blockId: string, pageIndex: number) => {
    setPlanUploadTarget({ blockId, pageIndex });
    planFileInputRef.current?.click();
  }, []);

  /** Store a plan bitmap and make it viewable; returns its path. */
  const storePlanBitmap = useCallback(async (bitmap: Blob): Promise<string> => {
    const storagePath = `${crypto.randomUUID()}.jpeg`;
    const { error: uploadError } = await supabase.storage
      .from('document-images')
      .upload(storagePath, bitmap, { contentType: 'image/jpeg' });
    if (uploadError) throw new Error(uploadError.message || 'upload failed');
    const { data: urlData } = await supabase.storage.from('document-images').createSignedUrl(storagePath, 3600);
    if (urlData?.signedUrl) setAppendixImageUrls(prev => ({ ...prev, [storagePath]: urlData.signedUrl }));
    return storagePath;
  }, [supabase]);

  /**
   * A plan arrives as an image or as a PDF (a marked-up drawing sheet). Either
   * way it is stored as one JPEG, larger than a photo -- line work has to stay
   * legible across a 170mm box -- and its pixel size is recorded so both
   * surfaces fit the same box from it.
   */
  const handlePlanUpload = useCallback(async (blockId: string, pageIndex: number, file: File) => {
    setPlanBusy({ blockId, pageIndex, what: 'upload' });
    try {
      let source: Blob;
      if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
        // Rasterised at high quality: the trim below re-encodes it once more.
        const { pages } = await rasterisePdf(file, PLAN_LONG_SIDE_PX, [0], 0.95);
        source = pages[0].blob;
      } else {
        const browserFile = await toBrowserImage(file);
        source = await compressImage(browserFile, { maxWidth: PLAN_LONG_SIDE_PX, maxHeight: PLAN_LONG_SIDE_PX, quality: 0.95 });
      }

      // The sheet's white margins are trimmed so the drawing's own extents are
      // what gets fitted to the page. The markers are new, so nothing to remap.
      const { blob: bitmap, width, height } = await trimBitmap(source);
      const storagePath = await storePlanBitmap(bitmap);
      updatePlanPage(blockId, pageIndex, { storagePath, naturalWidth: width, naturalHeight: height });
    } catch (err) {
      console.error('Error uploading plan:', err);
      void notify({
        title: 'The plan could not be added',
        message: <p>{err instanceof Error ? err.message : 'The file could not be read.'}</p>,
      });
    } finally {
      setPlanBusy(null);
      if (planFileInputRef.current) planFileInputRef.current.value = '';
    }
  }, [storePlanBitmap, updatePlanPage, notify]);

  const handlePlanFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (planUploadTarget && file) void handlePlanUpload(planUploadTarget.blockId, planUploadTarget.pageIndex, file);
  }, [planUploadTarget, handlePlanUpload]);

  /**
   * Turn a plan page 90 degrees clockwise. The stored bitmap is rotated and
   * re-uploaded (the old file is left in the bucket, as every replaced upload
   * is), and the page's markers turn with it: a point at fraction (x, y) of
   * the unrotated image sits at (1 - y, x) of the rotated one, and its arrow
   * swings the same 90 degrees. Rendering is untouched -- both surfaces go on
   * fitting an upright image into the page box.
   */
  const rotatePlan = useCallback(async (blockId: string, pageIndex: number) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const plan = planPages(block.data as PlanAppendixBlockData)[pageIndex];
    if (!plan?.storagePath) return;
    setPlanBusy({ blockId, pageIndex, what: 'rotate' });
    try {
      const { data: file, error: downloadError } = await supabase.storage
        .from('document-images')
        .download(plan.storagePath);
      if (downloadError || !file) throw new Error(downloadError?.message || 'The plan could not be downloaded');

      const { blob, width, height } = await rotateBitmap(file, 90);
      const storagePath = await storePlanBitmap(blob);
      replacePlanBitmap(
        blockId,
        plan.id,
        plan.storagePath,
        { storagePath, naturalWidth: width, naturalHeight: height },
        m => ({ ...m, x: 1 - m.y, y: m.x, rotation: ((m.rotation || 0) + 90) % 360 })
      );
    } catch (err) {
      console.error('Error rotating plan:', err);
      void notify({
        title: 'The plan could not be rotated',
        message: <p>{err instanceof Error ? err.message : 'Please try again.'}</p>,
      });
    } finally {
      setPlanBusy(null);
    }
  }, [supabase, storePlanBitmap, replacePlanBitmap, notify]);

  /**
   * Trim the white margins off a plan uploaded before trimming happened at
   * upload (or after a rotate of such a plan). Same shape as rotatePlan: the
   * stored bitmap is replaced and the page's markers are re-mapped onto the
   * new bounds so each stays on the spot it marked.
   */
  const trimPlan = useCallback(async (blockId: string, pageIndex: number) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const plan = planPages(block.data as PlanAppendixBlockData)[pageIndex];
    if (!plan?.storagePath) return;
    setPlanBusy({ blockId, pageIndex, what: 'trim' });
    try {
      const { data: file, error: downloadError } = await supabase.storage
        .from('document-images')
        .download(plan.storagePath);
      if (downloadError || !file) throw new Error(downloadError?.message || 'The plan could not be downloaded');

      const { blob, width, height, crop } = await trimBitmap(file);
      if (!crop) {
        void notify({
          title: 'Nothing to trim',
          message: <p>The plan already fills its image -- there are no white margins to remove.</p>,
        });
        return;
      }

      const storagePath = await storePlanBitmap(blob);
      const fromPath = plan.storagePath;
      replacePlanBitmap(
        blockId,
        plan.id,
        fromPath,
        { storagePath, naturalWidth: width, naturalHeight: height },
        m => ({ ...m, ...remapForCrop(m.x, m.y, crop) })
      );
    } catch (err) {
      console.error('Error trimming plan:', err);
      void notify({
        title: 'The plan could not be trimmed',
        message: <p>{err instanceof Error ? err.message : 'Please try again.'}</p>,
      });
    } finally {
      setPlanBusy(null);
    }
  }, [supabase, storePlanBitmap, replacePlanBitmap, notify]);

  /**
   * Change what an appendix's markers number. Existing markers, on every
   * page, point at photos or rows of the old source, so they cannot survive
   * the switch; the user is told before they go.
   */
  const setPlanLabelSource = useCallback(async (blockId: string, value: string) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const data = normalisePlan(block.data as PlanAppendixBlockData);
    const next: Partial<PlanAppendixBlockData> = value === 'image'
      ? { labelSource: 'image', tableBlockId: null }
      : { labelSource: 'item', tableBlockId: value.slice('item:'.length) };
    if (next.labelSource === data.labelSource && (next.tableBlockId ?? null) === (data.tableBlockId ?? null)) return;
    const count = planMarkersAll(data).length;
    if (count > 0) {
      const proceed = await confirm({
        title: 'Remove the placed markers?',
        message: (
          <p>
            The {count} marker{count === 1 ? '' : 's'} in this appendix refer to the current source and
            will be removed. You can place them again from the Place menu.
          </p>
        ),
        confirmLabel: 'Change and remove markers',
        tone: 'warning',
      });
      if (!proceed) return;
    }
    updateBlockData(blockId, { ...data, ...next, pages: (data.pages ?? []).map(p => ({ ...p, markers: [] })) });
  }, [updateBlockData, confirm]);

  /**
   * Drop markers for photos or table rows near the middle of one plan page;
   * the user drags them from there. Several placed at once cascade down and
   * to the right rather than landing on one spot, so each is still visible
   * and grabbable.
   */
  const placeMarkers = useCallback((blockId: string, pageIndex: number, links: Array<{ rowId?: string; imageId?: string }>) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix' || links.length === 0) return;
    const plan = planPages(block.data as PlanAppendixBlockData)[pageIndex];
    if (!plan) return;
    const added: PlanMarker[] = links.map((link, i) => {
      const step = (plan.markers.length + i) % 8;
      return {
        id: generateBlockId(),
        x: 0.4 + step * 0.03,
        y: 0.4 + step * 0.03,
        rotation: 0,
        rowId: link.rowId ?? null,
        imageId: link.imageId ?? null,
      };
    });
    updatePlanPage(blockId, pageIndex, { markers: [...plan.markers, ...added] });
  }, [updatePlanPage]);

  /** Remove matching markers from EVERY page of the appendix. */
  const removeMarker = useCallback((blockId: string, predicate: (m: PlanMarker) => boolean) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'plan_appendix') return;
    const data = normalisePlan(block.data as PlanAppendixBlockData);
    updateBlockData(blockId, {
      ...data,
      pages: (data.pages ?? []).map(p => ({ ...p, markers: p.markers.filter(m => !predicate(m)) })),
    });
  }, [updateBlockData]);

  // --- attachment ---

  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  const [attachmentTargetId, setAttachmentTargetId] = useState<string | null>(null);
  const [attachmentProgress, setAttachmentProgress] = useState<{ blockId: string; message: string } | null>(null);

  const triggerAttachmentUpload = useCallback((blockId: string) => {
    setAttachmentTargetId(blockId);
    attachmentFileInputRef.current?.click();
  }, []);

  const handleAttachmentUpload = useCallback(async (blockId: string, file: File) => {
    const block = documentRef.current?.content.find(b => b.id === blockId);
    if (!block || block.type !== 'pdf_attachment') return;
    if (file.size > 50 * 1024 * 1024) {
      void notify({ title: 'File too large', message: <p>Attached PDFs are limited to 50 MB.</p> });
      return;
    }
    setAttachmentProgress({ blockId, message: 'Reading pages…' });
    try {
      const uploaded = await uploadPdfAttachment(supabase, file, message => setAttachmentProgress({ blockId, message }));
      // Read the block again now the upload is done: a title typed while it
      // ran must not be overwritten by the one there when it started.
      const latest = documentRef.current?.content.find(b => b.id === blockId);
      if (!latest || latest.type !== 'pdf_attachment') return;
      const current = latest.data as PdfAttachmentBlockData;
      // A title the author typed is kept. One that is still the previous
      // file's own name was never chosen, so a replaced PDF brings its name.
      const autoTitle = current.filename.replace(/\.pdf$/i, '');
      const typed = current.title && current.title !== autoTitle;
      updateBlockData(blockId, {
        ...current,
        ...uploaded,
        title: typed ? current.title : uploaded.title,
      });
    } catch (err) {
      console.error('Error attaching PDF:', err);
      void notify({
        title: 'The PDF could not be attached',
        message: <p>{err instanceof Error ? err.message : 'The file could not be read.'}</p>,
      });
    } finally {
      setAttachmentProgress(null);
      if (attachmentFileInputRef.current) attachmentFileInputRef.current.value = '';
    }
  }, [supabase, updateBlockData, notify]);

  const handleAttachmentFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (attachmentTargetId && file) void handleAttachmentUpload(attachmentTargetId, file);
  }, [attachmentTargetId, handleAttachmentUpload]);

  /**
   * Copy each attached PDF's pages into the rendered report at the position
   * the page plan gave them. Walks the plan in order, advancing an insertion
   * cursor past every report page and inserting a whole attachment when its
   * first page is reached -- so an attachment between two appendices lands
   * between them, and one at the end lands at the end.
   */
  const mergeAttachments = useCallback(async (reportBlob: Blob): Promise<Blob> => {
    const current = documentRef.current;
    if (!current) return reportBlob;
    const { PDFDocument } = await import('pdf-lib');
    const merged = await PDFDocument.load(await reportBlob.arrayBuffer());

    let insertAt = 0;
    for (const page of pages) {
      if (page.appendixKind !== 'attachment') {
        insertAt++;
        continue;
      }
      if (page.appendixPageIndex !== 0) continue;
      const block = current.content.find(b => b.id === page.appendixBlockId);
      const data = block?.data as PdfAttachmentBlockData | undefined;
      if (!data?.storagePath) continue;

      const { data: file, error } = await supabase.storage.from('document-images').download(data.storagePath);
      if (error || !file) throw new Error(`${data.filename || 'attachment'} could not be downloaded`);
      // Loaded with ignoreEncryption only so an encrypted file can be refused
      // by name: pdf-lib cannot decrypt, and copying an encrypted file's
      // streams gives blank or garbled pages with no error. Upload refuses
      // such files (assertMergeable); one attached before that check lands in
      // the export's "could not be merged" dialog instead.
      const source = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true });
      if (source.isEncrypted) throw new Error(`${data.filename || 'An attachment'} is encrypted. ${ENCRYPTED_PDF_MESSAGE}`);
      const copied = await merged.copyPages(source, source.getPageIndices());
      for (const copiedPage of copied) {
        merged.insertPage(insertAt++, copiedPage);
      }
    }

    const bytes = await merged.save();
    return new Blob([bytes], { type: 'application/pdf' });
  }, [pages, supabase]);

  // =======================================
  // COMPUTED VALUES
  // =======================================

  const selectedSignatory = useMemo(() => {
    const found = engineers.find(e => e.id === document?.signatory_id);
    if (!found) return undefined;
    // A certified template requires a CPEng signatory. If the stored signatory
    // is not CPEng (e.g. an older report created before the template became
    // certified, which defaulted to its non-certified author), treat it as
    // unselected so the block shows "Select signatory" and only a CPEng engineer
    // can be chosen.
    const isCertified = (template?.signature_slots as SignatureSlot[] | undefined)
      ?.some(sl => sl.signer === 'certified');
    if (isCertified && !found.is_cpeng) return undefined;
    return found;
  }, [engineers, document?.signatory_id, template]);

  const signatoryName = useMemo(() => 
    selectedSignatory 
      ? `${selectedSignatory.first_name} ${selectedSignatory.last_name}`
      : '',
    [selectedSignatory]
  );

  // Word and image counts for the Info tab
  const documentStats = useMemo(() => {
    const stripTags = (html: string) => html.replace(/<[^>]*>/g, ' ');
    const countWords = (text: string) => text.split(/\s+/).filter(Boolean).length;
    let words = 0;
    let images = 0;
    for (const block of document?.content ?? []) {
      switch (block.type) {
        case 'paragraph':
          words += countWords(stripTags((block.data as { content?: string }).content || ''));
          break;
        case 'heading':
          words += countWords((block.data as { text?: string }).text || '');
          break;
        case 'bullet_list':
          words += ((block.data as { items?: string[] }).items || []).reduce((n, item) => n + countWords(item), 0);
          break;
        case 'scope_list':
          words += ((block.data as { items?: Array<{ text: string }> }).items || []).reduce((n, item) => n + countWords(item.text), 0);
          break;
        case 'table':
          words += ((block.data as { rows?: Array<Array<{ content: string }>> }).rows || [])
            .reduce((n, row) => n + row.reduce((m, cell) => m + countWords(cell.content), 0), 0);
          break;
        case 'image': {
          const data = block.data as { storagePath?: string | null; images?: unknown[] };
          images += (data.storagePath ? 1 : 0) + (data.images?.length || 0);
          break;
        }
        case 'image_appendix':
          images += ((block.data as { images?: unknown[] }).images || []).length;
          break;
        default:
          break;
      }
    }
    return { words, images };
  }, [document?.content]);

  // Refresh the page thumbnails shortly after anything on the pages changes
  useEffect(() => {
    const timer = setTimeout(() => setThumbnailVersion(v => v + 1), 300);
    return () => clearTimeout(timer);
  }, [document?.content, document?.header_fields, pages, appendixImageUrls, signatoryName]);

  // Thumbnails are static, scaled-down clones of the live pages
  useEffect(() => {
    if (activeTab !== 'pages' || sidebarCollapsed) return;
    const pageEls = window.document.querySelectorAll('.editor-main .document-page');
    pages.forEach((page, index) => {
      const host = thumbnailHostsRef.current.get(page.pageNumber);
      const source = pageEls[index] as HTMLElement | undefined;
      if (!host || !source) return;
      const clone = source.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
      clone
        .querySelectorAll([
          '.block-handle, .appendix-action-bar, .image-toolbar, .selection-toolbar, .slash-command-menu, .canvas-click-area, .photo-controls, .remove-btn, .spacer-label, .signature-action-bar, .spacer-toolbar, .table-toolbar',
          ...APPENDIX_EDIT_ONLY_CLASSES,
        ].join(', '))
        .forEach(el => el.remove());
      clone.classList.remove('focused');
      clone.querySelectorAll('.focused').forEach(el => el.classList.remove('focused'));
      // The thumbnail is the printed page: no editing-only tint on the table.
      clone.querySelectorAll('.table-appendix--editing').forEach(el => el.classList.remove('table-appendix--editing'));
      const scale = host.clientWidth / source.offsetWidth;
      clone.style.transform = `scale(${scale})`;
      clone.style.transformOrigin = 'top left';
      clone.style.boxShadow = 'none';
      clone.style.borderRadius = '0';
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      clone.setAttribute('aria-hidden', 'true');
      host.replaceChildren(clone);
    });
  }, [thumbnailVersion, activeTab, sidebarCollapsed, pages]);

  // Track which page is most visible in the editor
  useEffect(() => {
    const root = window.document.querySelector('.editor-main');
    const pageEls = Array.from(window.document.querySelectorAll('.editor-main .document-page'));
    if (!root || pageEls.length === 0) return;
    const ratios = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) ratios.set(entry.target, entry.intersectionRatio);
      let best = 0;
      let bestRatio = -1;
      pageEls.forEach((el, i) => {
        const ratio = ratios.get(el) ?? 0;
        if (ratio > bestRatio) { bestRatio = ratio; best = i; }
      });
      setActivePageIndex(best);
    }, { root, threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
    pageEls.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [pages.length, loading]);

  const scrollToPage = useCallback((index: number) => {
    const main = window.document.querySelector('.editor-main') as HTMLElement | null;
    const pageEls = window.document.querySelectorAll('.editor-main .document-page');
    const target = pageEls[index] as HTMLElement | undefined;
    if (!main || !target) return;
    // Scroll the editor's own pane (not whatever outer container happens to
    // scroll), leaving the same gap above the page as the pane's padding
    const offset = target.getBoundingClientRect().top - main.getBoundingClientRect().top;
    // Assign scrollTop directly (the pane's CSS scroll-behavior makes it smooth)
    main.scrollTop = Math.max(0, main.scrollTop + offset - 24);
  }, []);

  // =======================================
  // PDF EXPORT
  // =======================================

  const handleExportPDF = useCallback(async () => {
    if (!document || !template) return;

    // The builder shows placeholders for these; the PDF would print the label
    // with nothing after it. Which fields matter depends on what the chrome
    // draws, so this is per-chrome rather than always the letter's three.
    const liveContact = effectiveContact(projectData);
    const exportContact = document.header_fields?.contact_name
      || (liveContact ? `${liveContact.first_name} ${liveContact.last_name}`.trim() : '');
    const exportDear = document.header_fields?.dear_name || liveContact?.first_name || '';
    const exportContractor = document.header_fields?.contractor
      || projectData?.stage?.head_contractor?.company_name
      || projectData?.head_contractor?.company_name
      || '';
    const missingFields = (template.chrome === 'site_instruction'
      ? [
          !exportContractor ? 'Contractor' : null,
          document.sequence_number == null ? 'SI No' : null,
        ]
      : [
          !exportContact ? 'Attention (contact name)' : null,
          !exportDear ? 'Dear (salutation)' : null,
          !document.header_fields?.report_title ? 'Report title' : null,
        ]
    ).filter(Boolean);
    if (missingFields.length > 0) {
      const proceed = await confirm({
        title: 'Some header fields are blank',
        message: (
          <>
            <p>These will print blank in the PDF:</p>
            <ul>
              {missingFields.map(field => <li key={String(field)}>{field}</li>)}
            </ul>
          </>
        ),
        confirmLabel: 'Export anyway',
        tone: 'warning',
      });
      if (!proceed) return;
    }

    try {
      setIsExporting(true);

      // Collect every image's storage path, and every frame it is drawn in.
      // A photo is resampled to its frames before react-pdf embeds it (see
      // utils/images): the frames come from the same helpers the PDF draws
      // with, so the pixels are sized for the boxes they land in. A plan
      // page has no frame and is embedded as stored -- its line work was
      // rasterised at a size chosen to stay legible. An appendix photo the
      // page plan does not place (it should not happen) is fetched as stored
      // too.
      const imageFrames = new Map<string, FramePt[]>();
      const want = (path: string | null | undefined, frame?: FramePt) => {
        if (!path) return;
        const frames = imageFrames.get(path) ?? [];
        if (frame) frames.push(frame);
        imageFrames.set(path, frames);
      };

      for (const block of document.content) {
        if (block.type === 'image') {
          const data = block.data as ImageBlockData & { images?: Array<{ storagePath?: string | null }> };
          const paths = [data.storagePath, ...(data.images || []).map(img => img.storagePath)].filter(Boolean);
          const frame = imageBlockFramePt(data, paths.length);
          for (const path of paths) want(path, frame);
        } else if (block.type === 'image_appendix') {
          for (const img of (block.data as ImageAppendixBlockData).images || []) want(img.storagePath);
        } else if (block.type === 'plan_appendix') {
          for (const plan of planPages(block.data as PlanAppendixBlockData)) want(plan.storagePath);
        }
      }
      // Appendix photos are framed by the page plan: the page's layout and
      // the photo's own aspect, exactly as DocumentPDF's renderAppendixPage
      // places them.
      for (const page of pages) {
        if (page.appendixKind !== 'photo' || !page.appendixBlockId) continue;
        const block = document.content.find(b => b.id === page.appendixBlockId);
        if (!block) continue;
        const data = block.data as ImageAppendixBlockData;
        const pageIdx = page.appendixPageIndex || 0;
        const fragment = page.photoFragment ?? photoAppendixPages(data)[pageIdx];
        if (!fragment) continue;
        const grid = photoGrid(fragment.layout, pageIdx === 0);
        for (const img of (data.images || []).slice(fragment.start, fragment.end)) {
          const frame = photoFrame(grid, img.aspect);
          want(img.storagePath, { widthPt: frame.widthPt, heightPt: frame.heightPt });
        }
      }
      const imageStoragePaths = Array.from(imageFrames.keys());

      // Download images, resample each to its frame, and convert to base64
      // for PDF rendering. Bounded parallelism keeps us under the browser's
      // per-host connection limit while still being far faster than serial
      // fetches for large appendices.
      const imageUrls: Record<string, string> = {};
      const failedPaths: string[] = [];

      await mapWithConcurrency(imageStoragePaths, 6, async (storagePath) => {
        try {
          const { data: fileData, error: downloadError } = await supabase.storage
            .from('document-images')
            .download(storagePath);

          if (downloadError || !fileData) {
            console.error('Error downloading image:', storagePath, downloadError);
            failedPaths.push(storagePath);
            return;
          }

          const embedded = await resampleForPdf(fileData, imageFrames.get(storagePath) ?? []);
          imageUrls[storagePath] = await blobToDataUrl(embedded);
        } catch (err) {
          console.error('Error processing image:', storagePath, err);
          failedPaths.push(storagePath);
        }
      });

      if (failedPaths.length > 0) {
        const proceed = await confirm({
          title: 'Some images could not be loaded',
          message: (
            <p>
              {failedPaths.length} of {imageStoragePaths.length} image
              {imageStoragePaths.length === 1 ? '' : 's'} could not be loaded and will be
              missing from the PDF.
            </p>
          ),
          confirmLabel: 'Continue anyway',
          tone: 'warning',
        });
        if (!proceed) {
          setIsExporting(false);
          return;
        }
      }

      // Read each run of measurable text straight from the rendered page, so
      // the PDF reproduces the builder's line breaks exactly rather than
      // re-wrapping with react-pdf's own breaker.
      //
      // Keyed by LAYOUT KEY, not by block id: a paragraph's key is its block
      // id (a nested one inside an instruction row too), while a table
      // appendix cell's is `<blockId>:<rowId>:<columnId>`, the value the cell
      // writes into data-layout-key. One map serves both.
      const paragraphLayouts: Record<string, ParagraphLayout> = {};
      const collectLayout = (key: string, selector: string) => {
        // Content split across pages has one element per page; their lines
        // concatenate in page order into the whole run's layout.
        const els = window.document.querySelectorAll(
          `.editor-main .document-page ${selector}`
        );
        if (els.length > 0) {
          paragraphLayouts[key] = Array.from(els).flatMap(el => measureParagraphLayout(el as HTMLElement));
        }
      };

      // Instruction items hold blocks of their own, so this walks into them.
      // A nested paragraph is keyed by its OWN block id, exactly like a
      // top-level one -- which is why DocumentPDF needs no special case to
      // replay it.
      const collectParagraphs = (blocks: Block[]) => {
        for (const block of blocks) {
          if (block.type === 'paragraph') {
            collectLayout(block.id, `[data-block-id="${block.id}"] .paragraph-content`);
          } else if (block.type === 'instruction_list') {
            for (const row of (block.data as InstructionListBlockData).rows ?? []) {
              collectParagraphs(instructionRowBlocks(row));
            }
          } else if (block.type === 'table_appendix') {
            // Every text cell, keyed by the layout key the cell writes. A row
            // is on exactly one appendix page, so each key matches once.
            const data = block.data as TableAppendixBlockData;
            for (const row of data.rows ?? []) {
              if (row.kind !== 'row') continue;
              for (const column of visibleTableColumns(data.columns ?? [])) {
                if (column.kind === 'item') continue;
                const key = tableCellLayoutKey(block.id, row.id, column.id);
                collectLayout(key, `[data-layout-key="${key}"]`);
              }
            }
          }
        }
      };
      collectParagraphs(document.content);

      // The signature stamp for the PDF needs the image as base64 (react-pdf
      // cannot fetch a URL). Download the author-slot signature once, if signed.
      // Computed locally rather than via the render-time memo so this callback
      // does not depend on a value declared later in the component body.
      const exportAuthorSlot = (template?.signature_slots as SignatureSlot[] | undefined)
        ?.find(sl => sl.signer === 'author' || sl.signer === 'certified');
      const authorSignature = exportAuthorSlot
        ? signatures.find(sig => sig.slot_key === exportAuthorSlot.key)
        : undefined;

      // Unsigned exports carry the nominated signatory's identity with no
      // image, for the same reason the editor preview does: the block must be
      // the same height signed or not, or the PDF paginates differently from
      // the builder that measured it.
      let pdfSignatureStamp: SignatureStamp | null = signatoryInfo
        ? {
            imageUrl: null,
            name: signatoryInfo.name,
            title: signatoryInfo.title,
            registration: signatoryInfo.registration,
            scale: 1,
            offsetXMm: 0,
            offsetYMm: 0,
            aspectRatio: null,
          }
        : null;
      if (authorSignature) {
        let stampBase64: string | null = null;
        let stampAspectRatio: number | null = null;
        const stampPath = authorSignature.signature_storage_path;
        if (stampPath) {
          try {
            const { data: sigData, error: sigError } = await supabase.storage
              .from('signatures')
              .download(stampPath);
            if (sigError || !sigData) {
              console.error('Error downloading signature for PDF:', sigError);
            } else {
              stampBase64 = await blobToDataUrl(sigData);
              // react-pdf cannot size an absolutely positioned image by height
              // alone, so measure the file and let DocumentPDF compute the
              // width. The editor's <img> keeps its ratio natively.
              stampAspectRatio = await new Promise<number | null>((resolve) => {
                const probe = new window.Image();
                probe.onload = () => resolve(
                  probe.naturalHeight > 0 ? probe.naturalWidth / probe.naturalHeight : null
                );
                probe.onerror = () => resolve(null);
                probe.src = stampBase64 as string;
              });
            }
          } catch (err) {
            console.error('Error processing signature for PDF:', err);
          }
        }
        pdfSignatureStamp = {
          imageUrl: stampBase64,
          name: authorSignature.signer_name,
          title: authorSignature.signer_position_title,
          registration: authorSignature.signer_registration_number,
          scale: Number(authorSignature.signature_scale ?? 1),
          offsetXMm: Number(authorSignature.signature_offset_x_mm ?? 0),
          offsetYMm: Number(authorSignature.signature_offset_y_mm ?? 0),
          aspectRatio: stampAspectRatio,
        };
      }

      const pdfDoc = (
        <DocumentPDF
          document={document}
          headerFields={mergeHeaderFields(document.header_fields, template?.chrome ?? 'letter', document.created_at)}
          projectData={projectData}
          chrome={template?.chrome ?? 'letter'}
          showSiteAddress={!TEMPLATES_WITHOUT_PROJECT_ADDRESS.has(template?.code ?? '')}
          certificateFooterPage={certificateFooterPage}
          signatoryName={signatoryName}
          signatureStamp={pdfSignatureStamp}
          imageUrls={imageUrls}
          pages={pages}
          reportPageCount={reportPageCount}
          paragraphLayouts={paragraphLayouts}
        />
      );

      let blob = await pdf(pdfDoc).toBlob();

      // Attached PDFs are merged in AFTER react-pdf has rendered the report:
      // react-pdf cannot embed a PDF, and the attachment's pages must keep
      // their own size (an A3 landscape sheet stays one). DocumentPDF renders
      // nothing for those pages; pdf-lib copies the real ones into the same
      // positions the page plan gave them.
      if (pages.some(p => p.appendixKind === 'attachment')) {
        try {
          blob = await mergeAttachments(blob);
        } catch (mergeError) {
          console.error('Error merging attached PDFs:', mergeError);
          const detail = mergeError instanceof Error && mergeError.message ? ` (${mergeError.message})` : '';
          const proceed = await confirm({
            title: 'An attached PDF could not be merged',
            message: (
              <p>
                The report was generated but its attached PDF pages could not be added{detail}.
                You can export the report without them.
              </p>
            ),
            confirmLabel: 'Export without attachments',
            tone: 'warning',
          });
          if (!proceed) {
            setIsExporting(false);
            return;
          }
        }
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      // The date printed on the page: the override when one is set, else the
      // creation date -- what mergeHeaderFields resolves for the chrome.
      const printedDate = mergeHeaderFields(document.header_fields, template?.chrome ?? 'letter', document.created_at).document_date
        || document.created_at;
      link.download = `${exportFileName(template?.code, document.document_number, displayJobNumberOf(projectData), printedDate)}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting PDF:', error);
      const detail = error instanceof Error && error.message ? ` (${error.message})` : '';
      void notify({
        title: 'The PDF could not be generated',
        message: (
          <p>
            Please try again{detail}. If it keeps failing, check that the document fonts
            are available.
          </p>
        ),
      });
    } finally {
      setIsExporting(false);
    }
  }, [document, template, projectData, signatoryName, signatoryInfo, signatures, supabase, pages, reportPageCount, certificateFooterPage, mergeAttachments, confirm, notify]);

  // =======================================
  // RENDER
  // =======================================

  // --- Lifecycle derivations -------------------------------------------------
  // A document is editable only while Draft; Awaiting Approval, Signed and Sent
  // are all read-only, and the database enforces the same rule.
  const isDraftStatus = document?.status === 'Draft';

  const slots: SignatureSlot[] = useMemo(
    () => (template?.signature_slots as SignatureSlot[] | undefined) || [],
    [template]
  );

  // A certified template requires the signatory to be a certified (CPEng)
  // engineer, so the signatory dropdown is narrowed to them.
  const requiresCertified = useMemo(
    () => slots.some(sl => sl.signer === 'certified'),
    [slots]
  );

  const signatoryEngineers = useMemo(
    () => (requiresCertified ? engineers.filter(e => e.is_cpeng) : engineers),
    [requiresCertified, engineers]
  );

  const signedSlotKeys = useMemo(
    () => new Set(signatures.map(sig => sig.slot_key)),
    [signatures]
  );

  const canSignSlot = useCallback((slot: SignatureSlot): boolean => {
    if (!document || !myTeamMemberId) return false;
    if (signedSlotKeys.has(slot.key)) return false;

    // Mirrors the authorisation in sign_document(). The RPC is the real gate;
    // this only decides which buttons are worth showing.
    switch (slot.signer) {
      case 'author':
        // Only the nominated signatory. No creator fallback, no manager override.
        return myTeamMemberId === document.signatory_id;
      case 'certified':
        // The certified engineer themselves, or any manager on their behalf.
        return myTeamMemberId === document.signatory_id || isManager;
      case 'manager':
        return isManager;
      default:
        return false;
    }
  }, [document, myTeamMemberId, isManager, signedSlotKeys]);

  const mySignableSlots = useMemo(
    () => slots.filter(canSignSlot),
    [slots, canSignSlot]
  );

  // Slots this user signed that can still be withdrawn (only before the document
  // is signed off; after that it takes Return to draft).
  const myWithdrawableSlots = useMemo(() => {
    if (!document || isSignedStatus(document.status) || !myTeamMemberId) return [];
    return signatures
      .filter(sig => sig.signed_by === myTeamMemberId)
      .map(sig => ({ key: sig.slot_key, label: sig.slot_label }));
  }, [document, myTeamMemberId, signatures]);

  /**
   * May this user return the document to draft?
   *
   * Mirrors the authorisation in reopen_document(); the RPC is the real gate
   * and this only decides whether the button is worth showing.
   *
   * A manager may reopen anything, whoever signed it. Below that the rule
   * turns on whether the template needs a CPEng signature: withdrawing a
   * certification is a manager's call alone, because the engineer who wrote
   * the document and asked for the certificate is not the person who gave it.
   * On every other template the person who actually signed may take their own
   * signature back -- and only if theirs is the only one on the document,
   * since reopening deletes them all.
   *
   * A Sent document is a manager's alone, on every template: the client already
   * holds it.
   *
   * Ahead of all that: a document submitted for approval that nobody has signed
   * yet is only a request, so the writer who made it may take it back (not the
   * nominated signatory -- sending it back is a manager's call). This is the writer who asked for a
   * CPEng signature and then spotted a mistake -- without it they had no way
   * back to Draft at all.
   */
  const canReopen = useMemo(() => {
    if (!document || document.status === 'Draft') return false;
    if (isManager) return true;
    if (document.status === 'Sent') return false;
    if (!myTeamMemberId) return false;
    if (document.status === 'Awaiting Approval' && signatures.length === 0) {
      return myTeamMemberId === document.created_by;
    }
    if (requiresCertified) return false;
    return signatures.length > 0
      && signatures.every(sig => sig.signed_by === myTeamMemberId);
  }, [document, isManager, requiresCertified, myTeamMemberId, signatures]);

  // The signature that belongs in the letter's signature block: the one for the
  // template's 'author' slot. name/title/registration come from the signature
  // snapshot (synchronous once signatures load, so measurement is deterministic);
  // the image URL fills in async into a fixed-height box.
  const authorSignature = useMemo(() => {
    const authorSlot = slots.find(sl => sl.signer === 'author' || sl.signer === 'certified');
    if (!authorSlot) return undefined;
    return signatures.find(sig => sig.slot_key === authorSlot.key);
  }, [slots, signatures]);

  /**
   * What the signature block renders beneath the rule.
   *
   * Once signed this is the SNAPSHOT taken at signing time. Before that it is
   * the nominated signatory's current identity, with no image -- so the block
   * shows the same three lines (name, position, registration) either way.
   *
   * That is not cosmetic. The editor measures this block to decide where the
   * page breaks, so if signing added two lines the document silently reflowed:
   * a site instruction that fitted on one page grew to two the moment it was
   * signed (parity rule 4 -- a measured height must not depend on state that
   * changes after pagination). The signature IMAGE was already made
   * height-neutral by absolute positioning; these two text lines were not.
   */
  const signatureStamp: SignatureStamp | null = useMemo(() => {
    if (authorSignature) {
      return {
        imageUrl: stampImageUrl,
        name: authorSignature.signer_name,
        title: authorSignature.signer_position_title,
        registration: authorSignature.signer_registration_number,
        scale: Number(authorSignature.signature_scale ?? 1),
        offsetXMm: Number(authorSignature.signature_offset_x_mm ?? 0),
        offsetYMm: Number(authorSignature.signature_offset_y_mm ?? 0),
      };
    }

    if (!signatoryInfo) return null;
    return {
      imageUrl: null,
      name: signatoryInfo.name,
      title: signatoryInfo.title,
      registration: signatoryInfo.registration,
      scale: 1,
      offsetXMm: 0,
      offsetYMm: 0,
    };
  }, [authorSignature, stampImageUrl, signatoryInfo]);

  /**
   * Does the body carry the block a signature is actually drawn into?
   *
   * The stamp is rendered by the `signature` block and nowhere else -- not by
   * the chrome, not by the status. The certificates and the site instruction
   * are seeded with one (the site instruction's is locked), but a Report starts
   * blank, and anywhere else it is an ordinary block that can be deleted like
   * any other. Before this was checked nothing downstream noticed: the document
   * signed, issued, locked itself against further edits, and printed a PDF
   * with no signature anywhere on it. Issued with nothing on the paper saying
   * who by.
   */
  const hasSignatureBlock = useMemo(
    () => (document?.content || []).some(block => block.type === 'signature'),
    [document?.content]
  );

  /**
   * Does this template stamp a signature into the body at all?
   *
   * Only the author/certified slot is drawn into the body -- see
   * `authorSignature` -- so a manager's approval, which is recorded against
   * the document rather than stamped on it, is deliberately not held to the
   * signature-block check.
   */
  const stampsSignatureIntoBody = useMemo(
    () => slots.some(sl => sl.signer === 'author' || sl.signer === 'certified'),
    [slots]
  );

  /**
   * Refuse a lifecycle step that would end in a signature with nowhere to land,
   * and offer the fix where there still is one. Resolves true when the step may
   * go ahead.
   *
   * Held at BOTH steps that lead to a signature. Signing alone was not enough:
   * on a certified template the writer never signs, they submit, so the first
   * person to meet the check was the certifier -- after submission had locked
   * the content, with no way to add the block. A Report starts with a blank
   * body, so every report submitted as written hit that. Checking at submit
   * puts the prompt in front of the writer while the document is still a
   * Draft they can fix.
   */
  const ensureSignatureBlock = useCallback(async (step: 'sign' | 'submit'): Promise<boolean> => {
    if (hasSignatureBlock) return true;

    const why = (
      <p>
        A signature is drawn into the document&rsquo;s signature block, and this
        one has none &mdash; {step === 'sign'
          ? 'signing it would issue it with no signature on the page.'
          : 'it cannot be signed off without one.'}
      </p>
    );

    // Content is frozen outside Draft (enforce_document_edit_lock), so the
    // offer to add the block would be refused by the database.
    //
    // The message states the position rather than prescribing a way out,
    // because who can reopen from here depends on who has signed (see
    // canReopen): withdrawing signatures does NOT come back to Draft --
    // recompute_document_status() holds a submitted document at 'Awaiting
    // Approval' -- so the exit is Return to draft, which not every viewer has.
    if (!isDraftStatus) {
      await notify({
        title: 'This document has no signature block',
        message: (
          <>
            {why}
            <p>
              Its content is locked until it returns to draft, so the block cannot be
              added here.
            </p>
          </>
        ),
        tone: 'warning',
      });
      return false;
    }

    const addIt = await confirm({
      title: 'This document has no signature block',
      message: (
        <>
          {why}
          <p>
            {step === 'sign'
              ? 'Add a signature block to the end of the letter, then sign it.'
              : 'Add a signature block to the end of the letter, then submit it for approval.'}
          </p>
        </>
      ),
      confirmLabel: 'Add signature block',
      tone: 'warning',
    });
    // Added but not signed or submitted: the block carries a closing line and
    // a company line the writer will want to see before the document locks.
    if (addIt) addLetterSignature();
    return false;
  }, [hasSignatureBlock, isDraftStatus, confirm, notify, addLetterSignature]);

  /**
   * Open the signing dialog, or say why it cannot be opened.
   *
   * Checked before the dialog rather than inside handleSign so the user is not
   * shown a preview of a signature that has nowhere to land, and is offered
   * the fix instead of just the refusal.
   */
  const requestSign = useCallback(async (slot: SignatureSlot) => {
    const stampsIntoBody = slot.signer === 'author' || slot.signer === 'certified';
    if (stampsIntoBody && !(await ensureSignatureBlock('sign'))) return;

    // lifecycleError is drawn inside the dialog we are about to open, and
    // nothing else clears it. Without this, a failed attempt that the user
    // cancelled out of is still on screen the moment they open it again,
    // reading as a fresh refusal of an attempt they have not yet made.
    setLifecycleError(null);
    setSigningSlot(slot);
  }, [ensureSignatureBlock]);

  /** Submit for approval, once the body has somewhere for the signature to go. */
  const requestSubmitForApproval = useCallback(async () => {
    if (stampsSignatureIntoBody && !(await ensureSignatureBlock('submit'))) return;
    await handleSubmitForApproval();
  }, [stampsSignatureIntoBody, ensureSignatureBlock, handleSubmitForApproval]);

  // True when the author has nothing left to sign but the document still needs
  // somebody else's signature -- the case that needs an explicit hand-over.
  const needsSubmission = useMemo(() => {
    if (!isDraftStatus || slots.length === 0) return false;
    const required = slots.filter(s => s.required !== false);
    const mineUnsigned = required.filter(canSignSlot);
    const othersUnsigned = required.filter(s => !signedSlotKeys.has(s.key) && !canSignSlot(s));
    return mineUnsigned.length === 0 && othersUnsigned.length > 0;
  }, [isDraftStatus, slots, canSignSlot, signedSlotKeys]);

  const statusPillClass = documentStatusClass[document?.status ?? 'Draft'];

  /**
   * May this user move the document between Signed and Sent? Mirrors
   * mark_document_sent(): the writer, who normally emails it, or a manager.
   */
  const canChangeSent = useMemo(
    () => isSignedStatus(document?.status)
      && (isManager || (!!myTeamMemberId && myTeamMemberId === document?.created_by)),
    [document?.status, document?.created_by, isManager, myTeamMemberId]
  );

  // Fetch the nominated signatory's identity so the sign dialog can preview what
  // a manager would stamp when signing a certified slot on their behalf.
  useEffect(() => {
    let cancelled = false;
    const sid = document?.signatory_id;
    if (!sid) { setSignatoryInfo(null); return; }

    supabase
      .from('team_members')
      .select('first_name, last_name, position_title, registration_number, signature_storage_path, signature_scale, signature_offset_x_mm, signature_offset_y_mm')
      .eq('id', sid)
      .single()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setSignatoryInfo({
          name: `${data.first_name} ${data.last_name}`.trim(),
          title: data.position_title,
          registration: data.registration_number,
          signaturePath: data.signature_storage_path,
          placement: readPlacement(data),
        });
      });

    return () => { cancelled = true; };
  }, [document?.signatory_id, supabase]);

  // Load a signed URL for the author-slot signature so the signature block shows
  // the image in the editor. Private bucket, so a short-lived signed URL --
  // resolved through the shared cache, so after a signing it is the SAME url
  // the dialog just previewed and the image paints from the browser cache.
  useEffect(() => {
    let cancelled = false;
    const path = authorSignature?.signature_storage_path;

    if (!path) {
      setStampImageUrl(null);
      return;
    }

    getSignatureUrl(supabase, path).then(url => {
      if (!cancelled) setStampImageUrl(url);
    });

    return () => { cancelled = true; };
  }, [authorSignature?.signature_storage_path, supabase]);

  // Warm the cache for the two signatures the dialog could preview -- my own,
  // and the nominated signatory's -- as soon as their paths are known, so that
  // opening it does not start with a round trip.
  useEffect(() => {
    prefetchSignatureUrl(supabase, mySignaturePath);
    prefetchSignatureUrl(supabase, signatoryInfo?.signaturePath);
  }, [mySignaturePath, signatoryInfo?.signaturePath, supabase]);

  // Who the sign dialog represents: when a manager signs a certified slot for the
  // nominated engineer, it stamps the ENGINEER's signature, so the dialog previews
  // the signatory. Every other case is the signer's own.
  const signRepresented = useMemo(() => {
    const onBehalf = signingSlot?.signer === 'certified'
      && !!document && myTeamMemberId !== document.signatory_id;
    if (onBehalf) {
      return {
        onBehalf: true,
        name: signatoryInfo?.name || '',
        title: signatoryInfo?.title || null,
        registration: signatoryInfo?.registration || null,
        signaturePath: signatoryInfo?.signaturePath || null,
        placement: signatoryInfo?.placement || DEFAULT_SIGNATURE_PLACEMENT,
      };
    }
    return {
      onBehalf: false,
      name: myName,
      title: myPositionTitle,
      registration: myRegistrationNumber,
      signaturePath: mySignaturePath,
      placement: myPlacement,
    };
  }, [signingSlot, document, myTeamMemberId, signatoryInfo, myName, myPositionTitle, myRegistrationNumber, mySignaturePath, myPlacement]);

  // Signed URL for the represented signature, resolved when the confirmation
  // opens. Normally a cache hit (see the prefetch above), so the preview is
  // there as the dialog appears rather than a round trip later.
  useEffect(() => {
    let cancelled = false;

    if (!signingSlot || !signRepresented.signaturePath) {
      setMySignaturePreviewUrl(null);
      return;
    }

    getSignatureUrl(supabase, signRepresented.signaturePath).then(url => {
      if (!cancelled) setMySignaturePreviewUrl(url);
    });

    return () => { cancelled = true; };
  }, [signingSlot, signRepresented.signaturePath, supabase]);

  const [isRefreshingDrawings, setIsRefreshingDrawings] = useState(false);

  /**
   * Re-read the stage's drawing set into the certificate's drawing list.
   *
   * The rows are the document's own copy, so they do not follow the drawing set
   * on their own -- this is how a draft catches up after a drawing is revised.
   * Offered only while the document is a Draft: once signed the edit lock
   * refuses the write anyway, and a certificate is a statement about the
   * drawings as they were on the day it was signed.
   *
   * Taking the set's values back is the whole point, so edits made to a row
   * here are replaced -- but a drawing the set no longer holds would leave the
   * certificate silently, and pressing Refresh again would not bring it back.
   * Those are named and confirmed first.
   */
  const refreshDrawings = useCallback(async () => {
    const doc = documentRef.current;
    if (!doc || doc.status !== 'Draft') return;
    const target = (doc.content || []).find(b => b.type === 'drawing_list');
    if (!target) return;

    setIsRefreshingDrawings(true);
    try {
      const { blocks: [refreshed], dropped } = await captureDrawings(
        supabase,
        [JSON.parse(JSON.stringify(target)) as Block],
        doc.stage_id ?? null,
        doc.project_id
      );

      if (dropped.length > 0) {
        const proceed = await confirm({
          title: dropped.length === 1
            ? 'Take one drawing off the certificate?'
            : `Take ${dropped.length} drawings off the certificate?`,
          message: (
            <>
              <p>
                The stage&rsquo;s drawing set no longer holds {dropped.length === 1 ? 'this drawing' : 'these drawings'},
                so refreshing removes {dropped.length === 1 ? 'it' : 'them'} from the certificate:
              </p>
              <ul>
                {dropped.map(d => (
                  <li key={d.id}>{d.drawing_number} &mdash; {d.title}</li>
                ))}
              </ul>
            </>
          ),
          confirmLabel: 'Refresh',
          tone: 'danger',
        });
        if (!proceed) return;
      }

      applyDocumentUpdate(prev => ({
        ...prev,
        // The design-criteria paragraph follows the rows only while unedited.
        content: followDesignCriteria(
          (prev.content || []).map(b =>
            b.id === target.id ? { ...b, data: refreshed.data } : b
          ),
          target.data as DrawingListBlockData,
          refreshed.data as DrawingListBlockData
        ),
      }));
    } catch (err) {
      console.error('Error refreshing drawings:', err);
      void notify({
        title: 'Could not refresh the drawings',
        message: <p>The stage&rsquo;s drawing set could not be read. Try again in a moment.</p>,
        tone: 'warning',
      });
    } finally {
      setIsRefreshingDrawings(false);
    }
  }, [supabase, applyDocumentUpdate, confirm, notify]);

  const [isRefreshingQa, setIsRefreshingQa] = useState(false);

  /**
   * Re-read the job's completed RWC QA items into the certificate's QA list.
   *
   * A reconcile, not a re-capture: the bullets are the engineer's own sentences
   * and the references under them are typed from the laboratory's report, so a
   * row already here keeps its text and only membership follows the PEP. A QA
   * item whose completion date has gone would leave silently, and pressing
   * Refresh again would not bring it back, so those are named and confirmed.
   */
  const refreshQaItems = useCallback(async () => {
    const doc = documentRef.current;
    if (!doc || doc.status !== 'Draft') return;
    const target = (doc.content || []).find(b => b.type === 'qa_list');
    if (!target) return;

    setIsRefreshingQa(true);
    try {
      const { blocks: [refreshed], dropped } = await reconcileQaList(
        supabase,
        [JSON.parse(JSON.stringify(target)) as Block],
        doc.stage_id ?? null,
        doc.project_id
      );

      if (dropped.length > 0) {
        const proceed = await confirm({
          title: dropped.length === 1
            ? 'Take one QA item off the certificate?'
            : `Take ${dropped.length} QA items off the certificate?`,
          message: (
            <>
              <p>
                {dropped.length === 1 ? 'This bullet is' : 'These bullets are'} no longer
                recorded as completed on the job&rsquo;s QA list, so refreshing removes{' '}
                {dropped.length === 1 ? 'it' : 'them'} from the certificate:
              </p>
              <ul>
                {dropped.map(r => (
                  <li key={r.id}>{r.text}</li>
                ))}
              </ul>
            </>
          ),
          confirmLabel: 'Refresh',
          tone: 'danger',
        });
        if (!proceed) return;
      }

      applyDocumentUpdate(prev => ({
        ...prev,
        content: (prev.content || []).map(b =>
          b.id === target.id ? { ...b, data: refreshed.data } : b
        ),
      }));
    } catch (err) {
      console.error('Error refreshing QA items:', err);
      void notify({
        title: 'Could not refresh the QA items',
        message: <p>The job&rsquo;s QA items could not be read. Try again in a moment.</p>,
        tone: 'warning',
      });
    } finally {
      setIsRefreshingQa(false);
    }
  }, [supabase, applyDocumentUpdate, confirm, notify]);

  /**
   * The editor is keyed on documentId, so switching document unmounts all of
   * this and the loading state used to replace the whole screen -- a ~100ms
   * white flash on every click in the panel, and locally at that.
   *
   * The frame is kept instead: the toolbar's height, the panel, and the canvas
   * area with the spinner in it. The panel renders from the job scope held
   * above the key and its own cache, so the list the user just clicked in stays
   * exactly where it was and only the page changes.
   */
  if (loading) {
    return (
      <div className="document-editor">
        <header className="editor-toolbar" />
        <div className="editor-content">
          {navScope && (
            <DocumentNav
              currentDocumentId={documentId ?? ''}
              projectId={navScope.projectId}
              stageId={navScope.stageId}
              collapsed={navCollapsed}
              onCollapsedChange={onNavCollapsedChange}
              activeTab={navTab}
              onActiveTabChange={onNavTabChange}
              onOpenDocument={openSibling}
            />
          )}
          <div className="editor-main">
            <div className="editor-loading">Loading document...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !document || !template) {
    return (
      <div className="document-editor">
        <div className="editor-error">
          <div className="error-message">{error || 'Document not found'}</div>
          <div className="error-actions">
            <button className="toolbar-btn btn-secondary" onClick={closeEditor}>
              Back to Documents
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ensure header_fields has document_date (for existing documents that don't have it)
  const headerFields = mergeHeaderFields(
    document.header_fields, template?.chrome ?? 'letter', document.created_at);

  // Display values that incorporate stage info when the document is for a stage
  const displayJobNumber = displayJobNumberOf(projectData);
  const displayProjectDescription = displayProjectDescriptionOf(projectData);

  // Client address block: exactly the invoice's rules (header lines, care-of
  // "c/-" from stage then project, street / postal / email formats, city line).
  const client = projectData?.clients;
  const clientAddress = client
    ? buildClientAddressLines(
        {
          ...client,
          // Older clients may still hold the retired po_box field
          postal_address: client.postal_address || (client.po_box ? `PO Box ${client.po_box}` : null),
          header_lines: client.header_lines ?? null,
          billing_email: client.billing_email ?? null,
        },
        resolveCareOf(null, projectData?.stage, projectData)
      )
    : null;

  const renderClientAddress = () => (
    <div className="header-client">
      {clientAddress ? (
        <>
          {clientAddress.headerLines.map((line, i) => (
            <div key={`hdr-${i}`} className="client-header-line">{line}</div>
          ))}
          <div className="client-company">{clientAddress.topName}</div>
          {clientAddress.careOfClientName && (
            <div className="client-care-of">c/- {clientAddress.careOfClientName}</div>
          )}
          {clientAddress.addressLines.map((line, i) => (
            <div key={`addr-${i}`} className="client-address-line">{line}</div>
          ))}
          {clientAddress.cityLine && (
            <div className="client-address-line client-address-line--bold">{clientAddress.cityLine}</div>
          )}
        </>
      ) : (
        <>
          <div className="client-company">—</div>
          <div className="client-address-line"><span className="placeholder">[Street Address]</span></div>
          <div className="client-address-line client-address-line--bold"><span className="placeholder">[City State Postcode]</span></div>
        </>
      )}
    </div>
  );

  // Site/project address: single line, street + city only (no state/postcode), pulled from project.
  const displaySiteAddress = projectData
    ? [projectData.street_address, projectData.city].filter(Boolean).join(', ')
    : '';

  // Primary contact pulled live: the stage's own if it has one, else the
  // project's. Used to seed Attention / Dear when not overridden.
  const liveContact = effectiveContact(projectData);
  const projectContactFull = liveContact
    ? `${liveContact.first_name} ${liveContact.last_name}`.trim()
    : '';
  const projectContactFirst = liveContact?.first_name || '';

  // Overrides — empty string in header_fields means "use project value".
  const contactDisplay = headerFields.contact_name || projectContactFull;
  const dearDisplay = headerFields.dear_name || projectContactFirst;

  // Head contractor: the stage's, falling back to the project's — the same
  // precedence resolveCareOf uses for the client's care-of. Design and RWC set
  // it; Advisory and RWD have no field, so this resolves blank and the user
  // types it in the Fields sidebar.
  const headContractorName =
    projectData?.stage?.head_contractor?.company_name ||
    projectData?.head_contractor?.company_name ||
    '';

  // Placeholder shown in an override input when it is blank: the live project
  // value the document will fall back to.
  const derivedPlaceholders: Record<string, string> = {
    '': '',
    projectContact: projectContactFull || 'Project contact',
    contactFirstName: projectContactFirst || 'Contact first name',
    projectDescription: displayProjectDescription === '—' ? 'Project description' : displayProjectDescription,
    jobNumber: displayJobNumber === '—' ? 'Job number' : displayJobNumber,
    client: client?.company_name || 'Client',
    headContractor: headContractorName || 'None recorded',
  };

  // Blocks the template owns. On a site instruction the instruction rows, the
  // distribution list and the signature are the form -- the user edits their
  // contents but cannot move, duplicate or delete them, so every instruction
  // looks like every other one.
  const isBlockLocked = (block: Block) =>
    template?.chrome === 'site_instruction' && LOCKED_BLOCK_TYPES.has(block.type);

  // Does this document carry a captured drawing list? Decides whether the Fields
  // sidebar offers a Refresh -- asked of the content rather than the template
  // code, so any future template with the block gets it.
  const drawingListBlock = (document.content || []).find(b => b.type === 'drawing_list');
  const hasDrawingList = !!drawingListBlock;
  // The block itself says only "No drawings listed", because whatever it says
  // DocumentPDF must say too. What to do about it goes here instead.
  const drawingListIsEmpty =
    ((drawingListBlock?.data as { rows?: unknown[] } | undefined)?.rows || []).length === 0;

  const qaListBlock = (document.content || []).find(b => b.type === 'qa_list');
  const hasQaList = !!qaListBlock;
  const qaListIsEmpty =
    ((qaListBlock?.data as { rows?: unknown[] } | undefined)?.rows || []).length === 0;

  // One props object for both chrome copies (offscreen measurement and visible
  // page 1). They MUST be the same object or the measured height stops matching
  // what is drawn, and pagination silently goes wrong.
  const chromeProps: DocumentChromeProps = {
    variant: template?.chrome ?? 'letter',
    date: formatDateLongParts(headerFields.document_date || document.created_at),
    jobNumber: displayJobNumber,
    projectDescription: displayProjectDescription,
    // letter
    clientAddress: renderClientAddress(),
    contactDisplay,
    dearDisplay,
    reportTitle: headerFields.report_title || '',
    siteAddress: displaySiteAddress,
    // Same value reaches DocumentPDF above; see TEMPLATES_WITHOUT_PROJECT_ADDRESS.
    showSiteAddress: !TEMPLATES_WITHOUT_PROJECT_ADDRESS.has(template?.code ?? ''),
    // site instruction
    clientArchitect: headerFields.client_architect || client?.company_name || '',
    contractor: headerFields.contractor || headContractorName,
    siNo: document.sequence_number != null ? String(document.sequence_number) : '',
    method: headerFields.method || '',
  };

  // A certificate's trustee line replaces the page numbers outright, as in
  // DocumentPDF.
  const showPageNumbers = certificateFooterPage === null;

  return (
    /* Site instruction tokens are applied at the ROOT, not on the chrome, so
       they reach the body blocks too -- the chrome is a sibling of the body and
       custom properties only inherit downwards. The date-ordinal tokens go on
       unconditionally: both chromes show a long date. */
    <div
      className="document-editor"
      style={
        chromeProps.variant === 'site_instruction'
          ? { ...DATE_ORDINAL_CSS_VARS, ...SUBSCRIPT_CSS_VARS, ...APPENDIX_CSS_VARS, ...SI_CSS_VARS }
          : { ...DATE_ORDINAL_CSS_VARS, ...SUBSCRIPT_CSS_VARS, ...APPENDIX_CSS_VARS }
      }
    >
      {confirmDialog}

      {/* Toolbar */}
      <header className="editor-toolbar">
        <div className="toolbar-left">
          <div className="doc-identity">
            <div className="doc-identity__top">
              <span className="doc-identity__number">{document.document_number}</span>
              <span className={`status-pill ${statusPillClass}`}>
                {document.status}
              </span>
            </div>
            <div className="doc-identity__title" title={document.title || headerFields.report_title || ''}>
              {document.title || headerFields.report_title || 'Untitled document'}
            </div>
          </div>
        </div>

        <div className="toolbar-right">
          <span
            className={`save-indicator ${saveStatus}${saveStatus === 'idle' && hasUnsavedChanges ? ' dirty' : ''}`}
            aria-live="polite"
          >
            {(saveStatus !== 'idle' || hasUnsavedChanges || lastSavedAt) && <span className="save-indicator__dot" />}
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && 'Could not save'}
            {saveStatus === 'idle' && hasUnsavedChanges && 'Unsaved changes'}
            {saveStatus === 'idle' && !hasUnsavedChanges && lastSavedAt && 'All changes saved'}
          </span>

          {/* One button per slot this user may sign. Signing is irreversible for
              the signer, so it always goes through a confirmation. */}
          {!isSignedStatus(document.status) && mySignableSlots.map(slot => (
            <button
              key={slot.key}
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={() => void requestSign(slot)}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8.5l3 3 7-7" />
              </svg>
              Sign &middot; {slot.label}
            </button>
          ))}

          {needsSubmission && (
            <button
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={() => void requestSubmitForApproval()}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2.5 8h11" />
                <path d="M9.5 4l4 4-4 4" />
              </svg>
              Submit for Approval
            </button>
          )}

          {myWithdrawableSlots.map(slot => (
            <button
              key={`wd-${slot.key}`}
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={() => handleUnsign(slot.key)}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
              Withdraw &middot; {slot.label}
            </button>
          ))}

          {canReopen && (
            <button
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={handleReopen}
              title={signatures.length > 0
                ? 'Delete the signatures and return this document to Draft so it can be edited'
                : 'Return this document to Draft so it can be edited'}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3L2.5 6.5 6 10" />
                <path d="M2.5 6.5H10a3.5 3.5 0 0 1 0 7H8" />
              </svg>
              Return to draft
            </button>
          )}

          {canChangeSent && document.status === 'Signed' && (
            <button
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={() => void handleSetSent(true)}
              title="Record that this document has been sent to the client"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2L7 9" />
                <path d="M14 2l-4.5 12L7 9 2 6.5 14 2z" />
              </svg>
              Mark as sent
            </button>
          )}

          {canChangeSent && document.status === 'Sent' && (
            <button
              type="button"
              className="toolbar-btn btn-secondary"
              disabled={isSigning}
              onClick={() => void handleSetSent(false)}
              title="Move this document back to Signed -- it has not gone to the client"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
              Mark as not sent
            </button>
          )}

          <button
            type="button"
            className="toolbar-btn btn-primary"
            onClick={handleExportPDF}
            disabled={isExporting}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v8" />
              <path d="M4.5 6.5L8 10l3.5-3.5" />
              <path d="M2.5 13h11" />
            </svg>
            {isExporting ? 'Exporting…' : 'Export PDF'}
          </button>

          <span className="toolbar-divider" aria-hidden="true" />

          <button
            type="button"
            className="toolbar-icon-btn"
            onClick={closeEditor}
            title="Close (Esc)"
            aria-label="Close document"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6L18 18" />
            </svg>
          </button>
        </div>
      </header>

      {/* Hidden measurement container - renders content offscreen to measure heights */}
      <div
        ref={measurementRef}
        className="measurement-container"
      >
        <DocumentChrome {...chromeProps} />
        <div className="document-body-section">
          {/* Page-owning blocks are not in the letter's flow, so they are not
              measured here at the letter's width. */}
          <BlockCanvas
            blocks={document.content.filter(b => !APPENDIX_BLOCK_TYPES.has(b.type))}
            onChange={() => {}}
            isEditing={false}
            signatoryId={document.signatory_id}
            signatoryName={signatoryName}
            engineers={signatoryEngineers}
            onSignatoryChange={updateSignatory}
            signatureStamp={signatureStamp}
            isBlockLocked={isBlockLocked}
          />
        </div>
        {/* Table appendices measured whole, at the appendix page's width. */}
        <div className="measurement-appendix">
          {document.content.filter(b => b.type === 'table_appendix').map(block => (
            <TableAppendixPage
              key={block.id}
              block={block}
              blocks={document.content}
              numbers={photoNumberMap}
              isEditing={false}
            />
          ))}
        </div>
      </div>

      {/* Hidden file input for appendix photo uploads */}
      <input
        ref={appendixFileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleAppendixFileInputChange}
        disabled={uploadingAppendix}
        hidden
      />
      {/* ...for a location plan (an image or a PDF page)... */}
      <input
        ref={planFileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handlePlanFileInputChange}
        disabled={planBusy?.what === 'upload'}
        hidden
      />
      {/* ...and for an attached PDF. */}
      <input
        ref={attachmentFileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleAttachmentFileInputChange}
        disabled={attachmentProgress != null}
        hidden
      />

      {/* Editor Content - Visible page containers */}
      <div className="editor-content">
        {/* The job's other documents, so this one can be written against them.
            Ordered first in the DOM as well as by CSS so tab order runs
            left-to-right across the editor. */}
        <DocumentNav
          currentDocumentId={document.id}
          projectId={document.project_id}
          stageId={document.stage_id}
          collapsed={navCollapsed}
          onCollapsedChange={onNavCollapsedChange}
          activeTab={navTab}
          onActiveTabChange={onNavTabChange}
          onOpenDocument={openSibling}
          footer={
            // Delete sits at the foot of the left toolbar, in the rail's own
            // button style: out of the way of the page, but not floating over
            // it as it did before the toolbar existed.
            <button
              type="button"
              className="rail-btn rail-btn--danger"
              onClick={handleDeleteDocument}
              disabled={isDeletingDocument}
              title="Delete document"
              aria-label="Delete document"
            >
              <TrashIcon size={16} />
            </button>
          }
        />
        <div 
          className="editor-main"
          ref={editorMainRef}
          onClick={() => {
            // Clear appendix/signature focus when clicking on grey background
            // (appendix pages will stopPropagation to prevent this)
            setFocusedAppendixId(null);
            setShowAspectMenu(false);
          }}
        >
          <div className="document-preview">
            {pages.map((page, pageIndex) => {
              // The table, plan and attachment pages: each is its own
              // component inside the same page frame the photo appendix uses.
              if (page.type === 'appendix' && page.appendixBlockId && page.appendixKind && page.appendixKind !== 'photo') {
                const appendixBlock = document.content.find(b => b.id === page.appendixBlockId);
                if (!appendixBlock) return null;
                const isEditing = document.status === 'Draft';
                const isFocused = focusedAppendixId === page.appendixBlockId;
                const focus = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (isEditing) {
                    setFocusedAppendixId(page.appendixBlockId!);
                    setFocusedAppendixPageIndex(page.appendixPageIndex ?? 0);
                  }
                };

                if (page.appendixKind === 'attachment') {
                  const data = appendixBlock.data as PdfAttachmentBlockData;
                  const thumb = data.pages[page.appendixPageIndex ?? 0]?.thumbPath;
                  const progress = attachmentProgress?.blockId === appendixBlock.id ? attachmentProgress.message : null;
                  // The frame takes the attached page's own shape, scaled to
                  // the editor's 210mm page width: an A3 landscape sheet
                  // previews as a landscape page, not as a small sheet inside
                  // a portrait one. The export merges the real page at its
                  // real size regardless.
                  const frame = page.pageSize
                    ? { width: '210mm', height: `${(210 * page.pageSize.heightPt / page.pageSize.widthPt).toFixed(2)}mm` }
                    : undefined;
                  return (
                    <div
                      key={page.pageNumber}
                      className={`document-page appendix-page attachment-page ${isFocused ? 'focused' : ''}`}
                      style={frame}
                      onClick={focus}
                    >
                      {isEditing && isFocused && page.appendixPageIndex === 0 && data.pages.length > 0 && (
                        <div className="appendix-action-bar">
                          <div className="toolbar-group">
                            <span className="group-label">PDF</span>
                            <button
                              className="add-photos-btn"
                              onClick={(e) => { e.stopPropagation(); triggerAttachmentUpload(appendixBlock.id); }}
                              disabled={progress != null}
                            >
                              {progress ? progress : 'Replace PDF'}
                            </button>
                          </div>
                        </div>
                      )}
                      {data.pages.length > 0 ? (
                        <AttachmentPage
                          data={data}
                          pageIndex={page.appendixPageIndex ?? 0}
                          thumbUrl={thumb ? appendixImageUrls[thumb] || null : null}
                        />
                      ) : (
                        <div className="attachment-page__empty attachment-page__toolbar">
                          <div className="attachment-page__empty-title">
                            {progress || 'Attach a PDF'}
                          </div>
                          <div className="attachment-page__empty-hint">
                            Its pages are added to the export as they are -- a specification, repair details, an A3 drawing sheet. They carry no page number.
                          </div>
                          {isEditing && (
                            <button
                              type="button"
                              className="attachment-page__upload"
                              onClick={(e) => { e.stopPropagation(); triggerAttachmentUpload(appendixBlock.id); }}
                              disabled={progress != null}
                            >
                              {progress ? progress : 'Choose PDF'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    key={page.pageNumber}
                    className={`document-page appendix-page ${isFocused ? 'focused' : ''}`}
                    onClick={focus}
                  >
                    {page.appendixKind === 'plan' && isEditing && isFocused && focusedAppendixPageIndex === (page.appendixPageIndex ?? 0) && (() => {
                      // The plan's settings, on the same floating bar the photo
                      // appendix has: this page's file, rotation, trim and
                      // alignment; the appendix's marker source and placing;
                      // and the pages themselves. Shown on the page that was
                      // clicked, so a three-level appendix shows one bar.
                      const data = appendixBlock.data as PlanAppendixBlockData;
                      const pageIndex = page.appendixPageIndex ?? 0;
                      const plans = planPages(data);
                      const plan = plans[pageIndex] ?? plans[0];
                      const tables = document.content.filter(b => b.type === 'table_appendix');
                      const busy = planBusy && planBusy.blockId === appendixBlock.id && planBusy.pageIndex === pageIndex ? planBusy.what : null;
                      const uploading = busy === 'upload';
                      const rotating = busy === 'rotate';
                      const trimming = busy === 'trim';
                      const sourceValue = data.labelSource === 'item' ? `item:${data.tableBlockId ?? ''}` : 'image';
                      // Short on the button (the table's title made the bar
                      // wider than the editor); the full source is its tooltip
                      // and is spelled out in the menu.
                      const sourceLabel = data.labelSource === 'item' ? 'Item numbers' : 'Photo numbers';
                      const sourceTitle = data.labelSource === 'item'
                        ? `Item numbers from ${(tables.find(t => t.id === data.tableBlockId)?.data as TableAppendixBlockData | undefined)?.title || 'the table'}`
                        : 'Photo numbers';
                      const candidates = planCandidates(data, document.content, photoNumberMap);
                      const unplaced = candidates.filter(c => !c.placed);
                      const placedCount = candidates.length - unplaced.length;
                      return (
                        <div className="appendix-action-bar">
                          <div className="toolbar-group">
                            <span className="group-label">Plan</span>
                            <button
                              className="add-photos-btn"
                              onClick={(e) => { e.stopPropagation(); triggerPlanUpload(appendixBlock.id, pageIndex); }}
                              disabled={busy != null}
                            >
                              {uploading ? 'Uploading…' : plan.storagePath ? 'Replace' : '+ Upload plan'}
                            </button>
                          </div>
                          <div className="toolbar-divider" />
                          {/* One page per plan: a level, a wing. The pool of
                              photos is the appendix's, so a photo placed on any
                              page reads as placed on every page's menu. */}
                          <div className="toolbar-group">
                            <span className="group-label">Page {pageIndex + 1}/{plans.length}</span>
                            <div className="column-buttons">
                              <button
                                className="column-btn"
                                title="Add another plan page to this appendix (another level, say)"
                                disabled={busy != null}
                                onClick={(e) => { e.stopPropagation(); addPlanPage(appendixBlock.id); }}
                              >
                                + Page
                              </button>
                              <button
                                className="column-btn"
                                title={plans.length <= 1 ? 'An appendix keeps at least one page' : 'Remove this plan page'}
                                disabled={plans.length <= 1 || busy != null}
                                onClick={(e) => { e.stopPropagation(); void removePlanPage(appendixBlock.id, pageIndex); }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="toolbar-divider" />
                          <div className="toolbar-group">
                            <span className="group-label">Rotate</span>
                            <div className="column-buttons">
                              <button
                                className="column-btn"
                                title="Turn the plan 90° clockwise. A landscape sheet fills the page stood on its side."
                                disabled={!plan.storagePath || busy != null}
                                onClick={(e) => { e.stopPropagation(); void rotatePlan(appendixBlock.id, pageIndex); }}
                              >
                                {rotating ? 'Rotating…' : '↻ 90°'}
                              </button>
                              <button
                                className="column-btn"
                                title="Trim the white margins so the drawing fills the available space."
                                disabled={!plan.storagePath || busy != null}
                                onClick={(e) => { e.stopPropagation(); void trimPlan(appendixBlock.id, pageIndex); }}
                              >
                                {trimming ? 'Trimming…' : 'Trim'}
                              </button>
                            </div>
                          </div>
                          <div className="toolbar-divider" />
                          {/* Where the plan sits when it does not fill the box:
                              a tall plan leaves room beside it, a wide one room
                              below. The same offsets place it in the PDF. */}
                          <div className="toolbar-group">
                            <span className="group-label">Align</span>
                            <div className="column-buttons">
                              {([
                                ['left', 'Left', 'M2 3v10M5 5h9M5 8h6M5 11h9'],
                                ['center', 'Centre', 'M8 3v10M2 5h12M4 8h8M2 11h12'],
                                ['right', 'Right', 'M14 3v10M2 5h9M5 8h6M2 11h9'],
                              ] as const).map(([value, label, path]) => (
                                <button
                                  key={value}
                                  className={`column-btn align-btn ${(plan.align ?? 'center') === value ? 'active' : ''}`}
                                  title={`Align ${label.toLowerCase()}`}
                                  aria-label={`Align ${label.toLowerCase()}`}
                                  disabled={!plan.storagePath}
                                  onClick={(e) => { e.stopPropagation(); updatePlanPage(appendixBlock.id, pageIndex, { align: value }); }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d={path} /></svg>
                                </button>
                              ))}
                              <button
                                className={`column-btn ${plan.valign === 'middle' ? 'active' : ''}`}
                                title={plan.valign === 'middle' ? 'Sitting mid-page. Click to move it up under the title.' : 'Sitting under the title. Click to centre it on the page.'}
                                disabled={!plan.storagePath}
                                onClick={(e) => { e.stopPropagation(); updatePlanPage(appendixBlock.id, pageIndex, { valign: plan.valign === 'middle' ? 'top' : 'middle' }); }}
                              >
                                Middle
                              </button>
                            </div>
                          </div>
                          <div className="toolbar-divider" />
                          <div className="toolbar-group">
                            <span className="group-label">Markers</span>
                            <div className="dropdown-container">
                              <button
                                className="dropdown-btn"
                                title={sourceTitle}
                                onClick={(e) => { e.stopPropagation(); setShowPlanSourceMenu(v => !v); }}
                              >
                                {sourceLabel}
                                <span className="dropdown-arrow">▾</span>
                              </button>
                              {showPlanSourceMenu && (
                                <div className="dropdown-menu dropdown-menu--right">
                                  {[
                                    { value: 'image', label: 'Photo numbers' },
                                    // Only tables that print item numbers; the one in use
                                    // stays listed so the menu still shows it.
                                    ...tables.filter(t =>
                                      visibleTableColumns((t.data as TableAppendixBlockData).columns).some(c => c.kind === 'item')
                                      || sourceValue === `item:${t.id}`
                                    ).map(t => ({
                                      value: `item:${t.id}`,
                                      label: `Item numbers · ${(t.data as TableAppendixBlockData).title || 'Table appendix'}`,
                                    })),
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      className={`dropdown-item ${sourceValue === opt.value ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPlanSourceMenu(false);
                                        void setPlanLabelSource(appendixBlock.id, opt.value);
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="toolbar-divider" />
                          {/* Placing: every photo (or table item) the plan can
                              carry, with its state. A pin lands near the middle
                              and is dragged from there. */}
                          <div className="toolbar-group">
                            <span className="group-label">Place</span>
                            <div className="dropdown-container">
                              <button
                                className="dropdown-btn"
                                disabled={!plan.storagePath || busy != null}
                                title={!plan.storagePath ? 'Upload the plan first' : busy ? 'Wait for the plan to finish updating' : undefined}
                                onClick={(e) => { e.stopPropagation(); setShowPlaceMenu(v => !v); setShowPlanSourceMenu(false); }}
                              >
                                {candidates.length === 0
                                  ? (data.labelSource === 'item' ? 'No table rows' : 'No photos')
                                  : `${placedCount}/${candidates.length} placed`}
                                <span className="dropdown-arrow">▾</span>
                              </button>
                              {showPlaceMenu && (
                                <div className="dropdown-menu dropdown-menu--scroll dropdown-menu--right">
                                  {candidates.length === 0 && (
                                    <div className="dropdown-note">
                                      {data.labelSource === 'item'
                                        ? 'The table has no rows yet.'
                                        : 'Add photos to a photo appendix first.'}
                                    </div>
                                  )}
                                  {unplaced.length > 1 && (
                                    <button
                                      className="dropdown-item dropdown-item--all"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowPlaceMenu(false);
                                        placeMarkers(appendixBlock.id, pageIndex, unplaced.map(c => ({ rowId: c.rowId, imageId: c.imageId })));
                                      }}
                                    >
                                      Place all {unplaced.length} remaining
                                    </button>
                                  )}
                                  {candidates.map(c => (
                                    <button
                                      key={c.key}
                                      className={`dropdown-item ${c.placed ? 'is-placed' : ''}`}
                                      title={c.placed ? 'Placed (on one of this appendix\u2019s pages) \u2014 click to remove its marker' : 'Place a marker for this on this page'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (c.placed) {
                                          removeMarker(appendixBlock.id, m => (c.rowId ? m.rowId === c.rowId : m.imageId === c.imageId));
                                        } else {
                                          placeMarkers(appendixBlock.id, pageIndex, [{ rowId: c.rowId, imageId: c.imageId }]);
                                        }
                                      }}
                                    >
                                      <span>{c.label}</span>
                                      <span className="dropdown-item__state">{c.placed ? '✓ placed' : 'place'}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {page.appendixKind === 'table' && isEditing && isFocused && focusedAppendixPageIndex === (page.appendixPageIndex ?? 0) && (() => {
                      // The table's settings, on the same floating bar as the
                      // other appendices: rows, the photo fill, and the columns.
                      // Column controls are here and not on the page because a
                      // control on the page would be measured as part of it.
                      const data = appendixBlock.data as TableAppendixBlockData;
                      const photoCount = photoNumberMap.size;
                      const shown = visibleTableColumns(data.columns);
                      const hasPhoto = shown.some(c => c.kind === 'image');
                      const hiddenCount = data.columns.length - shown.length;
                      const widthSum = Math.round(shown.reduce((n, c) => n + c.widthPct, 0));
                      const setColumns = (columns: TableAppendixColumn[]) => updateTableColumns(appendixBlock.id, columns);
                      return (
                        <div className="appendix-action-bar">
                          <div className="toolbar-group">
                            <span className="group-label">Rows</span>
                            <div className="column-buttons">
                              <button
                                className="column-btn"
                                title="Add a row at the end"
                                onClick={(e) => { e.stopPropagation(); appendTableRow(appendixBlock.id, 'row'); }}
                              >
                                + Row
                              </button>
                              <button
                                className="column-btn"
                                title="Add a section heading at the end"
                                onClick={(e) => { e.stopPropagation(); appendTableRow(appendixBlock.id, 'section'); }}
                              >
                                + Section
                              </button>
                            </div>
                          </div>
                          <div className="toolbar-divider" />
                          <div className="toolbar-group">
                            <span className="group-label">Photos</span>
                            <button
                              className="add-photos-btn"
                              title={photoCount === 0
                                ? 'Add a photo appendix first'
                                : !hasPhoto
                                  ? 'Show the Photo column first (Columns menu): the rows it adds would show nothing without it'
                                  : 'Add a row for every photo that has none, each in its photo\'s place; rows you have already written are left alone'}
                              disabled={photoCount === 0 || !hasPhoto}
                              onClick={(e) => { e.stopPropagation(); handleFillFromPhotos(appendixBlock.id); }}
                            >
                              Fill from photos
                            </button>
                          </div>
                          <div className="toolbar-divider" />
                          <div className="toolbar-group">
                            <span className="group-label">Columns</span>
                            <div className="dropdown-container">
                              <button
                                className="dropdown-btn"
                                onClick={(e) => { e.stopPropagation(); setShowColumnsMenu(v => !v); }}
                              >
                                {shown.length} columns{hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}{widthSum !== 100 ? ` · ${widthSum}%` : ''}
                                <span className="dropdown-arrow">▾</span>
                              </button>
                              {showColumnsMenu && (
                                <div className="dropdown-menu dropdown-menu--columns dropdown-menu--right" onClick={(e) => e.stopPropagation()}>
                                  {data.columns.map((column, i) => (
                                    <div key={column.id} className={`column-row column-row--${column.kind}${column.hidden ? ' is-hidden' : ''}`}>
                                      <input
                                        type="text"
                                        className="column-row__label"
                                        value={column.label}
                                        placeholder="Column"
                                        title={column.kind === 'item' ? 'Numbered by row position' : column.kind === 'image' ? 'Filled from the photos a row is linked to' : 'Free text'}
                                        onChange={(e) => setColumns(data.columns.map(c => c.id === column.id ? { ...c, label: e.target.value } : c))}
                                      />
                                      <input
                                        type="number"
                                        className="column-row__width"
                                        value={column.widthPct}
                                        min={5}
                                        max={90}
                                        title={column.hidden ? 'Hidden: takes no width' : 'Width, % of the table'}
                                        disabled={column.hidden}
                                        // Typed freely, balanced on leaving the field: balancing
                                        // per keystroke would clamp "3" to 5 before the "0" arrives.
                                        onChange={(e) => setColumns(data.columns.map(c => c.id === column.id ? { ...c, widthPct: Number(e.target.value) || 0 } : c))}
                                        onBlur={() => setColumns(normaliseColumnWidths(data.columns, column.id))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      />
                                      <span className="column-row__unit">%</span>
                                      <div className="column-row__actions">
                                        <button
                                          type="button"
                                          title="Move left"
                                          aria-label="Move column left"
                                          disabled={i === 0}
                                          onClick={() => { const next = [...data.columns]; [next[i - 1], next[i]] = [next[i], next[i - 1]]; setColumns(next); }}
                                        >‹</button>
                                        <button
                                          type="button"
                                          title="Move right"
                                          aria-label="Move column right"
                                          disabled={i === data.columns.length - 1}
                                          onClick={() => { const next = [...data.columns]; [next[i], next[i + 1]] = [next[i + 1], next[i]]; setColumns(next); }}
                                        >›</button>
                                        {(() => {
                                          // Item and Photo are hidden, never deleted: they
                                          // hold nothing of their own, and hidden they keep
                                          // their header, width and place.
                                          const derived = column.kind !== 'text';
                                          const refusal = column.hidden ? null : columnRemovalRefusal(document.content, appendixBlock.id, data.columns, column);
                                          const label = derived
                                            ? (column.hidden ? 'Show column' : 'Hide column')
                                            : 'Remove column';
                                          return (
                                            <button
                                              type="button"
                                              className={derived ? 'column-row__visibility' : undefined}
                                              title={refusal ?? label}
                                              aria-label={label}
                                              aria-pressed={derived ? !column.hidden : undefined}
                                              disabled={refusal !== null}
                                              onClick={() => {
                                                if (!derived) {
                                                  setColumns(normaliseColumnWidths(data.columns.filter(c => c.id !== column.id)));
                                                  return;
                                                }
                                                // Shown again it takes back its own share; the
                                                // others give it up in proportion.
                                                const next = data.columns.map(c => (c.id === column.id ? { ...c, hidden: !c.hidden || undefined } : c));
                                                setColumns(normaliseColumnWidths(next, column.hidden ? column.id : undefined));
                                              }}
                                            >
                                              {!derived ? '×' : column.hidden ? (
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                  <path d="M3 3l18 18" />
                                                  <path d="M10.6 5.1A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.1 4.1M6.6 6.6A17.4 17.4 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
                                                  <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                                                </svg>
                                              ) : (
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                                                  <circle cx="12" cy="12" r="3" />
                                                </svg>
                                              )}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  ))}
                                  <div className="column-list__footer">
                                    <span className={`column-list__sum ${widthSum === 100 ? '' : 'is-off'}`}>
                                      {widthSum}% of 100
                                    </span>
                                    <button
                                        type="button"
                                        className="column-list__add"
                                        onClick={() => {
                                          // The new column takes a fifth; the others give it up in proportion.
                                          const id = generateBlockId();
                                          setColumns(normaliseColumnWidths([...data.columns, { id, kind: 'text', label: 'Column', widthPct: 20 }], id));
                                        }}
                                      >
                                        + Column
                                      </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {page.appendixKind === 'table' ? (
                      <TableAppendixPage
                        block={appendixBlock}
                        blocks={document.content}
                        numbers={photoNumberMap}
                        fragment={page.rowFragment}
                        isEditing={isEditing}
                        onChange={(data) => updateBlockData(appendixBlock.id, data)}
                        photos={tablePhotos}
                        pickerRowId={tablePicker?.blockId === appendixBlock.id ? tablePicker.rowId : null}
                        onPickerRowChange={(rowId) => setTablePicker(rowId ? { blockId: appendixBlock.id, rowId } : null)}
                      />
                    ) : (
                      <PlanAppendixPage
                        block={appendixBlock}
                        blocks={document.content}
                        numbers={photoNumberMap}
                        isEditing={isEditing}
                        pageIndex={page.appendixPageIndex ?? 0}
                        imageUrl={(() => {
                          const path = planPages(appendixBlock.data as PlanAppendixBlockData)[page.appendixPageIndex ?? 0]?.storagePath;
                          return path ? appendixImageUrls[path] || null : null;
                        })()}
                        uploading={planBusy?.blockId === appendixBlock.id && planBusy.pageIndex === (page.appendixPageIndex ?? 0) && planBusy.what === 'upload'}
                        locked={planBusy?.blockId === appendixBlock.id && planBusy.pageIndex === (page.appendixPageIndex ?? 0)}
                        onChange={(data) => updateBlockData(appendixBlock.id, data)}
                        onUploadRequest={() => triggerPlanUpload(appendixBlock.id, page.appendixPageIndex ?? 0)}
                      />
                    )}
                    {showPageNumbers && (
                      <div className="page-footer">
                        <span>Page {page.reportPageNumber} of {reportPageCount}</span>
                      </div>
                    )}
                    {page.pageNumber === certificateFooterPage && (
                      <img className="certificate-footer" src={`/${CERTIFICATE_FOOTER_SRC}`} alt="Terpkos Engineering Pty Ltd as trustee for the TEG Trust" />
                    )}
                  </div>
                );
              }

              // The photo appendix. Its pages, their photos and every box on
              // them come from photoAppendixLayout, which DocumentPDF reads
              // too: nothing here is sized by the browser's own layout.
              if (page.type === 'appendix' && page.appendixBlockId) {
                const appendixBlock = document.content.find(b => b.id === page.appendixBlockId);
                if (!appendixBlock) return null;

                const appendixData = appendixBlock.data as ImageAppendixBlockData;
                const allImages = appendixData.images || [];
                const defaults = appendixDefaultLayout(appendixData);
                const appendixPageIndex = page.appendixPageIndex ?? 0;
                const fragment = page.photoFragment
                  ?? { start: 0, end: 0, layout: defaults, custom: false };
                const layout = fragment.layout;
                const isFirstAppendixPage = appendixPageIndex === 0;
                const isEditing = document.status === 'Draft';
                const isFocused = focusedAppendixId === page.appendixBlockId;
                // Clamped: removing photos or dropping a page's own layout can
                // leave the focused index past the appendix's last page, and
                // the bar would vanish while the appendix stays focused.
                const focusedPage = Math.min(focusedAppendixPageIndex, (page.appendixTotalPages ?? 1) - 1);
                const showBar = isEditing && isFocused && focusedPage === appendixPageIndex;
                const appendixStartNumber = getAppendixStartNumber(document.content, page.appendixBlockId!);
                const grid = photoGrid(layout, isFirstAppendixPage);
                const pageImages = allImages.slice(fragment.start, fragment.end).map((img, i) => ({
                  ...img,
                  index: fragment.start + i,
                  number: appendixStartNumber + fragment.start + i,
                }));
                // A page's own layout is stored against its first photo, so a
                // page with no photos (an empty appendix) cannot have one.
                const anchorId = pageImages[0]?.id ?? null;
                const setPageLayout = (patch: Partial<PhotoPageLayout>) => {
                  if (anchorId) handleAppendixPageLayoutChange(page.appendixBlockId!, anchorId, { ...layout, ...patch });
                };

                return (
                  <div 
                    key={page.pageNumber} 
                    className={`document-page appendix-page ${isFocused ? 'focused' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent bubbling to editor-main
                      if (isEditing) {
                        setFocusedAppendixId(page.appendixBlockId!);
                        setFocusedAppendixPageIndex(appendixPageIndex);
                      }
                    }}
                  >
                    {/* The bar shows on the page that was clicked: "All pages"
                        sets the appendix's layout, "This page" this page's own. */}
                    {showBar && (
                      <div className="appendix-action-bar">
                        <div className="toolbar-group">
                          <span className="group-label">All pages</span>
                          <div className="dropdown-container">
                            <button 
                              className="dropdown-btn"
                              title="The photo frame on every page without a layout of its own"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPageLayoutMenu(false);
                                setShowAspectMenu(!showAspectMenu);
                              }}
                            >
                              {aspectOption(defaults.aspect).label}
                              <span className="dropdown-arrow">▾</span>
                            </button>
                            {showAspectMenu && (
                              <div className="dropdown-menu">
                                {ASPECT_OPTIONS.map(opt => (
                                  <button
                                    key={opt.value}
                                    className={`dropdown-item ${defaults.aspect === opt.value ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAppendixAspectChange(page.appendixBlockId!, opt.value);
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="toolbar-group">
                          <span className="group-label">Cols</span>
                          <div className="column-buttons">
                            {([1, 2] as const).map((col) => (
                              <button
                                key={col}
                                className={`column-btn ${defaults.columns === col ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAppendixColumnsChange(page.appendixBlockId!, col);
                                }}
                              >
                                {col}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="toolbar-group">
                          <span className="group-label">Rows</span>
                          <div className="column-buttons">
                            {([1, 2] as const).map((row) => (
                              <button
                                key={row}
                                className={`column-btn ${defaults.rows === row ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAppendixRowsChange(page.appendixBlockId!, row);
                                }}
                              >
                                {row}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="toolbar-divider" />

                        <div className="toolbar-group">
                          <span className="group-label">This page</span>
                          <div className="dropdown-container">
                            <button
                              className={`dropdown-btn ${fragment.custom ? 'dropdown-btn--set' : ''}`}
                              disabled={!anchorId}
                              title={anchorId
                                ? 'Lay this page out differently from the rest of the appendix'
                                : 'Add photos first: a page layout belongs to the photos on it'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAspectMenu(false);
                                setShowPageLayoutMenu(!showPageLayoutMenu);
                              }}
                            >
                              {fragment.custom
                                ? `${layout.columns} × ${layout.rows}, ${aspectOption(layout.aspect).short}`
                                : 'As all pages'}
                              <span className="dropdown-arrow">▾</span>
                            </button>
                            {showPageLayoutMenu && anchorId && (
                              <div className="dropdown-menu dropdown-menu--layout">
                                <button
                                  className={`dropdown-item ${fragment.custom ? '' : 'active'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAppendixPageLayoutChange(page.appendixBlockId!, anchorId, null);
                                  }}
                                >
                                  As all pages
                                </button>
                                <div className="dropdown-section">
                                  <span className="dropdown-section__label">Cols</span>
                                  <div className="column-buttons">
                                    {([1, 2] as const).map((col) => (
                                      <button
                                        key={col}
                                        className={`column-btn ${layout.columns === col ? 'active' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); setPageLayout({ columns: col }); }}
                                      >
                                        {col}
                                      </button>
                                    ))}
                                  </div>
                                  <span className="dropdown-section__label">Rows</span>
                                  <div className="column-buttons">
                                    {([1, 2] as const).map((row) => (
                                      <button
                                        key={row}
                                        className={`column-btn ${layout.rows === row ? 'active' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); setPageLayout({ rows: row }); }}
                                      >
                                        {row}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                {ASPECT_OPTIONS.map(opt => (
                                  <button
                                    key={opt.value}
                                    className={`dropdown-item ${layout.aspect === opt.value ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setPageLayout({ aspect: opt.value }); }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="toolbar-divider" />

                        {/* Continue numbering toggle */}
                        <div className="toolbar-group">
                          <span className="group-label">Numbering</span>
                          <button
                            className={`continue-numbering-btn ${appendixData.continueNumbering ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAppendixContinueNumberingChange(
                                page.appendixBlockId!,
                                !appendixData.continueNumbering
                              );
                            }}
                            title={
                              appendixData.continueNumbering
                                ? 'Photos continue from the previous appendix. Click to restart at Photo 1.'
                                : 'Photos restart at Photo 1. Click to continue from the previous appendix.'
                            }
                          >
                            {appendixData.continueNumbering ? '✓ Continuous' : 'Restart at 1'}
                          </button>
                        </div>

                        <div className="toolbar-divider" />

                        {/* Add photos button */}
                        <button
                          className="add-photos-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerAppendixFileUpload(page.appendixBlockId!);
                          }}
                          disabled={uploadingAppendix}
                        >
                          {uploadingAppendix ? 'Uploading...' : '+ Add Photos'}
                        </button>
                      </div>
                    )}

                    {/* Where a page laid out on its own starts, while editing.
                        In the top margin, so it costs the page no layout. */}
                    {isEditing && fragment.custom && (
                      <span className="page-layout-chip">
                        This page: {layout.columns} × {layout.rows}, {aspectOption(layout.aspect).label}
                      </span>
                    )}

                    <div className="page-content appendix-content">
                      {/* Title only on first page of this appendix - editable.
                          One line, cut with an ellipsis: the grid below is
                          sized for exactly one (PHOTO_TITLE_BLOCK_PT). */}
                      {isFirstAppendixPage && (
                        <LiveText
                          as="h2"
                          className="apx-title apx-title--single-line"
                          value={appendixData.title || ''}
                          editable={isEditing}
                          restoreKey={`${page.appendixBlockId}:title`}
                          data-placeholder="Appendix Title..."
                          onCommit={(title) => handleAppendixTitleChange(page.appendixBlockId!, title)}
                        />
                      )}
                      
                      {/* Photo grid */}
                      {pageImages.length > 0 ? (
                        <div 
                          className="appendix-grid"
                          style={{ 
                            gridTemplateColumns: `repeat(${grid.columns}, ${grid.cellWidthPt}pt)`,
                            gridTemplateRows: `repeat(${grid.rows}, ${grid.cellHeightPt}pt)`,
                            gap: `${grid.gapPt}pt`,
                          }}
                        >
                          {pageImages.map((img) => {
                            const isFirst = img.index <= 0;
                            const isLast = img.index >= allImages.length - 1;
                            const frame = photoFrame(grid, img.aspect);
                            const imageUrl = img.storagePath ? appendixImageUrls[img.storagePath] : undefined;
                            const aspectMenuOpen = photoAspectMenuFor === img.id;
                            return (
                            <div key={img.id} className="appendix-image-item">
                              <div
                                className="photo-slot"
                                style={{ width: `${grid.slotWidthPt}pt`, height: `${grid.slotHeightPt}pt` }}
                              >
                                <div
                                  className={`photo-frame${aspectMenuOpen ? ' menu-open' : ''}${img.aspect ? ' has-own-aspect' : ''}`}
                                  style={{
                                    left: `${frame.leftPt}pt`,
                                    top: `${frame.topPt}pt`,
                                    width: `${frame.widthPt}pt`,
                                    height: `${frame.heightPt}pt`,
                                  }}
                                >
                                  <div className="image-container">
                                    {imageUrl ? (
                                      <img src={imageUrl} alt={img.caption || `Photo ${img.number}`} />
                                    ) : (
                                      <div className="image-placeholder">
                                        <span>📷</span>
                                        <span>Loading...</span>
                                      </div>
                                    )}
                                  </div>
                                  {isEditing && imageUrl && (
                                    <>
                                      {/* The photo's own shape, top left: shown
                                          while it has one, else on hover. */}
                                      <div className="photo-aspect-control photo-toolbar">
                                        <button
                                          type="button"
                                          className={`photo-tool-btn photo-aspect-btn${aspectMenuOpen ? ' is-open' : ''}`}
                                          aria-haspopup="menu"
                                          aria-expanded={aspectMenuOpen}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPhotoAspectMenuFor(aspectMenuOpen ? null : img.id);
                                          }}
                                          title={img.aspect
                                            ? `This photo is ${aspectOption(img.aspect).label}, whole. Click to change.`
                                            : `This photo is cropped to the page's ${aspectOption(layout.aspect).label}. Click to give it its own shape.`}
                                        >
                                          {aspectOption(img.aspect ?? layout.aspect).short}
                                          <ChevronDownIcon size={12} />
                                        </button>
                                        {aspectMenuOpen && (
                                          <div className="photo-aspect-menu" role="menu">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={`photo-aspect-item ${img.aspect ? '' : 'active'}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAppendixPhotoAspectChange(page.appendixBlockId!, img.id, undefined);
                                              }}
                                            >
                                              As page ({aspectOption(layout.aspect).short})
                                            </button>
                                            <div className="photo-aspect-menu__divider" />
                                            {ASPECT_OPTIONS.map(opt => (
                                              <button
                                                type="button"
                                                role="menuitem"
                                                key={opt.value}
                                                className={`photo-aspect-item ${img.aspect === opt.value ? 'active' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAppendixPhotoAspectChange(page.appendixBlockId!, img.id, opt.value);
                                                }}
                                              >
                                                {opt.label}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <div className="photo-controls photo-toolbar">
                                        <button
                                          type="button"
                                          className="photo-tool-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppendixReorderImage(page.appendixBlockId!, img.id, 'up');
                                          }}
                                          disabled={isFirst}
                                          title="Move photo up"
                                          aria-label="Move photo up"
                                        >
                                          <ArrowUpIcon size={14} />
                                        </button>
                                        <button
                                          type="button"
                                          className="photo-tool-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppendixReorderImage(page.appendixBlockId!, img.id, 'down');
                                          }}
                                          disabled={isLast}
                                          title="Move photo down"
                                          aria-label="Move photo down"
                                        >
                                          <ArrowDownIcon size={14} />
                                        </button>
                                        <span className="photo-toolbar__divider" />
                                        <button
                                          type="button"
                                          className="photo-tool-btn photo-tool-btn--danger"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAppendixRemoveImage(page.appendixBlockId!, img.id);
                                          }}
                                          title="Remove photo"
                                          aria-label="Remove photo"
                                        >
                                          <TrashIcon size={14} />
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="image-label-row" style={{ width: `${photoLabelWidth(grid, frame)}pt` }}>
                                <span className="photo-label">Photo {img.number}</span>
                                <span className="label-separator">:</span>
                                <LiveText
                                  className="image-caption"
                                  value={img.caption || ''}
                                  editable={isEditing}
                                  restoreKey={`${page.appendixBlockId}:${img.id}:caption`}
                                  data-placeholder="Add caption..."
                                  onCommit={(caption) => handleAppendixCaptionChange(page.appendixBlockId!, img.id, caption)}
                                />
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      ) : isFirstAppendixPage && isEditing ? (
                        <div className="appendix-empty-state">
                          <div
                            className={`photo-dropzone ${dragOverAppendixId === page.appendixBlockId ? 'drag-over' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => triggerAppendixFileUpload(page.appendixBlockId!)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                triggerAppendixFileUpload(page.appendixBlockId!);
                              }
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (dragOverAppendixId !== page.appendixBlockId) setDragOverAppendixId(page.appendixBlockId!);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              // dragleave also fires when the pointer moves onto a child
                              // (icon, title, button); only clear when it really left the zone
                              const next = e.relatedTarget as Node | null;
                              if (next && e.currentTarget.contains(next)) return;
                              setDragOverAppendixId(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDragOverAppendixId(null);
                              const files = e.dataTransfer?.files;
                              if (files && files.length > 0) {
                                setFocusedAppendixId(page.appendixBlockId!);
                                handleAppendixImageUpload(page.appendixBlockId!, files);
                              }
                            }}
                          >
                            <div className="dropzone-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="16" rx="2" />
                                <circle cx="9" cy="10" r="2" />
                                <path d="M21 16l-5-5-8 8" />
                                <path d="M3 18l4-4 3 3" />
                              </svg>
                            </div>
                            <div className="dropzone-title">
                              {uploadingAppendix ? 'Uploading photos…' : 'Add photos to this appendix'}
                            </div>
                            <div className="dropzone-hint">
                              Drag and drop images here, or choose files. You can select several at once; each is resized and numbered automatically. iPhone HEIC photos are converted for you.
                            </div>
                            {appendixUploadErrors.length > 0 && (
                              <div className="dropzone-error" role="alert">
                                {appendixUploadErrors.length === 1
                                  ? `Could not add ${appendixUploadErrors[0]}`
                                  : `Could not add ${appendixUploadErrors.length} files: ${appendixUploadErrors.join('; ')}`}
                              </div>
                            )}
                            <button
                              type="button"
                              className="dropzone-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerAppendixFileUpload(page.appendixBlockId!);
                              }}
                              disabled={uploadingAppendix}
                            >
                              {uploadingAppendix ? 'Uploading…' : 'Choose photos'}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    
                    {/* Page footer */}
                    {showPageNumbers && (
                      <div className="page-footer">
                        <span>Page {page.reportPageNumber} of {reportPageCount}</span>
                      </div>
                    )}
                    {page.pageNumber === certificateFooterPage && (
                      <img className="certificate-footer" src={`/${CERTIFICATE_FOOTER_SRC}`} alt="Terpkos Engineering Pty Ltd as trustee for the TEG Trust" />
                    )}
                  </div>
                );
              }

              // Regular body page rendering
              return (
                <div 
                  key={page.pageNumber} 
                  className="document-page"
                  onClick={() => {
                    setFocusedAppendixId(null);
                    setShowAspectMenu(false);
                  }}
                >
                  <div className={`page-content${pageIndex === 0 && page.type === 'body' ? ' has-letterhead' : ''}`}>
                    {/* Page-1 chrome: letterhead + the template's header block.
                        Shared with the offscreen measurement copy above, so the
                        height measured there is the height rendered here. */}
                    {pageIndex === 0 && page.type === 'body' && (
                      <DocumentChrome {...chromeProps} />
                    )}

                    {/* Blocks for this page */}
                    {/* A blank page's first card, while editing: the marker draws nothing,
                        so this is what says where the page came from. Stripped
                        from the rail's thumbnails (APPENDIX_EDIT_ONLY_CLASSES). */}
                    {page.blankPageId && page.appendixPageIndex === 0 && document.status === 'Draft' && (
                      <span className="blank-page-chip">Blank page</span>
                    )}
                    {page.type === 'body' && (page.blockIndices.length > 0 || page.blankPageId || (pageIndex === 0 && document.content.length === 0)) && (
                      <div className="document-body-section">
                        <BlockCanvas
                            blocks={page.items.map(it => document.content[it.blockIndex]).filter(b => b && !APPENDIX_BLOCK_TYPES.has(b.type))}
                            fragments={page.items.map(it => it.fragment)}
                            rowFragments={page.items.map(it => it.rowFragment)}
                            onChange={(updatedBlocks) => {
                              // Always splice into the latest content (the ref), not the
                              // content captured when this page rendered.
                              const currentContent = documentRef.current?.content ?? document.content;

                              // If this page had no blocks, the new ones go after a
                              // blank page's marker when it is one, else at the end
                              if (page.blockIndices.length === 0) {
                                const markerAt = page.blankPageId ? currentContent.findIndex(b => b.id === page.blankPageId) : -1;
                                const at = markerAt === -1 ? currentContent.length : markerAt + 1;
                                updateBlocks([...currentContent.slice(0, at), ...updatedBlocks, ...currentContent.slice(at)]);
                                return;
                              }

                              // Handle additions, deletions, and updates by rebuilding
                              const firstOriginalIndex = page.blockIndices[0];
                              const lastOriginalIndex = page.blockIndices[page.blockIndices.length - 1];

                              // Build new array: blocks before this page + updated blocks + blocks after
                              const blocksBefore = currentContent.slice(0, firstOriginalIndex);
                              const blocksAfter = currentContent.slice(lastOriginalIndex + 1);

                              const newBlocks = [...blocksBefore, ...updatedBlocks, ...blocksAfter];
                              updateBlocks(newBlocks);
                            }}
                            isEditing={document.status === 'Draft'}
                            onFocusPreviousPage={pageIndex > 0 ? () => {
                              // Find the previous body page
                              const prevPage = pages[pageIndex - 1];
                              if (!prevPage || prevPage.type !== 'body' || prevPage.blockIndices.length === 0) return;
                              
                              // Focus the last editable on the previous page's element (a split
                              // paragraph has one element per page, so query the page, not the id)
                              setTimeout(() => {
                                const pageEls = window.document.querySelectorAll('.editor-main .document-page');
                                const prevPageEl = pageEls[pageIndex - 1];
                                const editables = prevPageEl?.querySelectorAll('[contenteditable="true"]');
                                const blockElement = editables && editables.length > 0
                                  ? (editables[editables.length - 1] as HTMLElement)
                                  : null;
                                if (blockElement) {
                                  blockElement.focus();
                                  // Place cursor at end
                                  const range = window.document.createRange();
                                  const sel = window.getSelection();
                                  range.selectNodeContents(blockElement);
                                  range.collapse(false);
                                  sel?.removeAllRanges();
                                  sel?.addRange(range);
                                }
                              }, 100);
                            } : undefined}
                            onFocusBlock={focusBlockWhenRendered}
                            signatoryId={document.signatory_id}
                            signatoryName={signatoryName}
                            engineers={signatoryEngineers}
                            onSignatoryChange={updateSignatory}
                            signatureStamp={signatureStamp}
                            isBlockLocked={isBlockLocked}
                          />
                      </div>
                    )}

                  </div>
                  
                  {/* Page footer */}
                  {showPageNumbers && (
                    <div className="page-footer">
                      <span>Page {page.reportPageNumber} of {reportPageCount}</span>
                    </div>
                  )}
                  {page.pageNumber === certificateFooterPage && (
                    <img className="certificate-footer" src={`/${CERTIFICATE_FOOTER_SRC}`} alt="Terpkos Engineering Pty Ltd as trustee for the TEG Trust" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <aside className={`editor-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {(() => {
            const tabs: Array<{ id: 'pages' | 'info' | 'variables'; label: string; icon: React.ReactNode }> = [
              {
                id: 'pages',
                label: 'Pages',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2.5" y="1.5" width="11" height="13" rx="1.5" />
                    <line x1="5" y1="5" x2="11" y2="5" />
                    <line x1="5" y1="8" x2="11" y2="8" />
                    <line x1="5" y1="11" x2="8.5" y2="11" />
                  </svg>
                ),
              },
              {
                id: 'info',
                label: 'Info',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="8" r="6.5" />
                    <line x1="8" y1="7.5" x2="8" y2="11.5" />
                    <circle cx="8" cy="5" r="0.4" fill="currentColor" />
                  </svg>
                ),
              },
              {
                id: 'variables',
                label: 'Fields',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.5 4h11M2.5 8h7M2.5 12h9" />
                    <circle cx="12.5" cy="8" r="1.75" />
                  </svg>
                ),
              },
            ];

            const bodyPages = pages.filter(p => p.type === 'body').length;
            const appendixPages = pages.length - bodyPages;
            const statusClass = statusPillClass;
            const isDraft = document.status === 'Draft';

            return (
              <>
                <div className="sidebar-header">
                  <button
                    type="button"
                    className="sidebar-toggle"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    title={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
                    aria-label={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1.5" y="2.5" width="13" height="11" rx="2" />
                      <line x1="10" y1="2.5" x2="10" y2="13.5" />
                    </svg>
                  </button>
                  {!sidebarCollapsed && (
                    <div className="sidebar-tabs" role="tablist">
                      {tabs.map(tab => (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={activeTab === tab.id}
                          className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          {tab.icon}
                          <span>{tab.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {sidebarCollapsed && (
                  <div className="sidebar-rail">
                    {tabs.map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`rail-btn ${activeTab === tab.id ? 'active' : ''}`}
                        title={tab.label}
                        aria-label={tab.label}
                        onClick={() => { setActiveTab(tab.id); setSidebarCollapsed(false); }}
                      >
                        {tab.icon}
                      </button>
                    ))}
                  </div>
                )}

                {!sidebarCollapsed && (
                  <div className="sidebar-content">
                    {/* Pages */}
                    {activeTab === 'pages' && (
                      <div className="pages-tab">
                        <div className="panel-heading">
                          <span className="panel-heading__title">Pages</span>
                          <span className="panel-heading__meta">
                            {pages.length} {pages.length === 1 ? 'page' : 'pages'}
                            {appendixPages > 0 && ` · ${appendixPages} appendix`}
                          </span>
                        </div>
                        <div className="pages-grid">
                          {pages.map((page, index) => {
                            // A blank page's pages are body pages that carry the
                            // marker's id; the rail treats them as an appendix's.
                            const isTextAppendix = page.type === 'body' && !!page.blankPageId;
                            const isAppendix = page.type === 'appendix' || isTextAppendix;
                            const isAttachment = page.appendixKind === 'attachment';
                            const isAppendixFirst = isAppendix && page.appendixPageIndex === 0;
                            // An attached PDF is one card, not a thumbnail per
                            // page: a 40-page specification would flood the rail.
                            if (isAttachment && !isAppendixFirst) return null;
                            const canDelete = isAppendixFirst && isDraft;
                            const ownerId = page.appendixBlockId ?? page.blankPageId;
                            const appendixBlock = ownerId
                              ? document.content.find(b => b.id === ownerId)
                              : null;
                            const appendixData = appendixBlock?.data as { title?: string; filename?: string } | undefined;
                            const appendixTitle = isTextAppendix ? 'Blank page'
                              : appendixData?.title || appendixData?.filename
                              || (isAttachment ? 'Attached PDF' : 'Appendix');
                            const chip = isAttachment ? 'Attachment'
                              : isTextAppendix ? 'Page'
                              : page.appendixKind === 'table' ? 'Table'
                              : page.appendixKind === 'plan' ? 'Plan'
                              : 'Appendix';
                            const attachmentPages = isAttachment
                              ? ((appendixBlock?.data as PdfAttachmentBlockData | undefined)?.pages ?? [])
                              : [];
                            const attachmentMeta = attachmentPages.length > 0
                              ? `${attachmentPages.length} ${attachmentPages.length === 1 ? 'page' : 'pages'} · ${describePageSize(attachmentPages[0])}`
                              : 'No file yet';
                            const isActive = index === activePageIndex
                              || (isAttachment && activePageIndex > index && activePageIndex < index + (page.appendixTotalPages ?? 1));
                            const appendixOrder = ownerId ? appendixSections.indexOf(ownerId) : -1;
                            const deleteId = page.appendixBlockId ?? page.blankPageId;

                            return (
                              <div
                                key={page.pageNumber}
                                className={`page-thumbnail ${isAppendix ? 'is-appendix' : ''} ${isActive ? 'is-active' : ''}`}
                                role="button"
                                tabIndex={0}
                                title={isAppendix ? `${appendixTitle} (page ${page.pageNumber})` : `Page ${page.pageNumber}`}
                                onClick={() => scrollToPage(index)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    scrollToPage(index);
                                  }
                                }}
                              >
                                <div
                                  className="thumbnail-frame"
                                  // An attached page's card takes the page's own shape, as its
                                  // preview does; every other page is A4 portrait.
                                  style={isAttachment && page.pageSize
                                    ? { aspectRatio: `${page.pageSize.widthPt} / ${page.pageSize.heightPt}` }
                                    : undefined}
                                >
                                  <div
                                    className="thumbnail-canvas"
                                    ref={(el) => {
                                      if (el) thumbnailHostsRef.current.set(page.pageNumber, el);
                                      else thumbnailHostsRef.current.delete(page.pageNumber);
                                    }}
                                  />
                                  {isAppendix && <span className="thumbnail-chip">{chip}</span>}
                                  {isAppendixFirst && isDraft && appendixSections.length > 1 && (
                                    <div className="thumbnail-move">
                                      <button
                                        type="button"
                                        title="Move this appendix earlier"
                                        aria-label="Move this appendix earlier"
                                        disabled={appendixOrder <= 0}
                                        onClick={(e) => { e.stopPropagation(); moveAppendixBlock(ownerId!, 'up'); }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" /></svg>
                                      </button>
                                      <button
                                        type="button"
                                        title="Move this appendix later"
                                        aria-label="Move this appendix later"
                                        disabled={appendixOrder < 0 || appendixOrder >= appendixSections.length - 1}
                                        onClick={(e) => { e.stopPropagation(); moveAppendixBlock(ownerId!, 'down'); }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v10M3.5 8.5L8 13l4.5-4.5" /></svg>
                                      </button>
                                    </div>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      className="thumbnail-delete"
                                      title={isAttachment ? 'Remove attachment' : 'Delete appendix'}
                                      aria-label={isAttachment ? 'Remove attachment' : 'Delete appendix'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteAppendixConfirm({
                                          blockId: deleteId!,
                                          title: appendixTitle,
                                        });
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4M12 4v9.33a1.33 1.33 0 01-1.33 1.34H5.33A1.33 1.33 0 014 13.33V4"/>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                <div className="thumbnail-label">
                                  <span className="thumbnail-label__number">
                                    {isAttachment ? '—' : page.reportPageNumber}
                                  </span>
                                  <span className="thumbnail-label__text">
                                    {isAttachment
                                      ? `${appendixTitle} · ${attachmentMeta}`
                                      : isAppendix
                                        ? (page.appendixTotalPages && page.appendixTotalPages > 1
                                            ? `${appendixTitle} ${(page.appendixPageIndex ?? 0) + 1}/${page.appendixTotalPages}`
                                            : appendixTitle)
                                        : (index === 0 ? 'Letter' : 'Continued')}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Info */}
                    {activeTab === 'info' && (
                      <div className="info-tab">
                        <div className="doc-card">
                          <div className="doc-card__number">{document.document_number}</div>
                          <div className="doc-card__title">{document.title || headerFields.report_title || 'Untitled document'}</div>
                          <div className="doc-card__row">
                            <span className={`status-pill ${statusClass}`}>{document.status}</span>
                            <span className="doc-card__template">{template.name}</span>
                          </div>
                        </div>

                        <div className="stat-grid">
                          <div className="stat">
                            <span className="stat__value">{totalPages}</span>
                            <span className="stat__label">{totalPages === 1 ? 'Page' : 'Pages'}</span>
                          </div>
                          <div className="stat">
                            <span className="stat__value">{documentStats.words.toLocaleString()}</span>
                            <span className="stat__label">Words</span>
                          </div>
                          <div className="stat">
                            <span className="stat__value">{documentStats.images}</span>
                            <span className="stat__label">{documentStats.images === 1 ? 'Photo' : 'Photos'}</span>
                          </div>
                          <div className="stat">
                            <span className="stat__value">{versionCountRef.current}</span>
                            <span className="stat__label">{versionCountRef.current === 1 ? 'Version' : 'Versions'}</span>
                          </div>
                        </div>

                        <dl className="detail-list">
                          <div className="detail">
                            <dt>Signatory</dt>
                            <dd>{signatoryName || <span className="is-empty">Not set</span>}</dd>
                          </div>
                          <div className="detail">
                            <dt>Document date</dt>
                            <dd>{headerFields.document_date ? format(new Date(headerFields.document_date), 'd MMM yyyy') : '—'}</dd>
                          </div>
                          {document.status === 'Sent' && (
                            <div className="detail">
                              <dt>Sent</dt>
                              <dd>
                                {document.sent_at
                                  ? format(new Date(document.sent_at), 'd MMM yyyy, h:mm a')
                                  : <span className="is-empty">Not recorded</span>}
                              </dd>
                            </div>
                          )}
                          <div className="detail">
                            <dt>Created</dt>
                            <dd>{document.created_at ? format(new Date(document.created_at), 'd MMM yyyy, h:mm a') : '—'}</dd>
                          </div>
                          <div className="detail">
                            <dt>Last saved</dt>
                            <dd>
                              {lastSavedAt
                                ? `${formatDistanceToNow(lastSavedAt, { addSuffix: true })}`
                                : (document.updated_at ? format(new Date(document.updated_at), 'd MMM yyyy, h:mm a') : '—')}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    )}

                    {/* Fields */}
                    {activeTab === 'variables' && (
                      <div className="variables-tab">
                        <div className="panel-heading">
                          <span className="panel-heading__title">Project fields</span>
                          <button
                            type="button"
                            className="panel-heading__action"
                            onClick={refreshProjectData}
                            title="Reload all fields from the project"
                          >
                            <RefreshIcon />
                            Refresh
                          </button>
                        </div>
                        <p className="panel-hint">Pulled live from the project and client records. Edit them there; refresh to pick up changes.</p>
                        <div className="field-list">
                          {(() => {
                            const clientAddressLines = clientAddress
                              ? [
                                  ...clientAddress.headerLines,
                                  ...(clientAddress.careOfClientName ? [`${clientAddress.topName} (c/- ${clientAddress.careOfClientName})`] : []),
                                  ...clientAddress.addressLines,
                                  ...(clientAddress.cityLine ? [clientAddress.cityLine] : []),
                                ]
                              : [];
                            const fields: Array<{ label: string; value: React.ReactNode; empty: boolean }> = [
                              { label: 'Job number', value: displayJobNumber, empty: displayJobNumber === '—' },
                              { label: 'Client', value: client?.company_name || '—', empty: !client?.company_name },
                              {
                                label: 'Client address',
                                value: clientAddressLines.length > 0
                                  ? clientAddressLines.map((l, i) => <div key={i}>{l}</div>)
                                  : '—',
                                empty: clientAddressLines.length === 0,
                              },
                              { label: 'Contact', value: projectContactFull || '—', empty: !projectContactFull },
                              { label: 'Project', value: displayProjectDescription, empty: displayProjectDescription === '—' },
                              { label: 'Site address', value: displaySiteAddress || '—', empty: !displaySiteAddress },
                            ];
                            return fields.map((f) => (
                              <div key={f.label} className="field">
                                <dt className="field__label">{f.label}</dt>
                                <dd className={`field__value${f.empty ? ' is-empty' : ''}`}>{f.value}</dd>
                              </div>
                            ));
                          })()}
                        </div>

                        <div className="panel-heading panel-heading--spaced">
                          <span className="panel-heading__title">Overrides</span>
                        </div>
                        <p className="panel-hint">Custom values for this document only. Leave blank to use the project value.</p>
                        <div className="override-list">
                          {HEADER_FIELD_SCHEMAS[chromeProps.variant].map(field => {
                            // The SI number lives on the document row, not in
                            // header_fields, and is written by its own RPC so
                            // the autosave never becomes a second writer.
                            if (field.bindsSequence) {
                              return (
                                <label key={field.label} className={`override ${sequenceInput ? 'has-value' : ''}`}>
                                  <span className="override__label">{field.label}</span>
                                  <span className="override__control">
                                    <input
                                      type="number"
                                      min={1}
                                      value={sequenceInput}
                                      onChange={(e) => setSequenceInput(e.target.value)}
                                      onBlur={commitSequence}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                      className="override__input"
                                      disabled={!isDraft}
                                    />
                                  </span>
                                </label>
                              );
                            }

                            // Bound straight to documents.title -- no
                            // header_fields key behind it. Goes through the
                            // normal autosave; a Draft rename re-expands
                            // document_number server-side (sync_document_number).
                            if (field.bindsTitle) {
                              const titleValue = document.title || '';
                              return (
                                <label key={field.label} className={`override ${titleValue ? 'has-value' : ''}`}>
                                  <span className="override__label">{field.label}</span>
                                  <span className="override__control">
                                    <input
                                      type="text"
                                      value={titleValue}
                                      onChange={(e) => applyDocumentUpdate(prev => ({ ...prev, title: e.target.value }))}
                                      placeholder={field.placeholder ?? ''}
                                      className="override__input"
                                      disabled={!isDraft}
                                    />
                                  </span>
                                </label>
                              );
                            }

                            const key = field.key as keyof typeof headerFields;
                            const value = (headerFields[key] as string | undefined) || '';

                            // A fixed set of choices rather than a free-text
                            // override, so the value stays one of the two the
                            // form is printed with.
                            if (field.type === 'select') {
                              return (
                                <label key={field.label} className={`override ${value ? 'has-value' : ''}`}>
                                  <span className="override__label">{field.label}</span>
                                  <span className="override__control">
                                    <select
                                      value={value}
                                      onChange={(e) => updateHeaderField(key, e.target.value)}
                                      className="override__input override__input--select"
                                      disabled={!isDraft}
                                    >
                                      {(field.options ?? []).map(option => (
                                        <option key={option} value={option}>{option}</option>
                                      ))}
                                    </select>
                                  </span>
                                </label>
                              );
                            }

                            const placeholder =
                              field.placeholder ?? derivedPlaceholders[field.derivedFrom ?? ''] ?? '';
                            return (
                              <label key={field.label} className={`override ${value ? 'has-value' : ''}`}>
                                <span className="override__label">{field.label}</span>
                                <span className="override__control">
                                  <input
                                    type={field.type}
                                    value={value}
                                    onChange={(e) => updateHeaderField(key, e.target.value, field.syncsTitle)}
                                    placeholder={placeholder}
                                    className="override__input"
                                    disabled={!isDraft}
                                  />
                                  {value && field.type === 'text' && isDraft && (
                                    <button
                                      type="button"
                                      className="override__clear"
                                      title="Clear override"
                                      aria-label={`Clear ${field.label}`}
                                      onClick={() => updateHeaderField(key, '', field.syncsTitle)}
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>

                        {/* The drawing list is captured into the document, not
                            read live, so it needs somewhere to be brought up to
                            date. That belongs here with the other project-derived
                            values rather than as a control floating in the page. */}
                        {hasDrawingList && (
                          <>
                            <div className="panel-heading panel-heading--spaced">
                              <span className="panel-heading__title">Drawings</span>
                              <button
                                type="button"
                                className="panel-heading__action"
                                onClick={refreshDrawings}
                                disabled={!isDraft || isRefreshingDrawings}
                                title="Reload the drawings from this stage's drawing set"
                              >
                                <RefreshIcon />
                                {isRefreshingDrawings ? 'Refreshing…' : 'Refresh'}
                              </button>
                            </div>
                            <p className="panel-hint">
                              {drawingListIsEmpty
                                ? 'This stage has no drawings yet. Add them to the drawing set on the RWD PEP, then refresh.'
                                : 'Copied from this stage\u2019s drawing set when the certificate was created. Refresh takes up any changes since \u2014 and replaces anything edited in the list here. The design criteria sentence follows too, unless you have reworded it.'}
                            </p>
                          </>
                        )}

                        {/* Same reasoning as the drawings above: the QA bullets
                            are captured from the PEP, so they need somewhere to
                            be brought up to date that is not a control floating
                            in the page. */}
                        {hasQaList && (
                          <>
                            <div className="panel-heading panel-heading--spaced">
                              <span className="panel-heading__title">QA items</span>
                              <button
                                type="button"
                                className="panel-heading__action"
                                onClick={refreshQaItems}
                                disabled={!isDraft || isRefreshingQa}
                                title="Reload the QA items completed on this job"
                              >
                                <RefreshIcon />
                                {isRefreshingQa ? 'Refreshing…' : 'Refresh'}
                              </button>
                            </div>
                            <p className="panel-hint">
                              {qaListIsEmpty
                                ? 'No QA items have a completion date on this job yet. Date them on the RWC PEP, then refresh.'
                                : 'Which bullets appear follows the QA items completed on this job \u2014 the ones with a completion date on the RWC PEP, not merely marked required. Refresh takes up any change to that; what you have written in a bullet is kept.'}
                            </p>
                          </>
                        )}

                        {/* The table appendix and the location plan have no
                            section here: rows, columns, the photo fill, the
                            plan file, rotation, marker source and placing all
                            live on the action bar above their pages. */}
                      </div>
                    )}
                  </div>
                )}

                {!sidebarCollapsed && isDraft && (
                  <div className="sidebar-insert">
                    <div className="panel-heading">
                      <span className="panel-heading__title">Insert</span>
                    </div>
                    <div className="insert-grid">
                      {/* A site instruction's body is the form itself, so only
                          the photo appendix can be added to it. */}
                      {chromeProps.variant !== 'site_instruction' && (
                      <button type="button" className="insert-card" onClick={addLetterParagraph}>
                        <span className="insert-card__icon">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <line x1="2.5" y1="3.5" x2="13.5" y2="3.5" />
                            <line x1="2.5" y1="7" x2="13.5" y2="7" />
                            <line x1="2.5" y1="10.5" x2="9.5" y2="10.5" />
                          </svg>
                        </span>
                        <span className="insert-card__text">
                          <span className="insert-card__title">Text</span>
                          <span className="insert-card__hint">Paragraph in the letter</span>
                        </span>
                      </button>
                      )}
                      {/* A certificate is a statement, not a record with
                          photographs. */}
                      {!TEMPLATES_WITHOUT_APPENDIX.has(template?.code ?? '') && (
                      <button type="button" className="insert-card" onClick={() => addBlock('image_appendix')}>
                        <span className="insert-card__icon">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
                            <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" />
                            <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" />
                            <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" />
                            <rect x="9" y="9" width="5.5" height="5.5" rx="1" />
                          </svg>
                        </span>
                        <span className="insert-card__text">
                          <span className="insert-card__title">Photo appendix</span>
                          <span className="insert-card__hint">Numbered photo pages</span>
                        </span>
                      </button>
                      )}
                      {chromeProps.variant === 'letter' && !TEMPLATES_WITHOUT_APPENDIX.has(template?.code ?? '') && (
                        <button
                          type="button"
                          className="insert-card"
                          onClick={() => {
                            // The marker and a first paragraph to type into.
                            const marker = createBlock('blank_page');
                            const first = createBlock('paragraph');
                            applyDocumentUpdate(prev => ({ ...prev, content: [...prev.content, marker, first] }));
                          }}
                        >
                          <span className="insert-card__icon">
                            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                              <path d="M3 3.5h10M3 6.5h10M3 9.5h6" />
                              <path d="M3 13h4" />
                            </svg>
                          </span>
                          <span className="insert-card__text">
                            <span className="insert-card__title">Blank page</span>
                            <span className="insert-card__hint">An empty page between appendices</span>
                          </span>
                        </button>
                      )}
                      {/* The defect-report appendices: reports only. A site
                          instruction is a one-page form and a certificate a
                          statement; neither carries an inspection record. */}
                      {chromeProps.variant === 'letter' && !TEMPLATES_WITHOUT_APPENDIX.has(template?.code ?? '') && (
                        <>
                          <button type="button" className="insert-card" onClick={() => addBlock('table_appendix')}>
                            <span className="insert-card__icon">
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
                                <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
                                <path d="M1.5 6h13M1.5 9.5h13M5.5 6v7.5" />
                              </svg>
                            </span>
                            <span className="insert-card__text">
                              <span className="insert-card__title">Table appendix</span>
                              <span className="insert-card__hint">Inspection record, schedule of works</span>
                            </span>
                          </button>
                          <button type="button" className="insert-card" onClick={() => addBlock('plan_appendix')}>
                            <span className="insert-card__icon">
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 14s-4.5-4-4.5-7.5a4.5 4.5 0 019 0C12.5 10 8 14 8 14z" />
                                <circle cx="8" cy="6.5" r="1.5" />
                              </svg>
                            </span>
                            <span className="insert-card__text">
                              <span className="insert-card__title">Location plan</span>
                              <span className="insert-card__hint">Plan with numbered markers</span>
                            </span>
                          </button>
                          <button type="button" className="insert-card" onClick={() => addBlock('pdf_attachment')}>
                            <span className="insert-card__icon">
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.5 5.5l-4.6 4.6a1.5 1.5 0 002.1 2.1l5-5a3 3 0 00-4.2-4.2L3.6 8.2a4.5 4.5 0 006.4 6.4L13 11.6" />
                              </svg>
                            </span>
                            <span className="insert-card__text">
                              <span className="insert-card__title">Attach PDF</span>
                              <span className="insert-card__hint">Specification, sketches, any page size</span>
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </aside>
      </div>

      {/* Delete Appendix Confirmation Modal */}
      {deleteAppendixConfirm && (
        <div className="delete-appendix-overlay" onClick={() => setDeleteAppendixConfirm(null)}>
          <div className="delete-appendix-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-appendix-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3>Delete Appendix</h3>
            </div>
            <p>Are you sure you want to delete <strong>"{deleteAppendixConfirm.title}"</strong>? Everything on its pages will be removed from the document.</p>
            {(() => {
              // A plan numbering its markers from this table loses them.
              const dependents = document.content.filter(b =>
                b.type === 'plan_appendix'
                && (b.data as PlanAppendixBlockData).labelSource === 'item'
                && (b.data as PlanAppendixBlockData).tableBlockId === deleteAppendixConfirm.blockId);
              const markerCount = dependents.reduce((n, b) => n + planMarkersAll(b.data as PlanAppendixBlockData).length, 0);
              return dependents.length > 0 ? (
                <p>
                  {dependents.length === 1 ? 'A location plan numbers' : `${dependents.length} location plans number`} its
                  markers from this table. {markerCount > 0 ? `Its ${markerCount} marker${markerCount === 1 ? '' : 's'} will be removed and it` : 'It'} will
                  go back to photo numbers.
                </p>
              ) : null;
            })()}
            <p className="delete-appendix-warning">This action cannot be undone.</p>
            <div className="delete-appendix-actions">
              <button className="btn-cancel" onClick={() => setDeleteAppendixConfirm(null)}>Cancel</button>
              <button className="btn-delete" onClick={() => handleDeleteAppendix(deleteAppendixConfirm.blockId)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Signing confirmation. Shows exactly what will be stamped, because a
          signature cannot be withdrawn by the signer once the document issues. */}
      {signingSlot && (
        <div className="sign-overlay" onClick={() => !isSigning && setSigningSlot(null)}>
          <div className="sign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sign-modal__header">
              <h3>Sign as &ldquo;{signingSlot.label}&rdquo;</h3>
            </div>

            <p className="sign-modal__intro">
              The following will be added to <strong>{document.title || document.document_number}</strong>:
            </p>

            {signRepresented.onBehalf && (
              <p className="sign-modal__intro">
                You are applying <strong>{signRepresented.name || 'the certified engineer'}</strong>&rsquo;s
                signature on their behalf.
              </p>
            )}

            {/* A 1:1 mock of the document's signature block, in the document's
                type at print size -- see .sign-modal__preview. The image is
                absolutely positioned in the two-line signature space with the
                signer's own scale and mm offsets, exactly as SignatureBlock and
                DocumentPDF place it, so the preview shows where the ink will
                actually land against the rule. */}
            <div className="sign-modal__preview">
              <div className="sign-modal__space">
                {mySignaturePreviewUrl ? (
                  <img
                    src={mySignaturePreviewUrl}
                    alt="Signature"
                    className="sign-modal__signature"
                    // Inline because these are the signer's placement data, not
                    // style. Same mm/pt semantics as SignatureBlock.
                    style={{
                      left: `${signRepresented.placement.offsetXMm}mm`,
                      bottom: `${-signRepresented.placement.offsetYMm}mm`,
                      height: `${SIGNATURE_BASE_HEIGHT_PT * signRepresented.placement.scale}pt`,
                    }}
                  />
                ) : (
                  <span className="sign-modal__no-signature">No signature on file</span>
                )}
              </div>
              <div className="sign-modal__rule" />
              <div className="sign-modal__name">{signRepresented.name}</div>
              {signRepresented.title && <div className="sign-modal__meta">{signRepresented.title}</div>}
              {signRepresented.registration && <div className="sign-modal__meta">{signRepresented.registration}</div>}
              {/* The dialog's own addition, not a line the block prints: which
                  date this signature will carry. */}
              <div className="sign-modal__date">{format(new Date(), 'd MMMM yyyy')}</div>
            </div>

            <p className="sign-modal__warning">
              {slots.filter(sl => sl.required !== false).length <= 1
                ? 'This will sign off the document. Once signed it is locked and cannot be edited.'
                : 'Once every required signature is in place the document is locked and cannot be edited.'}
            </p>

            {lifecycleError && <p className="sign-modal__error">{lifecycleError}</p>}

            <div className="sign-modal__actions">
              <button className="btn-cancel" onClick={() => setSigningSlot(null)} disabled={isSigning}>
                Cancel
              </button>
              <button
                className="btn-sign"
                onClick={() => handleSign(signingSlot)}
                disabled={isSigning || !mySignaturePreviewUrl}
              >
                {isSigning ? 'Signing...' : 'Sign'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DocumentEditor;
